import {
  formatTons,
  getClientSelectionId,
  getDeliveryHighlightKey,
  getInitialPreloadHighlightKey,
  getInitialPreloadOrderHighlightKey,
  getLoadingHighlightKey,
  getLoadingOrderHighlightKey,
  getOperationalStepReassignmentAvailability,
  getOperationalStepReassignmentCandidatePoolContexts,
  getOperationalStepReassignmentContexts,
  getRouteSegmentHighlightKey,
  getSegmentBufferHighlightKey,
} from "./resultsFleetData.js";
import { buildOperationalStepViewModel, resolvePortalDriverConfirmationHighlight } from "./resultsFleetCardTripGroups.js";
import { buildExcludedTruckRefreshByTruckId } from "./resultsChromeData.js";
import { resolveAssignmentDisplayName, resolveMissingTractorTitle } from "./resultsFleetCardShell.js";
import {
  normalizeClientDisplayLabel,
  normalizeProductCodeDisplay,
  resolveOrderProductCodeDisplay,
} from "./displayLabelFormatters.js";
import {
  buildProtectionStatusMarker,
  PROTECTION_VARIANT_REORDERED,
  PROTECTION_VARIANT_STANDARD,
} from "./protectionVisualState.js";
import { isFrozenDispatcherStatusCode } from "./dispatcherFreezeStatus.js";
import {
  resolveCompartmentFillRulesFromResults,
  resolveStopCompartmentFill,
} from "../lib/productCompartmentFillRules.js";

// Carte et séquence : mêmes teintes que les segments du Gantt (bleu chargement, sable livraison).
export const DELIVERY_ROUTE_COLOR = "#B09256";
export const DELIVERY_ROUTE_STRONG_COLOR = "#80662F";
export const LOADING_ROUTE_COLOR = "#5F86B8";
export const LOADING_ROUTE_STRONG_COLOR = "#3F6696";
export const ASPIRATOR_ROUTE_COLOR = "#EA580C";
export const INITIAL_PRELOAD_ROUTE_COLOR = "#7A8FB8";
export const END_OF_DAY_LOAD_ROUTE_COLOR = "#FACC15";
export const END_OF_DAY_LOAD_EVENING_ROUTE_COLOR = "#111827";
const END_OF_DAY_LOAD_LABEL = "Chargé en PM";
const LEGACY_END_OF_DAY_LOAD_LABELS = new Set(["charger ce soir", "chargement ce soir"]);

function normalizeEndOfDayLoadLabel(value) {
  if (typeof value !== "string") {
    return value;
  }

  const normalizedValue = normalizeTimelineTextIdentity(value).toLowerCase();
  return LEGACY_END_OF_DAY_LOAD_LABELS.has(normalizedValue) ? END_OF_DAY_LOAD_LABEL : value;
}

function normalizeEndOfDayLoadPeriodValue(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function normalizeDispatchPeriodValue(value = "") {
  const normalizedValue = normalizeEndOfDayLoadPeriodValue(value);
  return normalizedValue === "evening" || normalizedValue === "soir" ? "evening" : "day";
}

function normalizeEndOfDayLoadDateValue(value = "") {
  return typeof value === "string" ? value.trim().slice(0, 10) : "";
}

function resolveEndOfDayLoadVisualModeValue(source = null) {
  const deliveryMode = normalizeEndOfDayLoadPeriodValue(
    source?.delivery_mode
    || source?.deliveryMode
    || source?.endOfDayLoadDeliveryMode
    || source?.end_of_day_load_delivery_mode,
  );
  const targetPeriod = normalizeEndOfDayLoadPeriodValue(
    source?.target_period
    || source?.targetPeriod
    || source?.endOfDayLoadTargetPeriod
    || source?.end_of_day_load_target_period,
  );
  if (deliveryMode === "evening" || deliveryMode === "soir" || targetPeriod === "evening" || targetPeriod === "soir") {
    return "evening";
  }
  if (
    deliveryMode === "next_day"
    || deliveryMode === "tomorrow"
    || deliveryMode === "demain"
    || deliveryMode === "day"
    || deliveryMode === "jour"
    || targetPeriod === "day"
    || targetPeriod === "jour"
  ) {
    return "next_day";
  }

  const sourceDispatchDate = normalizeEndOfDayLoadDateValue(
    source?.source_dispatch_date
    || source?.sourceDispatchDate
    || source?.endOfDayLoadSourceDispatchDate
    || source?.end_of_day_load_source_dispatch_date,
  );
  const targetDispatchDate = normalizeEndOfDayLoadDateValue(
    source?.target_dispatch_date
    || source?.targetDispatchDate
    || source?.endOfDayLoadTargetDispatchDate
    || source?.end_of_day_load_target_dispatch_date,
  );
  return sourceDispatchDate && targetDispatchDate && sourceDispatchDate === targetDispatchDate
    ? "evening"
    : "next_day";
}

function resolveEndOfDayLoadDisplayModeValue(source = null) {
  return resolveEndOfDayLoadVisualModeValue(source);
}

function resolveEndOfDayLoadTargetDispatchDateValue(source = null) {
  return normalizeEndOfDayLoadDateValue(
    source?.target_dispatch_date
    || source?.targetDispatchDate
    || source?.endOfDayLoadTargetDispatchDate
    || source?.end_of_day_load_target_dispatch_date,
  );
}

function resolveEndOfDayLoadSourceDispatchDateValue(source = null) {
  return normalizeEndOfDayLoadDateValue(
    source?.source_dispatch_date
    || source?.sourceDispatchDate
    || source?.endOfDayLoadSourceDispatchDate
    || source?.end_of_day_load_source_dispatch_date,
  );
}

function shouldShowEndOfDayLoadInDispatchPeriod(source = null, dispatchPeriod = "day", dispatchDate = "", {
  dateRole = "target",
} = {}) {
  const visualMode = resolveEndOfDayLoadVisualModeValue(source);
  const normalizedDispatchPeriod = normalizeDispatchPeriodValue(dispatchPeriod);
  const activeDispatchDate = normalizeEndOfDayLoadDateValue(dispatchDate);
  const targetDispatchDate = resolveEndOfDayLoadTargetDispatchDateValue(source);
  const sourceDispatchDate = resolveEndOfDayLoadSourceDispatchDateValue(source);
  const markerDispatchDate = normalizeEndOfDayLoadDateValue(source?.marker_dispatch_date || source?.markerDispatchDate);
  const comparisonDate = dateRole === "source"
    ? sourceDispatchDate || markerDispatchDate || targetDispatchDate
    : targetDispatchDate || sourceDispatchDate || markerDispatchDate;
  const isExpectedPeriod = dateRole === "source"
    ? normalizedDispatchPeriod === "day"
    : visualMode === "evening"
      ? normalizedDispatchPeriod === "evening"
      : normalizedDispatchPeriod === "day";

  if (!isExpectedPeriod) {
    return false;
  }

  return !activeDispatchDate || !comparisonDate || comparisonDate === activeDispatchDate;
}

function isPlainTimelineObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function resolveEveningStandardTransferDiagnostics(candidate = null) {
  if (!isPlainTimelineObject(candidate)) {
    return null;
  }
  const diagnostics = [
    candidate._planning_diagnostics,
    candidate.planning_diagnostics,
    candidate.planningDiagnostics,
    candidate.last_reassignment_diagnostics,
    candidate.lastReassignmentDiagnostics,
    candidate.diagnostics,
    candidate,
  ].find((item) => isPlainTimelineObject(item) && String(item.action || "").trim() === "evening_standard_transfer");

  return diagnostics || null;
}

function collectEveningStandardTransferDetailObjects(diagnostics = null, context = null) {
  const detailObjects = [];
  const addObject = (value) => {
    if (isPlainTimelineObject(value) && !detailObjects.includes(value)) {
      detailObjects.push(value);
    }
  };
  const localPatch = isPlainTimelineObject(diagnostics?.local_patch) ? diagnostics.local_patch : {};

  addObject(context);
  addObject(diagnostics);
  addObject(diagnostics?.evening_standard_transfer);
  addObject(localPatch);
  addObject(localPatch.details);
  return detailObjects;
}

function collectEveningStandardTransferValues(detailObjects = [], keys = []) {
  const values = [];
  const addValue = (value) => {
    if (Array.isArray(value)) {
      value.forEach(addValue);
      return;
    }
    const normalizedValue = typeof value === "string" ? value.trim() : "";
    if (normalizedValue && !values.includes(normalizedValue)) {
      values.push(normalizedValue);
    }
  };

  detailObjects.forEach((detail) => {
    if (!isPlainTimelineObject(detail)) {
      return;
    }
    keys.forEach((key) => addValue(detail[key]));
  });
  return values;
}

export function resolveEveningStandardTransferGanttFilter(state = {}, dispatchDate = "") {
  const candidates = [
    state?.lastReassignmentDiagnosticPayload,
    state?.resultsData,
    state,
  ];
  const normalizedDispatchDate = normalizeEndOfDayLoadDateValue(dispatchDate);

  for (const candidate of candidates) {
    const diagnostics = resolveEveningStandardTransferDiagnostics(candidate);
    if (!diagnostics) {
      continue;
    }
    const resolutionMode = typeof diagnostics?.resolution_mode === "string"
      ? diagnostics.resolution_mode.trim().toLowerCase()
      : "";
    const rejectionReason = typeof diagnostics?.local_patch?.rejection_reason === "string"
      ? diagnostics.local_patch.rejection_reason.trim()
      : "";
    if (resolutionMode.endsWith("_rejected") || rejectionReason) {
      continue;
    }
    const detailObjects = collectEveningStandardTransferDetailObjects(diagnostics, candidate);
    const diagnosticDates = collectEveningStandardTransferValues(detailObjects, [
      "dispatch_date",
      "source_dispatch_date",
      "sourceDispatchDate",
      "target_dispatch_date",
      "targetDispatchDate",
    ]).map(normalizeEndOfDayLoadDateValue).filter(Boolean);
    if (
      normalizedDispatchDate
      && diagnosticDates.length
      && !diagnosticDates.includes(normalizedDispatchDate)
    ) {
      continue;
    }

    const orderIds = collectEveningStandardTransferValues(detailObjects, [
      "affected_order_ids",
      "moved_order_ids",
      "order_ids",
      "updated_order_ids",
    ]);
    const deliveryIds = collectEveningStandardTransferValues(detailObjects, [
      "delivery_ids",
      "moved_delivery_ids",
      "affected_delivery_ids",
    ]);
    if (!orderIds.length && !deliveryIds.length) {
      continue;
    }

    return {
      deliveryIds: new Set(deliveryIds),
      orderIds: new Set(orderIds),
    };
  }

  return null;
}

function collectEndOfDayLoadMarkerIdentityValues(marker = null) {
  const orderIds = [];
  const deliveryIds = [];
  const addOrderId = (value) => {
    const orderId = typeof value === "string" ? value.trim() : "";
    if (orderId && !orderIds.includes(orderId)) {
      orderIds.push(orderId);
    }
  };
  const addDeliveryId = (value) => {
    const deliveryId = typeof value === "string" ? value.trim() : "";
    if (deliveryId && !deliveryIds.includes(deliveryId)) {
      deliveryIds.push(deliveryId);
    }
  };

  normalizeEndOfDayLoadValues(marker?.order_ids).forEach(addOrderId);
  normalizeEndOfDayLoadValues(marker?.delivery_ids).forEach(addDeliveryId);
  for (const item of (Array.isArray(marker?.items) ? marker.items : [])) {
    if (!isPlainTimelineObject(item)) {
      continue;
    }
    addOrderId(item.order_id);
    addDeliveryId(item.delivery_id);
  }
  for (const item of (Array.isArray(marker?.target_suspended_deliveries) ? marker.target_suspended_deliveries : [])) {
    if (!isPlainTimelineObject(item)) {
      continue;
    }
    addOrderId(item.order_id);
    addDeliveryId(item.delivery_id);
  }

  return { deliveryIds, orderIds };
}

function filterEveningStandardTransferResidualEndOfDayLoads(markers = [], transferFilter = null) {
  const safeMarkers = Array.isArray(markers) ? markers.filter(Boolean) : [];
  if (!transferFilter || (!transferFilter.orderIds?.size && !transferFilter.deliveryIds?.size)) {
    return safeMarkers;
  }

  return safeMarkers.filter((marker) => {
    const { deliveryIds, orderIds } = collectEndOfDayLoadMarkerIdentityValues(marker);
    return !orderIds.some((orderId) => transferFilter.orderIds.has(orderId))
      && !deliveryIds.some((deliveryId) => transferFilter.deliveryIds.has(deliveryId));
  });
}

export function resolveEndOfDayLoadRouteColor(source = null) {
  return resolveEndOfDayLoadVisualModeValue(source) === "evening"
    ? END_OF_DAY_LOAD_EVENING_ROUTE_COLOR
    : END_OF_DAY_LOAD_ROUTE_COLOR;
}
export const DEPOT_ROUTE_COLOR = "#0f5c57";

export const MANUAL_BUFFER_REASON_OPTIONS = Object.freeze([
  { value: "manual", label: "Manuel" },
  { value: "attente", label: "Attente" },
  { value: "chargement_lent", label: "Chargement lent" },
  { value: "dechargement_lent", label: "Déchargement lent" },
  { value: "bateau", label: "Bateau" },
  { value: "acces_site", label: "Accès site" },
  { value: "autre", label: "Autre" },
]);

const MANUAL_BUFFER_REASON_LABEL_BY_CODE = new Map(
  MANUAL_BUFFER_REASON_OPTIONS.map(({ value, label }) => [value, label]),
);

export function getRouteDiagnostic(route) {
  if (!route || !route.diagnostic || typeof route.diagnostic !== "object") {
    return {};
  }

  return route.diagnostic;
}

export function getTripRoutableStops(trip) {
  if (trip && Array.isArray(trip.routable_stops) && trip.routable_stops.length) {
    return trip.routable_stops.filter(Boolean);
  }

  return Array.isArray(trip?.stops) ? trip.stops.filter(Boolean) : [];
}

function hasStopCoordinates(stop) {
  return Boolean(stop && typeof stop.latitude === "number" && typeof stop.longitude === "number");
}

export function collectStopsWithCoordinates(stops) {
  return (Array.isArray(stops) ? stops : [])
    .filter(hasStopCoordinates)
    .map((stop) => ({
      stop,
      latlng: [stop.latitude, stop.longitude],
    }));
}

function collectGeometryCoordinates(geometry) {
  if (!geometry || typeof geometry !== "object") {
    return [];
  }

  if (Array.isArray(geometry.coordinates)) {
    return geometry.coordinates;
  }

  if (geometry.type === "Feature" && geometry.geometry && Array.isArray(geometry.geometry.coordinates)) {
    return geometry.geometry.coordinates;
  }

  if (geometry.geometry && Array.isArray(geometry.geometry.coordinates)) {
    return geometry.geometry.coordinates;
  }

  return [];
}

export function collectRouteLatLngs(geometry) {
  const coordinates = collectGeometryCoordinates(geometry);
  if (!coordinates.length) {
    return [];
  }

  return coordinates
    .filter((coordinate) => Array.isArray(coordinate) && coordinate.length >= 2)
    .map((coordinate) => [coordinate[1], coordinate[0]]);
}

export function collectRouteSegmentLatLngGroups(segmentGeometries) {
  if (!Array.isArray(segmentGeometries)) {
    return [];
  }

  return segmentGeometries
    .map((geometry) => collectRouteLatLngs(geometry))
    .filter((latlngs) => latlngs.length > 1);
}

export function flattenRouteSegmentLatLngGroups(segmentLatLngGroups) {
  return (Array.isArray(segmentLatLngGroups) ? segmentLatLngGroups : []).reduce((flattened, latlngs, index) => {
    if (!Array.isArray(latlngs) || !latlngs.length) {
      return flattened;
    }

    if (index === 0) {
      flattened.push(...latlngs);
      return flattened;
    }

    flattened.push(...latlngs.slice(1));
    return flattened;
  }, []);
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const radiusKm = 6371;
  const deltaLat = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(deltaLon / 2) *
      Math.sin(deltaLon / 2);

  return radiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function formatDurationShort(minutes) {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) {
    return null;
  }

  const roundedMinutes = Math.round(minutes);
  const hours = Math.floor(roundedMinutes / 60);
  const remainingMinutes = roundedMinutes % 60;
  if (hours === 0) {
    return `${remainingMinutes} min`;
  }
  if (remainingMinutes === 0) {
    return `${hours} h`;
  }

  return `${hours} h ${remainingMinutes} min`;
}

export function formatManualBufferReasonLabel(reasonCode = "") {
  const normalizedReasonCode = typeof reasonCode === "string" ? reasonCode.trim() : "";
  return MANUAL_BUFFER_REASON_LABEL_BY_CODE.get(normalizedReasonCode) || "";
}

function estimateDriveMinutesFromDistance(distanceKm, speedKmh) {
  if (distanceKm == null || !Number.isFinite(distanceKm) || distanceKm < 0 || !speedKmh || speedKmh <= 0) {
    return null;
  }

  if (distanceKm === 0) {
    return 0;
  }

  return (distanceKm / speedKmh) * 60;
}

function estimateSegmentDurationMinutes(distanceKm, speedKmh) {
  return estimateDriveMinutesFromDistance(distanceKm, speedKmh);
}

function resolveRouteSegmentDisplayDurationMinutes(segment) {
  const displayDurationMinutes = normalizeTimelineDurationMinutes(segment?.displayDurationMinutes);
  if (displayDurationMinutes != null) {
    return displayDurationMinutes;
  }

  return normalizeTimelineDurationMinutes(segment?.durationMinutes);
}

function parseRouteScheduleTimestamp(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function resolveRouteSegmentScheduledTravelDurationMinutes(stops) {
  const [startStop, endStop] = Array.isArray(stops) ? stops : [];
  if (!startStop || !endStop) {
    return null;
  }

  const startTimestamp = parseRouteScheduleTimestamp(
    startStop.departure_at || startStop.service_end_at || startStop.scheduled_at || startStop.arrival_at,
  );
  const endTimestamp = parseRouteScheduleTimestamp(endStop.arrival_at || endStop.scheduled_at);
  if (!startTimestamp || !endTimestamp) {
    return null;
  }

  const durationMinutes = (endTimestamp.getTime() - startTimestamp.getTime()) / 60000;
  if (!Number.isFinite(durationMinutes) || durationMinutes < 0) {
    return null;
  }

  return durationMinutes;
}

function buildRouteSegmentStopGroups(stops) {
  const orderedStops = Array.isArray(stops) ? stops.filter(Boolean) : [];
  if (orderedStops.length < 2) {
    return orderedStops.length ? [orderedStops] : [];
  }

  return orderedStops.slice(0, -1).map((stop, index) => [stop, orderedStops[index + 1]]);
}

function parseDistanceLabelKm(distanceLabel) {
  if (typeof distanceLabel !== "string") {
    return null;
  }

  const match = distanceLabel.match(/-?\d+(?:[.,]\d+)?/);
  if (!match) {
    return null;
  }

  const parsedDistance = Number.parseFloat(match[0].replace(",", "."));
  return Number.isFinite(parsedDistance) ? parsedDistance : null;
}

function normalizeRouteDistanceSource(candidate) {
  const source = typeof candidate === "string" ? candidate.trim().toLowerCase() : "";
  if (source === "geometry") {
    return "geometrymeasured";
  }
  if (source === "estimate") {
    return "haversineestimate";
  }
  if (source === "roadapi" || source === "geometrymeasured" || source === "haversineestimate" || source === "unavailable") {
    return source;
  }

  return "unavailable";
}

function isHaversineRouteDistanceSource(candidate) {
  return normalizeRouteDistanceSource(candidate) === "haversineestimate";
}

function tripHasHaversineRouteSource(trip) {
  const route = trip?.route && typeof trip.route === "object" ? trip.route : null;
  if (!route) {
    return false;
  }

  if (
    isHaversineRouteDistanceSource(route.estimated_distance_source)
    || isHaversineRouteDistanceSource(route.actual_distance_source)
  ) {
    return true;
  }

  const segmentDistances = Array.isArray(route.segment_distances) ? route.segment_distances : [];
  return segmentDistances.some((segmentDistance) => isHaversineRouteDistanceSource(segmentDistance?.source));
}

function assignmentHasHaversineRouteWarning(assignment, segments = []) {
  const timelineSegments = Array.isArray(segments) ? segments : [];
  if (timelineSegments.some((segment) => isHaversineRouteDistanceSource(segment?.routeDistanceSource))) {
    return true;
  }

  const trips = Array.isArray(assignment?.trips) ? assignment.trips : [];
  return trips.some(tripHasHaversineRouteSource);
}

// Bulle du triangle : trajets entre deux sites (plus de 0 km) sans distance HERE, le plus long en exemple.
function summarizeHaversineRouteSegments(assignment, segments = []) {
  const routed = (Array.isArray(segments) ? segments : []).filter((segment) => (
    (segment?.routeDistanceSource === "roadapi" || isHaversineRouteDistanceSource(segment?.routeDistanceSource))
    && parseDistanceLabelKm(segment?.distanceLabel) > 0
  ));
  const estimated = routed.filter((segment) => isHaversineRouteDistanceSource(segment.routeDistanceSource));
  if (!estimated.length) {
    return null;
  }
  const example = estimated.reduce((longest, segment) => (
    parseDistanceLabelKm(segment.distanceLabel) > parseDistanceLabelKm(longest.distanceLabel) ? segment : longest
  ));
  const routes = (Array.isArray(assignment?.trips) ? assignment.trips : []).map((trip) => trip?.route || {});
  return {
    count: estimated.length,
    total: routed.length,
    example: { from: example.routeFromLabel, to: example.routeToLabel, distanceLabel: example.distanceLabel },
    cause: routes.some((route) => route.error_code === "stale_route") ? "stale_route" : "",
  };
}

function formatRouteDistanceSourceLabel(source) {
  if (source === "roadapi") {
    return null;
  }
  if (source === "geometrymeasured") {
    return "Mesuré (géométrie)";
  }
  if (source === "haversineestimate") {
    return "Estimé (haversine)";
  }

  return "Indisponible";
}

function getRouteHoverDistanceSourceLabel(source, sourceLabel = "") {
  const trimmedSourceLabel = typeof sourceLabel === "string" ? sourceLabel.trim() : "";

  if (source === "roadapi") {
    return "HERE Routing (mode camion)";
  }

  return trimmedSourceLabel || formatRouteDistanceSourceLabel(source) || "Indisponible";
}

function describeRouteDistanceSource(source) {
  if (source === "roadapi") {
    return "la distance routière réelle fournie par l'API de routage HERE pour ce segment";
  }
  if (source === "geometrymeasured") {
    return "la distance mesurée localement à partir de la géométrie renvoyée par HERE";
  }
  if (source === "haversineestimate") {
    return "la distance estimée en haversine entre les arrêts faute de longueur routière exploitable";
  }

  return "aucune distance exploitable disponible pour ce segment";
}

function resolveBackendSegmentDistanceKm(segmentDistance) {
  if (!segmentDistance || typeof segmentDistance !== "object") {
    return null;
  }

  if (typeof segmentDistance.distance_km === "number" && Number.isFinite(segmentDistance.distance_km) && segmentDistance.distance_km >= 0) {
    return segmentDistance.distance_km;
  }

  if (typeof segmentDistance.distance_meters === "number" && Number.isFinite(segmentDistance.distance_meters) && segmentDistance.distance_meters >= 0) {
    return segmentDistance.distance_meters / 1000;
  }

  return parseDistanceLabelKm(segmentDistance.label);
}

const MONTREAL_ISLAND_ROUTE_ALERT_CODE = "montreal_island";

function normalizeRouteSegmentAlerts(alerts) {
  if (!Array.isArray(alerts)) {
    return [];
  }

  return alerts
    .filter((alert) => alert && typeof alert === "object")
    .map((alert) => ({
      code: typeof alert.code === "string" ? alert.code.trim() : "",
      label: typeof alert.label === "string" ? alert.label.trim() : "",
      severity: typeof alert.severity === "string" ? alert.severity.trim() : "info",
      reason: typeof alert.reason === "string" ? alert.reason.trim() : "",
      suggestedAction: typeof alert.suggested_action === "string" ? alert.suggested_action.trim() : "",
    }))
    .filter((alert) => alert.code || alert.label || alert.reason || alert.suggestedAction);
}

function findMontrealRouteAlert(alerts) {
  return (Array.isArray(alerts) ? alerts : []).find((alert) => alert?.code === MONTREAL_ISLAND_ROUTE_ALERT_CODE) || null;
}

function normalizeRouteAlternativeSummary(summary) {
  if (!summary || typeof summary !== "object") {
    return null;
  }

  return {
    id: typeof summary.id === "string" ? summary.id.trim() : "",
    label: typeof summary.label === "string" ? summary.label.trim() : "",
    distanceLabel: typeof summary.distance_label === "string" ? summary.distance_label.trim() : "",
    durationLabel: typeof summary.duration_label === "string" ? summary.duration_label.trim() : "",
    estimatedArrivalAt: typeof summary.estimated_arrival_at === "string" ? summary.estimated_arrival_at.trim() : "",
    distanceMeters: Number.isFinite(Number(summary.distance_meters)) ? Number(summary.distance_meters) : null,
    durationSeconds: Number.isFinite(Number(summary.duration_seconds)) ? Number(summary.duration_seconds) : null,
    deltaDistanceMeters: Number.isFinite(Number(summary.delta_distance_meters)) ? Number(summary.delta_distance_meters) : null,
    deltaDurationSeconds: Number.isFinite(Number(summary.delta_duration_seconds)) ? Number(summary.delta_duration_seconds) : null,
    docks: Array.isArray(summary.docks) ? summary.docks.filter(Boolean) : [],
    segments: Array.isArray(summary.segments) ? summary.segments.filter(Boolean) : [],
  };
}

function normalizeRouteAlternativeCurrent(current) {
  return current && typeof current === "object" ? current : null;
}

function normalizeRouteAlternativeDeltaMinutes(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue / 60 : null;
}

function measureGeometryDistanceKm(geometry) {
  const routeLatLngs = collectRouteLatLngs(geometry);
  if (routeLatLngs.length < 2) {
    return null;
  }

  let distanceKm = 0;
  for (let index = 0; index < routeLatLngs.length - 1; index += 1) {
    const start = routeLatLngs[index];
    const end = routeLatLngs[index + 1];
    distanceKm += haversineKm(start[0], start[1], end[0], end[1]);
  }

  return distanceKm;
}

function resolveRouteSegmentDistance(segmentIndex, stops, route) {
  const hasExplicitBackendSegments = route && Array.isArray(route.segment_distances);
  const backendSegment = hasExplicitBackendSegments ? route.segment_distances[segmentIndex] : null;
  if (backendSegment && typeof backendSegment === "object") {
    const backendSource = normalizeRouteDistanceSource(backendSegment.source);
    const backendDistanceKm = resolveBackendSegmentDistanceKm(backendSegment);
    const routeSegmentAlerts = normalizeRouteSegmentAlerts(backendSegment.route_segment_alerts);
    const routeAlternativeCurrent = normalizeRouteAlternativeCurrent(backendSegment.route_alternative_current);
    const routeAlternativeSummary = normalizeRouteAlternativeSummary(backendSegment.route_alternative_summary);
    return {
      distanceKm: backendDistanceKm,
      manualBufferMinutes: normalizeTimelineDurationMinutes(backendSegment.manual_buffer_minutes),
      manualBufferNote: typeof backendSegment.manual_buffer_note === "string" ? backendSegment.manual_buffer_note.trim() : null,
      manualBufferReason: typeof backendSegment.manual_buffer_reason === "string" ? backendSegment.manual_buffer_reason.trim() : null,
      manualBufferUpdatedAt: typeof backendSegment.manual_buffer_updated_at === "string" ? backendSegment.manual_buffer_updated_at.trim() : null,
      manualBufferUpdatedBy: typeof backendSegment.manual_buffer_updated_by === "string" ? backendSegment.manual_buffer_updated_by.trim() : null,
      routeAlternativeApplied: backendSegment.route_alternative_applied === true,
      routeAlternativeAvailable: backendSegment.route_alternative_available === true,
      routeAlternativeDeltaDistanceMeters: Number.isFinite(Number(backendSegment.route_alternative_delta_distance_meters))
        ? Number(backendSegment.route_alternative_delta_distance_meters)
        : routeAlternativeSummary?.deltaDistanceMeters ?? null,
      routeAlternativeDeltaDurationMinutes: normalizeRouteAlternativeDeltaMinutes(backendSegment.route_alternative_delta_duration_seconds)
        ?? normalizeRouteAlternativeDeltaMinutes(routeAlternativeSummary?.deltaDurationSeconds),
      routeAlternativeId: typeof backendSegment.route_alternative_id === "string" ? backendSegment.route_alternative_id.trim() : "",
      routeAlternativeLabel: typeof backendSegment.route_alternative_label === "string" ? backendSegment.route_alternative_label.trim() : "",
      routeAlternativeReason: typeof backendSegment.route_alternative_reason === "string" ? backendSegment.route_alternative_reason.trim() : "",
      routeAlternativeCurrent,
      routeAlternativeSummary,
      routeSegmentAlerts,
      montrealRiskAlert: findMontrealRouteAlert(routeSegmentAlerts),
      source: backendDistanceKm != null ? backendSource : "unavailable",
    };
  }

  const geometry = route && Array.isArray(route.segment_geometries) ? route.segment_geometries[segmentIndex] : null;
  const geometryDistanceKm = measureGeometryDistanceKm(geometry);
  if (geometryDistanceKm != null) {
    return {
      distanceKm: geometryDistanceKm,
      manualBufferMinutes: null,
      manualBufferNote: null,
      manualBufferReason: null,
      manualBufferUpdatedAt: null,
      manualBufferUpdatedBy: null,
      routeAlternativeApplied: false,
      routeAlternativeAvailable: false,
      routeAlternativeDeltaDistanceMeters: null,
      routeAlternativeDeltaDurationMinutes: null,
      routeAlternativeId: "",
      routeAlternativeLabel: "",
      routeAlternativeReason: "",
      routeAlternativeCurrent: null,
      routeAlternativeSummary: null,
      routeSegmentAlerts: [],
      montrealRiskAlert: null,
      source: "geometrymeasured",
    };
  }

  if (!Array.isArray(stops) || stops.length < 2 || !hasStopCoordinates(stops[0]) || !hasStopCoordinates(stops[1])) {
    return {
      distanceKm: null,
      manualBufferMinutes: null,
      manualBufferNote: null,
      manualBufferReason: null,
      manualBufferUpdatedAt: null,
      manualBufferUpdatedBy: null,
      routeAlternativeApplied: false,
      routeAlternativeAvailable: false,
      routeAlternativeDeltaDistanceMeters: null,
      routeAlternativeDeltaDurationMinutes: null,
      routeAlternativeId: "",
      routeAlternativeLabel: "",
      routeAlternativeReason: "",
      routeAlternativeCurrent: null,
      routeAlternativeSummary: null,
      routeSegmentAlerts: [],
      montrealRiskAlert: null,
      source: hasExplicitBackendSegments ? "unavailable" : "unavailable",
    };
  }

  return {
    distanceKm: haversineKm(stops[0].latitude, stops[0].longitude, stops[1].latitude, stops[1].longitude),
    manualBufferMinutes: null,
    manualBufferNote: null,
    manualBufferReason: null,
    manualBufferUpdatedAt: null,
    manualBufferUpdatedBy: null,
    routeAlternativeApplied: false,
    routeAlternativeAvailable: false,
    routeAlternativeDeltaDistanceMeters: null,
    routeAlternativeDeltaDurationMinutes: null,
    routeAlternativeId: "",
    routeAlternativeLabel: "",
    routeAlternativeReason: "",
    routeAlternativeCurrent: null,
    routeAlternativeSummary: null,
    routeSegmentAlerts: [],
    montrealRiskAlert: null,
    source: "haversineestimate",
  };
}

function formatDistanceKm(distanceKm) {
  if (distanceKm == null || !Number.isFinite(distanceKm) || distanceKm < 0) {
    return null;
  }

  return `${Math.round(distanceKm)} km`;
}

function buildRouteSegment(index, rawStops, speedKmh, route = null, {
  preferScheduledDuration = true,
  tripFixed = 0,
} = {}) {
  const filteredStops = rawStops.filter(Boolean);
  const distance = resolveRouteSegmentDistance(index - 1, filteredStops, route);
  const distanceKm = distance.distanceKm;
  // Temps fixe par trajet (Paramètres système) : un trajet entre deux sites, dès 500 m, comme le backend.
  const tripFixedMinutes = distanceKm != null && distanceKm >= 0.5 && Number(tripFixed) > 0 ? Math.round(Number(tripFixed)) : 0;
  const speedMinutes = estimateSegmentDurationMinutes(distanceKm, speedKmh);
  const estimatedDurationMinutes = speedMinutes == null ? null : speedMinutes + tripFixedMinutes;
  const scheduledDurationMinutes = resolveRouteSegmentScheduledTravelDurationMinutes(filteredStops);
  const durationMinutes = preferScheduledDuration
    ? scheduledDurationMinutes ?? estimatedDurationMinutes
    : estimatedDurationMinutes ?? scheduledDurationMinutes;
  const displayDurationMinutes = durationMinutes;

  return {
    index,
    title: index === 1 ? "1ere route" : `${index}e route`,
    stops: filteredStops,
    distanceKm,
    distanceSource: distance.source,
    distanceSourceLabel: formatRouteDistanceSourceLabel(distance.source),
    distanceLabel: formatDistanceKm(distanceKm),
    estimatedDurationMinutes,
    manual_buffer_minutes: distance.manualBufferMinutes,
    manual_buffer_note: distance.manualBufferNote,
    manual_buffer_reason: distance.manualBufferReason,
    manual_buffer_updated_at: distance.manualBufferUpdatedAt,
    manual_buffer_updated_by: distance.manualBufferUpdatedBy,
    hasMontrealRiskAlert: Boolean(distance.montrealRiskAlert),
    montrealRiskAlert: distance.montrealRiskAlert,
    routeAlternativeApplied: Boolean(distance.routeAlternativeApplied),
    routeAlternativeAvailable: Boolean(distance.routeAlternativeAvailable),
    routeAlternativeDeltaDistanceMeters: distance.routeAlternativeDeltaDistanceMeters,
    routeAlternativeDeltaDurationMinutes: distance.routeAlternativeDeltaDurationMinutes,
    routeAlternativeId: distance.routeAlternativeId,
    routeAlternativeLabel: distance.routeAlternativeLabel,
    routeAlternativeReason: distance.routeAlternativeReason,
    routeAlternativeCurrent: distance.routeAlternativeCurrent,
    routeAlternativeSummary: distance.routeAlternativeSummary,
    routeSegmentAlerts: distance.routeSegmentAlerts,
    durationMinutes,
    displayDurationMinutes,
    scheduledDurationMinutes,
    speedKmh,
    tripFixedMinutes,
  };
}

function getRouteSegmentStartStop(segment) {
  return segment?.stops?.[0] || null;
}

function getRouteSegmentTargetStop(segment) {
  return segment?.stops?.[1] || segment?.stops?.[0] || null;
}

export function buildRouteMapStopKey(stop, { fallbackIndex = null } = {}) {
  if (!stop || typeof stop !== "object") {
    return "";
  }

  const sequence = Number(stop.sequence);
  if (Number.isInteger(sequence) && sequence > 0) {
    return `sequence:${sequence}`;
  }

  const stopType = typeof stop.stop_type === "string" ? stop.stop_type.trim() : "";
  const clientId = getClientSelectionId(stop);
  const deliveryId = normalizeTimelineStopMatchValue(stop.delivery_id);
  const orderId = normalizeTimelineStopMatchValue(stop.order_id);
  const stepId = normalizeTimelineStopMatchValue(stop.step_id);
  const latitude = typeof stop.latitude === "number" ? stop.latitude.toFixed(6) : "";
  const longitude = typeof stop.longitude === "number" ? stop.longitude.toFixed(6) : "";
  const address = typeof stop.address === "string" ? stop.address.trim().toLowerCase() : "";
  const resolvedKey = [
    stopType ? `type:${stopType}` : "",
    clientId ? `client:${clientId}` : "",
    deliveryId ? `delivery:${deliveryId}` : "",
    orderId ? `order:${orderId}` : "",
    stepId ? `step:${stepId}` : "",
    latitude && longitude ? `coords:${latitude}|${longitude}` : "",
    address ? `address:${address}` : "",
  ].filter(Boolean).join("::");

  if (resolvedKey) {
    return resolvedKey;
  }

  const numericFallbackIndex = Number(fallbackIndex);
  return Number.isInteger(numericFallbackIndex) ? `fallback:${numericFallbackIndex}` : "";
}

function buildDisplayedRouteSegment(ownerSegment, routeSegment, fromStop, toStop, overrides = {}) {
  const resolvedRouteSegment = routeSegment || ownerSegment || null;
  if (!resolvedRouteSegment) {
    return null;
  }

  const stops = [fromStop, toStop].filter(Boolean);

  return {
    ...resolvedRouteSegment,
    ...overrides,
    index: ownerSegment?.index ?? resolvedRouteSegment.index,
    title: ownerSegment?.title || resolvedRouteSegment.title,
    stops: stops.length ? stops : (Array.isArray(resolvedRouteSegment.stops) ? resolvedRouteSegment.stops : []),
  };
}

function buildZeroDistanceDisplayedRouteSegment(ownerSegment, stop) {
  return buildDisplayedRouteSegment(ownerSegment, ownerSegment, stop, stop, {
    distanceKm: 0,
    distanceLabel: formatDistanceKm(0),
    durationMinutes: 0,
  });
}

function resolveTimelineRouteTravelAttribution({
  displayedRouteSegment,
  displayContext = null,
  lastAttributedOutboundRouteIndex = null,
  segment = null,
  targetStop = null,
}) {
  let resolvedDisplayedRouteSegment = displayedRouteSegment;
  let travelDurationMinutes = normalizeTimelineDurationMinutes(displayedRouteSegment?.durationMinutes) || 0;
  let displayTravelDurationMinutes = resolveRouteSegmentDisplayDurationMinutes(displayedRouteSegment) ?? travelDurationMinutes;
  let nextLastAttributedOutboundRouteIndex = lastAttributedOutboundRouteIndex;

  const outboundRouteIndex = displayContext?.usesOutboundSegment
    && displayContext?.outboundSegment
    ? getTechnicalRouteSegmentIndex(displayContext.outboundSegment)
    : null;

  if (displayContext?.usesOutboundSegment && travelDurationMinutes > 0 && outboundRouteIndex != null) {
    nextLastAttributedOutboundRouteIndex = outboundRouteIndex;
  }

  const inboundRouteIndex = !displayContext?.usesOutboundSegment && travelDurationMinutes > 0
    ? getTechnicalRouteSegmentIndex(displayedRouteSegment)
      ?? getTechnicalRouteSegmentIndex(segment)
    : null;

  if (
    inboundRouteIndex != null
    && nextLastAttributedOutboundRouteIndex != null
    && inboundRouteIndex === nextLastAttributedOutboundRouteIndex
  ) {
    travelDurationMinutes = 0;
    displayTravelDurationMinutes = 0;
    resolvedDisplayedRouteSegment = buildZeroDistanceDisplayedRouteSegment(segment, targetStop);
    nextLastAttributedOutboundRouteIndex = null;
  }

  return {
    displayTravelDurationMinutes,
    displayedRouteSegment: resolvedDisplayedRouteSegment,
    lastAttributedOutboundRouteIndex: nextLastAttributedOutboundRouteIndex,
    travelDurationMinutes,
  };
}

function hasPositiveRouteSegmentDistanceOrDuration(segment) {
  const distanceKm = Number(segment?.distanceKm);
  const durationMinutes = Number(segment?.durationMinutes);

  return (Number.isFinite(distanceKm) && distanceKm > 0)
    || (Number.isFinite(durationMinutes) && durationMinutes > 0);
}

function resolveRouteSegmentDisplayContext(routeSegments, segmentIndex, visualLayout = null) {
  const segment = routeSegments[segmentIndex] || null;
  const previousStop = getRouteSegmentStartStop(segment);
  const currentStop = getRouteSegmentTargetStop(segment);
  const isDepotReturnSegment = currentStop?.stop_type === "depot_return";
  const isSameSiteFollower = Boolean(visualLayout?.isSameSiteSequenceGroup)
    && Number.isInteger(visualLayout.sameSiteGroupIndex)
    && visualLayout.sameSiteGroupIndex > 0;
  const sameSiteGroupSize = Number.isInteger(visualLayout?.sameSiteGroupSize)
    ? visualLayout.sameSiteGroupSize
    : 1;
  const sameSiteGroupRunStartIndex = Number.isInteger(visualLayout?.sameSiteGroupRunStartIndex)
    ? visualLayout.sameSiteGroupRunStartIndex
    : segmentIndex;
  const sameSiteGroupOutboundSegment = routeSegments[sameSiteGroupRunStartIndex + sameSiteGroupSize] || null;
  const shouldZeroSameSiteFollower = isSameSiteFollower
    && hasPositiveRouteSegmentDistanceOrDuration(sameSiteGroupOutboundSegment);
  const outboundSegmentIndex = Boolean(visualLayout?.isSameSiteSequenceGroup)
    && visualLayout?.sameSiteGroupIndex === 0
    && sameSiteGroupSize > 1
      ? sameSiteGroupRunStartIndex + sameSiteGroupSize
      : segmentIndex + 1;
  const outboundSegment = routeSegments[outboundSegmentIndex] || null;
  const nextStop = getRouteSegmentTargetStop(outboundSegment);
  const canUseOutboundSegment = Boolean(
    !isDepotReturnSegment
    && !isSameSiteFollower
    && outboundSegment
    && currentStop
    && nextStop
    && nextStop.stop_type !== "depot_return",
  );
  const routeFromStop = canUseOutboundSegment ? currentStop : previousStop;
  const routeToStop = canUseOutboundSegment ? nextStop : currentStop;
  const displayedRouteSegment = shouldZeroSameSiteFollower
    ? buildZeroDistanceDisplayedRouteSegment(segment, currentStop)
    : buildDisplayedRouteSegment(segment, canUseOutboundSegment ? outboundSegment : segment, routeFromStop, routeToStop);

  return {
    currentStop,
    displayedRouteSegment,
    isDepotReturnSegment,
    nextStop,
    outboundSegment: canUseOutboundSegment ? outboundSegment : null,
    previousStop,
    routeFromStop,
    routeToStop,
    shouldZeroSameSiteFollower,
    usesOutboundSegment: canUseOutboundSegment,
  };
}

function resolveTimelineManualBufferSourceSegment(routeSegments, segmentIndex, displayContext = null) {
  const currentStop = displayContext?.currentStop || getRouteSegmentTargetStop(routeSegments[segmentIndex]) || null;
  const currentStopType = typeof currentStop?.stop_type === "string" ? currentStop.stop_type.trim() : "";

  if (currentStopType !== "loading" && currentStopType !== "delivery") {
    return null;
  }

  const outboundSegment = routeSegments[segmentIndex + 1] || null;
  const outboundStartStop = getRouteSegmentStartStop(outboundSegment);
  if (buildRouteMapStopKey(outboundStartStop) && buildRouteMapStopKey(outboundStartStop) === buildRouteMapStopKey(currentStop)) {
    return outboundSegment;
  }

  const currentSegment = routeSegments[segmentIndex] || null;
  const currentSegmentStartStop = getRouteSegmentStartStop(currentSegment);
  if (buildRouteMapStopKey(currentSegmentStartStop) && buildRouteMapStopKey(currentSegmentStartStop) === buildRouteMapStopKey(currentStop)) {
    return currentSegment;
  }

  return null;
}

function getTechnicalRouteSegmentIndex(segment) {
  const resolvedSegmentIndex = Number(segment?.index);
  return Number.isInteger(resolvedSegmentIndex) && resolvedSegmentIndex > 0
    ? resolvedSegmentIndex
    : null;
}

function resolveRouteSequenceMapHighlightSegmentIndex(routeSegments, segmentIndex, visualLayout = null, displayContext = null) {
  const segment = routeSegments[segmentIndex] || null;
  const resolvedDisplayContext = displayContext || resolveRouteSegmentDisplayContext(routeSegments, segmentIndex, visualLayout);
  const sameSiteGroupRunStartIndex = Number.isInteger(visualLayout?.sameSiteGroupRunStartIndex)
    ? visualLayout.sameSiteGroupRunStartIndex
    : segmentIndex;
  const sameSiteGroupSize = Number.isInteger(visualLayout?.sameSiteGroupSize)
    ? visualLayout.sameSiteGroupSize
    : 1;
  const sameSiteGroupOutboundSegment = routeSegments[sameSiteGroupRunStartIndex + sameSiteGroupSize] || null;

  if (resolvedDisplayContext?.usesOutboundSegment) {
    return getTechnicalRouteSegmentIndex(resolvedDisplayContext.outboundSegment)
      ?? getTechnicalRouteSegmentIndex(segment);
  }

  if (
    visualLayout?.isSameSiteSequenceGroup
    && Number.isInteger(visualLayout?.sameSiteGroupIndex)
    && visualLayout.sameSiteGroupIndex > 0
  ) {
    return getTechnicalRouteSegmentIndex(segment)
      ?? getTechnicalRouteSegmentIndex(sameSiteGroupOutboundSegment);
  }

  return getTechnicalRouteSegmentIndex(segment);
}

function hasRouteAlternativeSignal(segment) {
  return Boolean(
    segment?.routeAlternativeApplied
    || segment?.routeAlternativeAvailable
    || segment?.routeAlternativeCurrent
    || segment?.routeAlternativeSummary,
  );
}

function collectRouteAlternativeSegmentIndexes(routeSegments, segmentIndex, visualLayout = null, displayContext = null, mapHighlightSegmentIndex = null) {
  const indexes = new Set();
  const appendSegmentIndex = (technicalIndex) => {
    const parsedIndex = Number(technicalIndex);
    if (!Number.isInteger(parsedIndex) || parsedIndex <= 0 || parsedIndex > routeSegments.length) {
      return;
    }

    const candidateSegment = routeSegments[parsedIndex - 1] || null;
    if (!hasRouteAlternativeSignal(candidateSegment)) {
      return;
    }

    indexes.add(parsedIndex);
  };
  const appendSegment = (candidateSegment) => {
    appendSegmentIndex(getTechnicalRouteSegmentIndex(candidateSegment));
  };

  appendSegmentIndex(mapHighlightSegmentIndex);
  appendSegment(routeSegments[segmentIndex] || null);

  if (visualLayout?.isSameSiteSequenceGroup) {
    const sameSiteGroupRunStartIndex = Number.isInteger(visualLayout?.sameSiteGroupRunStartIndex)
      ? visualLayout.sameSiteGroupRunStartIndex
      : segmentIndex;
    const sameSiteGroupSize = Number.isInteger(visualLayout?.sameSiteGroupSize)
      ? visualLayout.sameSiteGroupSize
      : 1;
    const sameSiteGroupRunEndIndex = Math.min(routeSegments.length, sameSiteGroupRunStartIndex + Math.max(sameSiteGroupSize, 1));
    for (let groupIndex = sameSiteGroupRunStartIndex; groupIndex < sameSiteGroupRunEndIndex; groupIndex += 1) {
      appendSegment(routeSegments[groupIndex] || null);
    }

    appendSegment(routeSegments[sameSiteGroupRunEndIndex] || null);

    if (displayContext?.outboundSegment) {
      appendSegment(displayContext.outboundSegment);
    }
  }

  return Array.from(indexes).sort((left, right) => left - right);
}

export function buildClientRouteHighlightSegmentMap(trip, assignment) {
  const clientRouteSegmentMap = new Map();
  const routeSegments = getTripRouteSegments(trip, assignment);
  if (!routeSegments.length) {
    return clientRouteSegmentMap;
  }

  const visualLayoutBySegment = buildTimelineVisualLayoutBySegment(routeSegments, trip);

  routeSegments.forEach((segment, segmentIndex) => {
    const visualLayout = visualLayoutBySegment[segmentIndex] || null;
    const displayContext = resolveRouteSegmentDisplayContext(routeSegments, segmentIndex, visualLayout);
    const currentStop = displayContext?.currentStop
      || getRouteSegmentTargetStop(segment)
      || getRouteSegmentStartStop(segment)
      || null;
    const clientId = getClientSelectionId(currentStop);
    const highlightSegmentIndex = resolveRouteSequenceMapHighlightSegmentIndex(
      routeSegments,
      segmentIndex,
      visualLayout,
      displayContext,
    );

    if (!clientId || !Number.isInteger(highlightSegmentIndex) || clientRouteSegmentMap.has(clientId)) {
      return;
    }

    clientRouteSegmentMap.set(clientId, highlightSegmentIndex);
  });

  return clientRouteSegmentMap;
}

function isOperationalRouteStop(stop) {
  return stop?.stop_type === "loading" || stop?.stop_type === "delivery";
}

function isStartOfDayPreloadedRouteSegment(segment, { allowLoadingContinuation = false } = {}) {
  const fromStop = getRouteSegmentStartStop(segment);
  const toStop = getRouteSegmentTargetStop(segment);
  const travelDurationMinutes = normalizeTimelineDurationMinutes(segment?.durationMinutes) || 0;

  return Boolean(
    toStop?.stop_type === "loading"
    && toStop?.is_preloaded_previous_day
    && travelDurationMinutes <= 0
    && (
      (allowLoadingContinuation && fromStop?.stop_type === "loading" && fromStop?.is_preloaded_previous_day)
      || (!allowLoadingContinuation && fromStop?.stop_type === "depot_start")
    ),
  );
}

function buildStartOfDayPreloadMetadata(routeSegments = []) {
  const metadata = Array.from({ length: routeSegments.length }, () => ({
    isStartOfDayPreload: false,
    preloadCount: 0,
    preloadIndex: -1,
  }));
  let preloadCount = 0;
  let allowLoadingContinuation = false;

  while (
    preloadCount < routeSegments.length
    && isStartOfDayPreloadedRouteSegment(routeSegments[preloadCount], { allowLoadingContinuation })
  ) {
    metadata[preloadCount] = {
      isStartOfDayPreload: true,
      preloadCount: 0,
      preloadIndex: preloadCount,
    };
    preloadCount += 1;
    allowLoadingContinuation = true;
  }

  if (preloadCount > 0) {
    for (let preloadIndex = 0; preloadIndex < preloadCount; preloadIndex += 1) {
      metadata[preloadIndex] = {
        ...metadata[preloadIndex],
        preloadCount,
      };
    }
  }

  return metadata;
}

function buildVisibleRouteSegmentEntries(routeSegments = [], preloadMetadataBySegment = []) {
  const entries = [];
  const visibleSequenceBySegmentIndex = new Map();

  (Array.isArray(routeSegments) ? routeSegments : []).forEach((segment, segmentIndex) => {
    if (preloadMetadataBySegment[segmentIndex]?.isStartOfDayPreload) {
      return;
    }

    visibleSequenceBySegmentIndex.set(segmentIndex, entries.length + 1);
    entries.push({ segment, segmentIndex });
  });

  return {
    entries,
    visibleSequenceBySegmentIndex,
  };
}

function resolveStartOfDayPreloadedLoadTons(routeSegments = [], preloadMetadataBySegment = []) {
  return (Array.isArray(routeSegments) ? routeSegments : []).reduce((total, segment, segmentIndex) => {
    if (!preloadMetadataBySegment[segmentIndex]?.isStartOfDayPreload) {
      return total;
    }

    const quantityTons = Number(getRouteSegmentTargetStop(segment)?.quantity_tons);
    return Number.isFinite(quantityTons) && quantityTons > 0 ? total + quantityTons : total;
  }, 0);
}

function getInitialDepotTravelSegment(routeSegments = [], preloadMetadataBySegment = [], initialPreloadSteps = []) {
  const resolvedRouteSegments = Array.isArray(routeSegments) ? routeSegments : [];
  const firstInitialPreloadStep = Array.isArray(initialPreloadSteps)
    ? initialPreloadSteps.find(isInitialPreloadStep) || null
    : null;
  const firstVisibleSegmentIndex = resolvedRouteSegments.findIndex((_, segmentIndex) => !preloadMetadataBySegment[segmentIndex]?.isStartOfDayPreload);

  if (firstVisibleSegmentIndex < 0) {
    return null;
  }

  const firstVisibleSegment = resolvedRouteSegments[firstVisibleSegmentIndex] || null;
  const fromStop = getRouteSegmentStartStop(firstVisibleSegment);
  const toStop = getRouteSegmentTargetStop(firstVisibleSegment);
  const hasLeadingStartOfDayPreload = firstVisibleSegmentIndex > 0
    && preloadMetadataBySegment.slice(0, firstVisibleSegmentIndex).every((metadata) => metadata?.isStartOfDayPreload);

  if (hasLeadingStartOfDayPreload) {
    return {
      anchorStop: getRouteSegmentTargetStop(resolvedRouteSegments[0] || null),
      anchorStep: firstInitialPreloadStep,
      fromStop: getRouteSegmentStartStop(resolvedRouteSegments[0] || null) || fromStop || null,
      segment: firstVisibleSegment,
      segmentIndex: firstVisibleSegmentIndex,
      toStop: toStop || fromStop || null,
    };
  }

  return fromStop?.stop_type === "depot_start" && isOperationalRouteStop(toStop)
    ? {
        anchorStop: null,
        anchorStep: firstInitialPreloadStep,
        fromStop,
        segment: firstVisibleSegment,
        segmentIndex: firstVisibleSegmentIndex,
        toStop,
      }
    : null;
}

function buildInitialDepotTravelDisplayedRouteSegment(segment) {
  const ownerSegment = segment?.segment || segment || null;
  const fromStop = segment?.fromStop || getRouteSegmentStartStop(ownerSegment);
  const toStop = segment?.toStop || getRouteSegmentTargetStop(ownerSegment) || fromStop;

  return buildDisplayedRouteSegment(
    ownerSegment,
    ownerSegment,
    fromStop,
    toStop,
    {
      isInitialRouteSegment: true,
      serviceContextStop: "from",
    },
  );
}

function isInitialPreloadStep(step) {
  return Boolean(
    step
    && step.step_type === "loading"
    && step.is_initial_preload === true,
  );
}

function getTripInitialPreloadSteps(trip) {
  return Array.isArray(trip?.operational_steps)
    ? trip.operational_steps.filter(isInitialPreloadStep)
    : [];
}

function resolveInitialPreloadLoadTons(initialPreloadSteps = []) {
  return (Array.isArray(initialPreloadSteps) ? initialPreloadSteps : []).reduce((total, step) => {
    const quantityTons = Number(step?.quantity_tons);
    return Number.isFinite(quantityTons) && quantityTons > 0 ? total + quantityTons : total;
  }, 0);
}

function normalizeEndOfDayLoadValues(values = []) {
  const resolvedValues = Array.isArray(values) ? values : [values];
  return Array.from(new Set(
    resolvedValues
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .map((value) => normalizeTimelineStopMatchValue(value))
      .filter(Boolean),
  ));
}

function buildEndOfDayLoadSourcePreloadContext(marker = null) {
  if (!marker || typeof marker !== "object") {
    return null;
  }

  const markerItems = Array.isArray(marker?.items) ? marker.items.filter(Boolean) : [];
  const markerKey = normalizeTimelineStopMatchValue(marker?.marker_key || marker?.end_of_day_load_marker_key);
  const orderIds = normalizeEndOfDayLoadValues([
    marker?.order_ids,
    markerItems.map((item) => item?.order_id),
  ]);
  const deliveryIds = normalizeEndOfDayLoadValues([
    marker?.delivery_ids,
    markerItems.map((item) => item?.delivery_id),
  ]);
  const loadingStepIds = normalizeEndOfDayLoadValues(markerItems.map((item) => item?.loading_step_id || item?.step_id));

  if (!markerKey && !orderIds.length && !deliveryIds.length && !loadingStepIds.length) {
    return null;
  }

  return {
    deliveryMode: normalizeTimelineStopMatchValue(marker?.delivery_mode),
    deliveryIds,
    loadingStepIds,
    markerDispatchDate: normalizeTimelineStopMatchValue(marker?.marker_dispatch_date),
    markerKey,
    orderIds,
    sourceDispatchDate: normalizeTimelineStopMatchValue(marker?.source_dispatch_date),
    // Camion qui a chargé : souvent un autre que celui qui livre.
    sourceTruckId: normalizeTimelineStopMatchValue(marker?.source_truck_id) || resolveEndOfDayLoadMarkerSourceTruckId(marker),
    targetPeriod: normalizeTimelineStopMatchValue(marker?.target_period),
    targetDispatchDate: normalizeTimelineStopMatchValue(marker?.target_dispatch_date),
  };
}

function buildEndOfDayLoadSourcePreloadContextIndex(markers = []) {
  const index = {
    byDeliveryId: new Map(),
    byLoadingStepId: new Map(),
    byMarkerKey: new Map(),
    byOrderId: new Map(),
    contexts: [],
  };

  (Array.isArray(markers) ? markers : []).forEach((marker) => {
    const context = buildEndOfDayLoadSourcePreloadContext(marker);
    if (!context) {
      return;
    }

    index.contexts.push(context);

    if (context.markerKey) {
      index.byMarkerKey.set(context.markerKey, context);
    }

    context.orderIds.forEach((orderId) => {
      index.byOrderId.set(orderId, context);
    });
    context.deliveryIds.forEach((deliveryId) => {
      index.byDeliveryId.set(deliveryId, context);
    });
    context.loadingStepIds.forEach((loadingStepId) => {
      index.byLoadingStepId.set(loadingStepId, context);
    });
  });

  return index;
}

function buildInitialPreloadSourcePreloadCoverage(trips = [], contextIndex = null) {
  const coverage = {
    markerKeys: new Set(),
    orderIds: new Set(),
  };

  (Array.isArray(trips) ? trips : []).forEach((trip) => {
    getTripInitialPreloadSteps(trip).forEach((step) => {
      const sourcePreloadContext = resolveEndOfDayLoadSourcePreloadContextForStep(step, contextIndex);
      if (!sourcePreloadContext) {
        return;
      }

      const markerKey = sourcePreloadContext.markerKey
        || normalizeTimelineStopMatchValue(step?.endOfDayLoadMarkerKey || step?.end_of_day_load_marker_key || step?.marker_key);
      if (markerKey) {
        coverage.markerKeys.add(markerKey);
      }

      const orderIds = sourcePreloadContext.orderIds?.length
        ? sourcePreloadContext.orderIds
        : normalizeEndOfDayLoadValues(step?.order_id);
      orderIds.forEach((orderId) => {
        coverage.orderIds.add(orderId);
      });
    });
  });

  return coverage;
}

function isEndOfDayLoadSourcePreloadMarkerCoveredByInitialPreload(marker = null, coverage = null) {
  if (!marker || !coverage) {
    return false;
  }

  const context = buildEndOfDayLoadSourcePreloadContext(marker);
  if (!context) {
    return false;
  }

  if (context.markerKey && coverage.markerKeys?.has(context.markerKey)) {
    return true;
  }

  return context.orderIds.some((orderId) => coverage.orderIds?.has(orderId));
}

function filterEndOfDayLoadSourcePreloadsCoveredByInitialPreloads(markers = [], trips = []) {
  const safeMarkers = Array.isArray(markers) ? markers.filter(Boolean) : [];
  if (!safeMarkers.length) {
    return [];
  }

  const contextIndex = buildEndOfDayLoadSourcePreloadContextIndex(safeMarkers);
  const coverage = buildInitialPreloadSourcePreloadCoverage(trips, contextIndex);
  if (!coverage.markerKeys.size && !coverage.orderIds.size) {
    return safeMarkers;
  }

  return safeMarkers.filter((marker) => !isEndOfDayLoadSourcePreloadMarkerCoveredByInitialPreload(marker, coverage));
}

function resolveEndOfDayLoadSourcePreloadContextForStep(step, contextIndex = null) {
  const directMarkerKey = normalizeTimelineStopMatchValue(
    step?.endOfDayLoadMarkerKey
    || step?.end_of_day_load_marker_key
    || step?.marker_key,
  );
  const orderId = normalizeTimelineStopMatchValue(step?.order_id);
  const deliveryId = normalizeTimelineStopMatchValue(step?.delivery_id);
  const stepId = normalizeTimelineStopMatchValue(step?.step_id || step?.loading_step_id);

  const indexedContext = (directMarkerKey && contextIndex?.byMarkerKey?.get(directMarkerKey))
    || (orderId && contextIndex?.byOrderId?.get(orderId))
    || (deliveryId && contextIndex?.byDeliveryId?.get(deliveryId))
    || (stepId && contextIndex?.byLoadingStepId?.get(stepId))
    || null;

  if (indexedContext) {
    return indexedContext;
  }

  const hasSourcePreloadFlag = step?.is_end_of_day_load_source_preload === true
    || step?.isEndOfDayLoadSourcePreload === true;
  if (!hasSourcePreloadFlag && !directMarkerKey) {
    return null;
  }

  return {
    deliveryIds: normalizeEndOfDayLoadValues(deliveryId),
    deliveryMode: normalizeTimelineStopMatchValue(
      step?.endOfDayLoadDeliveryMode
      || step?.end_of_day_load_delivery_mode
      || step?.delivery_mode,
    ),
    loadingStepIds: normalizeEndOfDayLoadValues(stepId),
    markerDispatchDate: normalizeTimelineStopMatchValue(
      step?.endOfDayLoadMarkerDispatchDate
      || step?.end_of_day_load_marker_dispatch_date
      || step?.marker_dispatch_date,
    ),
    markerKey: directMarkerKey,
    orderIds: normalizeEndOfDayLoadValues(orderId),
    sourceDispatchDate: normalizeTimelineStopMatchValue(
      step?.endOfDayLoadSourceDispatchDate
      || step?.end_of_day_load_source_dispatch_date
      || step?.source_dispatch_date,
    ),
    sourceTruckId: normalizeTimelineStopMatchValue(
      step?.endOfDayLoadSourceTruckId
      || step?.end_of_day_load_source_truck_id
      || step?.source_truck_id,
    ),
    targetPeriod: normalizeTimelineStopMatchValue(
      step?.endOfDayLoadTargetPeriod
      || step?.end_of_day_load_target_period
      || step?.target_period,
    ),
    targetDispatchDate: normalizeTimelineStopMatchValue(
      step?.endOfDayLoadTargetDispatchDate
      || step?.end_of_day_load_target_dispatch_date
      || step?.target_dispatch_date,
    ),
  };
}

function buildInitialPreloadStopFromStep(step, sourcePreloadContextIndex = null) {
  const sourcePreloadContext = resolveEndOfDayLoadSourcePreloadContextForStep(step, sourcePreloadContextIndex);
  const isEndOfDayLoadSourcePreload = Boolean(sourcePreloadContext);
  const orderIds = sourcePreloadContext?.orderIds?.length
    ? sourcePreloadContext.orderIds
    : normalizeEndOfDayLoadValues(step?.order_id);
  const deliveryIds = sourcePreloadContext?.deliveryIds?.length
    ? sourcePreloadContext.deliveryIds
    : normalizeEndOfDayLoadValues(step?.delivery_id);
  const endOfDayLoadDisplayMode = isEndOfDayLoadSourcePreload
    ? resolveEndOfDayLoadDisplayModeValue({
      delivery_mode: sourcePreloadContext?.deliveryMode,
      source_dispatch_date: sourcePreloadContext?.sourceDispatchDate,
      target_dispatch_date: sourcePreloadContext?.targetDispatchDate,
      target_period: sourcePreloadContext?.targetPeriod,
    }, { sourcePreload: true })
    : "";

  return {
    address: step?.address || "",
    client_name: step?.client_name || "",
    compartment_allocations: Array.isArray(step?.compartment_allocations) ? step.compartment_allocations : [],
    endOfDayLoadDeliveryIds: deliveryIds,
    endOfDayLoadDeliveryMode: sourcePreloadContext?.deliveryMode || "",
    endOfDayLoadDisplayMode,
    endOfDayLoadMarkerDispatchDate: sourcePreloadContext?.markerDispatchDate || "",
    endOfDayLoadMarkerKey: sourcePreloadContext?.markerKey || "",
    endOfDayLoadOrderIds: orderIds,
    endOfDayLoadSourceDispatchDate: sourcePreloadContext?.sourceDispatchDate || "",
    endOfDayLoadSourceTruckId: sourcePreloadContext?.sourceTruckId || "",
    endOfDayLoadTargetDispatchDate: sourcePreloadContext?.targetDispatchDate || "",
    endOfDayLoadTargetPeriod: sourcePreloadContext?.targetPeriod || "",
    grain_type: step?.grain_type || "",
    isEndOfDayLoad: false,
    isEndOfDayLoadSourcePreload: isEndOfDayLoadSourcePreload,
    isInitialPreload: true,
    is_end_of_day_load: false,
    is_end_of_day_load_source_preload: isEndOfDayLoadSourcePreload,
    is_initial_preload: true,
    is_preloaded_previous_day: true,
    label: step?.label || step?.client_name || step?.order_id || "Chargement veille",
    marker_key: sourcePreloadContext?.markerKey || "",
    order_id: step?.order_id || "",
    quantity_tons: typeof step?.quantity_tons === "number" ? step.quantity_tons : 0,
    service_time_minutes: 0,
    step_id: step?.step_id || "",
    stop_type: "loading",
  };
}

function buildInitialPreloadDisplayedRouteSegment(stop) {
  return {
    distanceKm: 0,
    distanceLabel: "0 km",
    distanceSource: "geometrymeasured",
    distanceSourceLabel: null,
    displayDurationMinutes: 0,
    durationMinutes: 0,
    estimatedDurationMinutes: 0,
    index: 0,
    isEndOfDayLoad: false,
    isEndOfDayLoadSourcePreload: stop?.isEndOfDayLoadSourcePreload === true || stop?.is_end_of_day_load_source_preload === true,
    isInitialPreload: true,
    scheduledDurationMinutes: 0,
    speedKmh: null,
    stops: [stop, stop],
  };
}

function resolveInitialPreloadOperationGroupKey(stop, fallbackIndex = 0) {
  const siteGroupKey = resolveTimelineStopGroupKey(stop)
    || `initial-preload:${normalizeTimelineStopMatchValue(stop?.order_id) || fallbackIndex}`;
  const isEndOfDayLoadSourcePreload = stop?.isEndOfDayLoadSourcePreload === true
    || stop?.is_end_of_day_load_source_preload === true;
  if (!isEndOfDayLoadSourcePreload) {
    return siteGroupKey;
  }

  const markerKey = normalizeTimelineStopMatchValue(stop?.endOfDayLoadMarkerKey || stop?.marker_key)
    || normalizeTimelineStopMatchValue(stop?.order_id)
    || String(fallbackIndex);
  return `end-of-day-load:${markerKey}:${siteGroupKey}`;
}

// Les chargements de la veille n'ont pas d'heure : ceux d'un même Chargé PM (même site) se suivent,
// même quand les livraisons les alternent, pour garder la case « 1-2 » de la veille au soir.
function groupInitialPreloadStopsBySite(preloadStops = []) {
  const groupKeys = preloadStops.map((stop, index) => resolveInitialPreloadOperationGroupKey(stop, index));
  return preloadStops
    .map((stop, index) => ({ rank: groupKeys.indexOf(groupKeys[index]), stop }))
    .sort((left, right) => left.rank - right.rank)
    .map(({ stop }) => stop);
}

function buildInitialPreloadGroupMetadata(preloadStops = []) {
  const metadata = Array.from({ length: Array.isArray(preloadStops) ? preloadStops.length : 0 }, () => ({
    isSameSiteSequenceGroup: false,
    sameSiteGroupContainsMixedStopTypes: false,
    sameSiteGroupIndex: 0,
    sameSiteGroupKey: "",
    sameSiteGroupSize: 1,
    sameSiteGroupStartOffset: 0,
  }));
  let cursor = 0;

  while (cursor < metadata.length) {
    const currentStop = preloadStops[cursor] || null;
    const baseGroupKey = resolveInitialPreloadOperationGroupKey(currentStop, cursor);
    let groupEnd = cursor + 1;

    while (groupEnd < metadata.length) {
      const candidateStop = preloadStops[groupEnd] || null;
      if (resolveInitialPreloadOperationGroupKey(candidateStop, groupEnd) !== baseGroupKey) {
        break;
      }

      groupEnd += 1;
    }

    const groupSize = groupEnd - cursor;
    if (groupSize > 1) {
      const sameSiteGroupKey = `initial-preload:${baseGroupKey}`;
      for (let groupIndex = cursor; groupIndex < groupEnd; groupIndex += 1) {
        metadata[groupIndex] = {
          ...metadata[groupIndex],
          isSameSiteSequenceGroup: true,
          sameSiteGroupIndex: groupIndex - cursor,
          sameSiteGroupKey,
          sameSiteGroupSize: groupSize,
          sameSiteGroupStartOffset: cursor,
        };
      }
    } else {
      metadata[cursor] = {
        ...metadata[cursor],
        sameSiteGroupStartOffset: cursor,
      };
    }

    cursor = groupEnd;
  }

  return metadata;
}

function shouldPreferScheduledRouteDurations(state = null) {
  const hovercardContext = state?.resultsData && typeof state.resultsData.fleet_segment_hovercard_context === "object"
    ? state.resultsData.fleet_segment_hovercard_context
    : null;

  if (hovercardContext && Object.prototype.hasOwnProperty.call(hovercardContext, "here_time_aware_routing_enabled")) {
    return hovercardContext.here_time_aware_routing_enabled === true;
  }

  return true;
}

function buildRouteDurationOptionsFromState(state = null) {
  return {
    preferScheduledDuration: shouldPreferScheduledRouteDurations(state),
  };
}

export function getTripRouteSegments(trip, assignment, {
  preferScheduledDuration = true,
} = {}) {
  const stops = getTripRoutableStops(trip);
  if (!stops.length) {
    return [];
  }

  const speedKmh = assignment && typeof assignment.avg_speed_kmh === "number" ? assignment.avg_speed_kmh : null;
  const route = trip && typeof trip.route === "object" ? trip.route : null;

  return buildRouteSegmentStopGroups(stops)
    .map((segmentStops, segmentIndex) => buildRouteSegment(segmentIndex + 1, segmentStops, speedKmh, route, {
      preferScheduledDuration,
      tripFixed: assignment?.trip_fixed_minutes,
    }));
}

function getRouteSegmentStopLabel(stop) {
  if (!stop || typeof stop !== "object") {
    return "cet arrêt";
  }

  return normalizeClientDisplayLabel(stop.label || stop.client_name) || stop.order_id || stop.delivery_id || "cet arrêt";
}

function buildOperationRepetitionMetadata(routeSegments = []) {
  const countsByLabel = new Map();
  const occurrencesByLabel = new Map();
  const metadataBySegmentIndex = new Map();

  routeSegments.forEach((segment) => {
    const operationStop = getRouteSegmentTargetStop(segment);
    const operationLabel = getRouteStopLabel(operationStop).trim().toLowerCase();
    if (!operationLabel) {
      return;
    }

    countsByLabel.set(operationLabel, (countsByLabel.get(operationLabel) || 0) + 1);
  });

  routeSegments.forEach((segment) => {
    const operationStop = getRouteSegmentTargetStop(segment);
    const operationLabel = getRouteStopLabel(operationStop).trim().toLowerCase();
    if (!operationLabel) {
      return;
    }

    const occurrenceIndex = (occurrencesByLabel.get(operationLabel) || 0) + 1;
    occurrencesByLabel.set(operationLabel, occurrenceIndex);
    metadataBySegmentIndex.set(segment?.index, {
      occurrenceCount: countsByLabel.get(operationLabel) || 1,
      occurrenceIndex,
    });
  });

  return metadataBySegmentIndex;
}

function buildOperationIdentity(stop, {
  assignmentId = "",
  trip = null,
  tripId = "",
  tripSequence = 0,
  repetitionMetadata = null,
} = {}) {
  const operationLabel = getRouteStopLabel(stop);
  const operationAddress = getRouteStopAddressLabel(stop?.address);
  const operationTypeLabel = getStopTypeLabel(stop);
  const operationOrderId = typeof stop?.order_id === "string" ? stop.order_id.trim() : "";
  const operationDeliveryId = typeof stop?.delivery_id === "string" ? stop.delivery_id.trim() : "";
  const orders = Array.isArray(trip?.orders) ? trip.orders.filter(Boolean) : [];
  const matchedOrder = orders.find((candidate) => (
    (operationOrderId && candidate?.order_id === operationOrderId)
    || (operationDeliveryId && Array.isArray(candidate?.deliveries) && candidate.deliveries.some((delivery) => delivery?.delivery_id === operationDeliveryId))
  )) || null;
  const matchedDelivery = Array.isArray(matchedOrder?.deliveries)
    ? (matchedOrder.deliveries.find((delivery) => operationDeliveryId && delivery?.delivery_id === operationDeliveryId) || matchedOrder.deliveries[0] || null)
    : null;
  const operationOwnerLabel = normalizeClientDisplayLabel(
    matchedDelivery?.client_name
    || matchedOrder?.client_name
    || (stop?.stop_type === "delivery" ? stop?.client_name || stop?.label : ""),
  );
  const occurrenceCount = Number(repetitionMetadata?.occurrenceCount) || 1;
  const occurrenceIndex = Number(repetitionMetadata?.occurrenceIndex) || 1;
  const visitLabel = occurrenceCount > 1 ? `Visite ${occurrenceIndex}/${occurrenceCount}` : "";
  const referenceLabel = operationOrderId || operationDeliveryId;
  const identityToken = operationDeliveryId
    || operationOrderId
    || resolveTimelineStopGroupKey(stop)
    || operationLabel
    || stop?.stop_type
    || "operation";
  const normalizedAspirator = [matchedOrder?.aspirator, matchedDelivery?.aspirator, stop?.aspirator]
    .find((value) => typeof value === "string" && value.trim()) || "";
  const requiresAspirator = ["oui", "long"].includes(String(normalizedAspirator).trim().toLowerCase());
  const aspiratorMissionRouteSignature = requiresAspirator
    ? resolveTimelineOrderRouteSignature(matchedOrder)
    : "";
  const aspiratorMissionGroupKey = requiresAspirator
    ? (
        aspiratorMissionRouteSignature
          ? `signature:${aspiratorMissionRouteSignature}`
          : operationOrderId
            ? `order:${operationOrderId}`
            : operationDeliveryId
              ? `delivery:${operationDeliveryId}`
              : ""
      )
    : "";
  const normalizedOntario = [matchedOrder?.ontario, matchedDelivery?.ontario, stop?.ontario]
    .find((value) => typeof value === "string" && value.trim()) || "";
  const requiresOntario = String(normalizedOntario).trim().toLowerCase() === "oui";

  return {
    aspiratorMissionGroupKey,
    aspiratorMissionRouteSignature,
    aspiratorRequirementLabel: requiresAspirator ? "Aspiration requise" : "",
    operationAddress,
    operationDeliveryId,
    operationDisambiguatorLabel: visitLabel
      ? (referenceLabel ? `${visitLabel} · ${referenceLabel}` : visitLabel)
      : "",
    operationKey: `${assignmentId || "assignment"}::${tripId || "trip"}::${tripSequence || 0}::${identityToken}`,
    operationLabel,
    operationOwnerLabel,
    operationOrderId,
    operationTypeLabel,
    requiresAspirator,
    requiresOntario,
  };
}

function buildRouteSegmentDistanceTooltip(segment) {
  if (segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload) {
    return "Chargé la veille en PM : aucun trajet ce jour-là.";
  }

  if (segment?.isInitialPreload) {
    return "Préchargement veille hors plage chauffeur: distance routière comptée à 0 km. Le stock est déjà à bord au début logique de la journée.";
  }

  if (!segment) {
    return "Distance du trajet entre deux arrêts de la tournée.";
  }

  const fromStop = segment.stops?.[0] || null;
  const toStop = segment.stops?.[1] || null;
  const sourceLabel = getRouteHoverDistanceSourceLabel(segment.distanceSource, segment.distanceSourceLabel);
  const sourceDescription = describeRouteDistanceSource(segment.distanceSource);
  const impactSentence = "La vitesse du camion ne change pas cette distance. Elle sert seulement à calculer la durée.";

  if (!segment.distanceLabel) {
    return `Distance entre ${getRouteSegmentStopLabel(fromStop)} et ${getRouteSegmentStopLabel(toStop)} indisponible. Source: ${sourceLabel}. ${sourceDescription}. ${impactSentence}`;
  }

  return `Distance entre ${getRouteSegmentStopLabel(fromStop)} et ${getRouteSegmentStopLabel(toStop)}: ${segment.distanceLabel}. Source: ${sourceLabel}. Cette valeur provient de ${sourceDescription}. ${impactSentence}`;
}

function hasExplicitStopServiceDuration(stop) {
  return typeof stop?.service_time_minutes === "number"
    && (stop.stop_type === "loading" || stop.stop_type === "delivery" || stop.service_time_minutes > 0);
}

function resolveDisplayedStopServiceMinutes(stop, serviceMinutesOverride = null) {
  if (serviceMinutesOverride != null && Number.isFinite(Number(serviceMinutesOverride))) {
    return Math.max(0, Number(serviceMinutesOverride));
  }

  return typeof stop?.service_time_minutes === "number" ? stop.service_time_minutes : null;
}

function formatDisplayedServiceDurationLabel(minutes) {
  const normalizedMinutes = Number(minutes);

  if (!Number.isFinite(normalizedMinutes) || normalizedMinutes < 0) {
    return "0 min";
  }

  return formatDurationShort(normalizedMinutes) || "0 min";
}

function buildRouteSegmentServiceTooltip(stop, serviceMinutesOverride = null) {
  if (stop?.is_end_of_day_load_source_preload || stop?.isEndOfDayLoadSourcePreload) {
    return `Temps de chargement PM sur ${getRouteSegmentStopLabel(stop)}: 0 min. Ce chargement est représenté hors plage chauffeur et hors temps de chargement journalier.`;
  }

  if (stop?.is_initial_preload || stop?.isInitialPreload) {
    return `Temps de chargement sur ${getRouteSegmentStopLabel(stop)}: 0 min. Ce préchargement est réalisé la veille, hors plage chauffeur et hors temps de chargement journalier.`;
  }

  const displayedServiceMinutes = resolveDisplayedStopServiceMinutes(stop, serviceMinutesOverride);
  const displayedServiceLabel = formatDisplayedServiceDurationLabel(displayedServiceMinutes);
  const hasVisualRedistribution = displayedServiceMinutes != null
    && typeof stop?.service_time_minutes === "number"
    && displayedServiceMinutes !== stop.service_time_minutes;

  if (!stop || displayedServiceMinutes == null) {
    return "Temps passé à l'arrêt.";
  }

  if (stop.stop_type === "loading") {
    if (displayedServiceMinutes === 0) {
      return `Temps de chargement sur ${getRouteSegmentStopLabel(stop)}: 0 min. Ce service est déjà compté sur le premier chargement de cette série de chargements consécutifs.`;
    }
    if (hasVisualRedistribution) {
      return `Temps de chargement affiché sur ${getRouteSegmentStopLabel(stop)}: ${displayedServiceLabel}. Dans la vue flotte, le temps total de la série de chargements consécutifs est regroupé sur le premier chargement pour refléter le tonnage total chargé avant la phase de livraison.`;
    }
    return `Temps de chargement sur ${getRouteSegmentStopLabel(stop)}: ${displayedServiceLabel}. Cette valeur est calculée à partir du tonnage chargé et du débit de chargement du camion (t/h), et ne correspond pas au trajet.`;
  }

  if (stop.stop_type === "delivery") {
    if (displayedServiceMinutes === 0) {
      return `Temps de déchargement sur ${getRouteSegmentStopLabel(stop)}: 0 min. Ce service a déjà été compté sur la visite précédente au même site.`;
    }
    if (hasVisualRedistribution) {
      return `Temps de déchargement affiché sur ${getRouteSegmentStopLabel(stop)}: ${displayedServiceLabel}. Dans la vue flotte, le temps total de déchargement du même site est réparti entre les arrêts consécutifs pour garder une lecture par étape.`;
    }
    return `Temps de déchargement sur ${getRouteSegmentStopLabel(stop)}: ${displayedServiceLabel}. Cette valeur est calculée à partir du tonnage déchargé et du débit de déchargement du camion (t/h), et ne correspond pas au trajet.`;
  }

  if (stop.stop_type === "depot_start") {
    if (displayedServiceMinutes <= 0) {
      return "Temps d'inspection camion au départ: 0 min.";
    }

    return `Temps d'inspection camion au départ sur ${getRouteSegmentStopLabel(stop)}: ${displayedServiceLabel}. Cette inspection est incluse dans l'étape Inspection avant le premier trajet.`;
  }

  if (displayedServiceMinutes <= 0) {
    return "Temps passé à l'arrêt.";
  }

  return `Temps d'opération à l'arrêt sur ${getRouteSegmentStopLabel(stop)}: ${displayedServiceLabel}. Cette valeur ne correspond pas au trajet.`;
}

function getRouteSegmentDurationServiceContext(segment, serviceMinutesOverride = null) {
  if (segment?.suppressServiceContext) {
    return null;
  }

  const stops = Array.isArray(segment?.stops) ? segment.stops : [];
  const targetStop = segment?.serviceContextStop === "from"
    ? stops[0] || stops[stops.length - 1] || null
    : stops[1] || stops[stops.length - 1] || null;
  const displayedServiceMinutes = resolveDisplayedStopServiceMinutes(targetStop, serviceMinutesOverride);

  if (!hasExplicitStopServiceDuration(targetStop)) {
    return null;
  }

  if (targetStop.stop_type === "loading") {
    return {
      lineLabel: "Chargement exclu",
      noteLabel: "Temps de chargement",
      subjectLabel: "chargement",
      stopLabel: getRouteSegmentStopLabel(targetStop),
      value: formatDisplayedServiceDurationLabel(displayedServiceMinutes),
    };
  }

  if (targetStop.stop_type === "delivery") {
    return {
      lineLabel: "Déchargement exclu",
      noteLabel: "Temps de déchargement",
      subjectLabel: "déchargement",
      stopLabel: getRouteSegmentStopLabel(targetStop),
      value: formatDisplayedServiceDurationLabel(displayedServiceMinutes),
    };
  }

  if (targetStop.stop_type === "depot_start") {
    return {
      lineLabel: "Inspection camion",
      noteLabel: "Temps d'inspection camion",
      subjectLabel: "inspection camion",
      stopLabel: getRouteSegmentStopLabel(targetStop),
      value: formatDisplayedServiceDurationLabel(displayedServiceMinutes),
    };
  }

  return {
    lineLabel: "Service exclu",
    noteLabel: "Temps à l'arrêt",
    subjectLabel: "service à l'arrêt",
    stopLabel: getRouteSegmentStopLabel(targetStop),
    value: formatDisplayedServiceDurationLabel(displayedServiceMinutes),
  };
}

function buildRouteSegmentDurationTooltip(segment, serviceMinutesOverride = null) {
  if (segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload) {
    return "Chargé la veille en PM : aucun temps ce jour-là.";
  }

  if (segment?.isInitialPreload) {
    return "Préchargement veille hors plage chauffeur: temps de conduite estimé à 0 min. Cette étape représente un stock initial déjà chargé avant le départ journalier.";
  }

  const displayedDurationMinutes = resolveRouteSegmentDisplayDurationMinutes(segment);

  if (!segment || displayedDurationMinutes == null) {
    return "Temps de conduite estimé entre deux arrêts.";
  }

  const fromStop = segment.stops?.[0] || null;
  const toStop = segment.stops?.[1] || null;
  const distanceLabel = segment.distanceLabel || formatDistanceKm(segment.distanceKm) || "distance indisponible";
  const distanceSourceLabel = getRouteHoverDistanceSourceLabel(segment.distanceSource, segment.distanceSourceLabel);
  const durationLabel = formatDurationShort(displayedDurationMinutes) || `${Math.round(displayedDurationMinutes)} min`;
  const distanceSourceSuffix = distanceSourceLabel ? ` (${distanceSourceLabel})` : "";
  const scheduledDurationMinutes = normalizeTimelineDurationMinutes(segment?.scheduledDurationMinutes);
  const scheduledDurationLabel = scheduledDurationMinutes != null
    ? (formatDurationShort(scheduledDurationMinutes) || "0 min")
    : "";
  const serviceContext = getRouteSegmentDurationServiceContext(segment, serviceMinutesOverride);
  const serviceSentence = serviceContext
    ? segment?.isInitialRouteSegment === true
      ? `${serviceContext.noteLabel} à ${serviceContext.stopLabel}: ${serviceContext.value}. Cette inspection est incluse dans l'étape Inspection, mais la durée de conduite affichée reste calculée hors inspection.`
      : `${serviceContext.noteLabel} à ${serviceContext.stopLabel}: ${serviceContext.value}. Ce ${serviceContext.subjectLabel} est exclu de cette durée.`
    : "Aucun temps de chargement ni de déchargement n'est inclus dans cette durée.";
  const sourceSentence = segment.distanceSource === "roadapi"
    ? "HERE est interrogé en mode camion pour fournir le trajet routier et peut aussi fournir un temps réseau/planning distinct."
    : "La distance affichée provient de la meilleure source disponible pour ce segment."
  const comparisonSentence = scheduledDurationMinutes != null
    ? `Le temps ${segment.distanceSource === "roadapi" ? "HERE/planning" : "planning"} disponible pour le même trajet est ${scheduledDurationLabel}. Il n'y a pas deux diminutions successives: la durée affichée et le temps ${segment.distanceSource === "roadapi" ? "HERE/planning" : "planning"} sont deux valeurs parallèles, pas un recalcul en chaîne.`
    : "Il n'y a pas deux diminutions successives: quand un temps réseau/planning existe, il reste distinct de cette estimation camion au lieu d'être recalculé une seconde fois.";

  if (segment.speedKmh && segment.speedKmh > 0 && segment.distanceKm != null) {
    return `Temps de conduite estimé entre ${getRouteSegmentStopLabel(fromStop)} et ${getRouteSegmentStopLabel(toStop)}: ${distanceLabel}${distanceSourceSuffix} / ${Math.round(segment.speedKmh)} km/h${segment.tripFixedMinutes > 0 ? ` + ${segment.tripFixedMinutes} min fixes` : ""} = ≈ ${durationLabel}. Cette durée reste une estimation camion calculée à partir de la source de distance affichée. ${sourceSentence} ${comparisonSentence} ${serviceSentence}`;
  }

  return `Temps de conduite estimé entre ${getRouteSegmentStopLabel(fromStop)} et ${getRouteSegmentStopLabel(toStop)}: ≈ ${durationLabel}. Base distance: ${distanceLabel}${distanceSourceSuffix}. ${sourceSentence} ${comparisonSentence} ${serviceSentence}`;
}

function buildRouteSegmentCompartmentHoverCard(compartmentLabels = []) {
  if (!Array.isArray(compartmentLabels) || compartmentLabels.length === 0) {
    return null;
  }

  return {
    type: "note",
    note: `Compartiment(s) : ${compartmentLabels.join(", ")}`,
  };
}

function buildRouteSegmentProductHoverCard(label) {
  if (typeof label !== "string" || !label.trim()) {
    return null;
  }

  return {
    type: "note",
    note: label.trim(),
  };
}

function findTripOrderForStop(trip, stop) {
  const orders = Array.isArray(trip?.orders) ? trip.orders.filter(Boolean) : [];

  return orders.find((order) => (
    (order?.order_id && order.order_id === stop?.order_id)
    || (Array.isArray(order?.deliveries) && order.deliveries.some((delivery) => delivery?.delivery_id === stop?.delivery_id))
  )) || null;
}

function resolveRouteSegmentProductData(trip, stop) {
  if (!stop || typeof stop !== "object") {
    return {
      productCode: "",
      productLabel: "",
    };
  }

  const order = findTripOrderForStop(trip, stop);

  return {
    productCode: resolveOrderProductCodeDisplay(order)
      || normalizeProductCodeDisplay(stop?.grain_code),
    productLabel: typeof order?.grain_type === "string"
      ? order.grain_type.trim()
      : (typeof stop?.grain_type === "string" ? stop.grain_type.trim() : ""),
  };
}

// Repère « produit en expansion » : règles actives des résultats, grain_code de la commande
// (trajet, sinon commandes de la journée), sinon celui de l'étape — jamais le repli raw_data.
function buildCompartmentFillContext(state = null) {
  const rules = resolveCompartmentFillRulesFromResults(state?.resultsData);
  if (!rules) {
    return null;
  }

  let ordersById = null;
  return {
    rules,
    findOrder(orderId) {
      if (!ordersById) {
        ordersById = new Map();
        [state?.resultsData?.assignments, state?.assignmentList].forEach((assignments) => {
          (Array.isArray(assignments) ? assignments : []).forEach((assignment) => {
            (Array.isArray(assignment?.trips) ? assignment.trips : []).forEach((trip) => {
              (Array.isArray(trip?.orders) ? trip.orders : []).forEach((order) => {
                const candidateOrderId = typeof order?.order_id === "string" ? order.order_id.trim() : "";
                if (candidateOrderId && !ordersById.has(candidateOrderId)) {
                  ordersById.set(candidateOrderId, order);
                }
              });
            });
          });
        });
      }

      return ordersById.get(orderId) || null;
    },
  };
}

// Chargements seulement (préchargements et Chargé PM compris) : aucun repère sur les livraisons.
function resolveRouteSegmentCompartmentFill(trip, stop, compartmentFillContext = null) {
  const stopType = typeof stop?.stop_type === "string" ? stop.stop_type.trim() : String(stop?.step_type || "").trim();
  if (!compartmentFillContext || !stop || typeof stop !== "object" || stopType === "delivery") {
    return { compartmentFillPercent: null, compartmentFillProductCode: "" };
  }

  const stopOrderId = typeof stop.order_id === "string" ? stop.order_id.trim() : "";
  const order = findTripOrderForStop(trip, stop)
    || (stopOrderId ? compartmentFillContext.findOrder(stopOrderId) : null);
  const { percent, code } = resolveStopCompartmentFill(order, stop, compartmentFillContext.rules);

  return { compartmentFillPercent: percent, compartmentFillProductCode: code };
}

function resolveRouteSegmentSiloLabel(trip, stop) {
  if (!stop || typeof stop !== "object") {
    return "";
  }

  const directCandidates = [stop.loading_silo, stop.silo];
  for (const candidate of directCandidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  const order = findTripOrderForStop(trip, stop);
  if (!order) {
    return "";
  }

  const deliveries = Array.isArray(order.deliveries) ? order.deliveries.filter(Boolean) : [];
  const primaryDelivery = deliveries.find((delivery) => delivery?.delivery_id === stop?.delivery_id) || deliveries[0] || null;
  const deliveryCandidates = primaryDelivery ? [primaryDelivery.loading_silo, primaryDelivery.silo] : [];
  for (const candidate of deliveryCandidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  const orderCandidates = [order.loading_silo, order.silo];
  for (const candidate of orderCandidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return "";
}

function resolveRouteSegmentProductChip(trip, stop) {
  if (!stop || typeof stop !== "object") {
    return null;
  }

  const { productCode, productLabel } = resolveRouteSegmentProductData(trip, stop);
  if (productCode) {
    return {
      hoverCardDetails: buildRouteSegmentProductHoverCard(productCode),
      hoverCardTitle: "Code produit",
      label: productCode,
      title: `Code produit : ${productCode}`,
    };
  }

  if (!productLabel) {
    return null;
  }

  return {
    hoverCardDetails: buildRouteSegmentProductHoverCard(productLabel),
    hoverCardTitle: "Produit",
    label: productLabel,
    title: `Produit : ${productLabel}`,
  };
}

function buildRouteSegmentQuantityHoverCard(stop) {
  if (typeof stop?.quantity_tons !== "number") {
    return null;
  }

  const quantityLabel = `${formatTons(stop.quantity_tons)} t`;

  return {
    type: "formula",
    lines: [
      {
        label: "Quantité",
        value: quantityLabel,
      },
    ],
    total: quantityLabel,
    totalLabel: "Quantité",
  };
}

function buildRouteSegmentDistanceHoverCard(segment) {
  const distanceLabel = segment?.distanceLabel || null;
  const distanceSourceLabel = getRouteHoverDistanceSourceLabel(segment?.distanceSource, segment?.distanceSourceLabel);

  return {
    type: distanceLabel ? "formula" : "note",
    lines: distanceLabel
      ? [
          {
            label: "Trajet routier",
            value: distanceLabel,
          },
          distanceSourceLabel
            ? {
                label: "Source",
                value: distanceSourceLabel,
              }
            : null,
        ].filter(Boolean)
      : [],
    note: buildRouteSegmentDistanceTooltip(segment),
    total: distanceLabel,
    totalLabel: "Distance",
  };
}

function buildRouteSegmentServiceHoverCard(stop, serviceMinutesOverride = null) {
  const note = buildRouteSegmentServiceTooltip(stop, serviceMinutesOverride);

  if (!hasExplicitStopServiceDuration(stop)) {
    return {
      type: "note",
      note,
    };
  }

  const displayedServiceMinutes = resolveDisplayedStopServiceMinutes(stop, serviceMinutesOverride);
  const isLoadingStop = stop.stop_type === "loading";
  const isDeliveryStop = stop.stop_type === "delivery";
  const isDepotStartStop = stop.stop_type === "depot_start";
  const totalLabel = isLoadingStop
    ? "Temps de chargement"
    : isDeliveryStop
      ? "Temps de déchargement"
      : isDepotStartStop
        ? "Temps d'inspection"
        : "Temps à l'arrêt";

  const meunerieExtraMinutes = isLoadingStop
    ? Math.max(0, Number(stop?.meunerie_loading_extra_minutes) || 0)
    : 0;
  const aspiratorServiceMinutes = isDeliveryStop
    ? Math.max(0, Math.round(Number(stop?.aspirator_service_minutes) || 0))
    : 0;
  const fixedMinutes = Math.max(0, Math.round(Number(
    isDeliveryStop ? stop?.delivery_fixed_minutes : isLoadingStop && !stop?.is_preloaded_previous_day ? stop?.loading_fixed_minutes : 0,
  ) || 0));
  const extraServiceMinutes = meunerieExtraMinutes + aspiratorServiceMinutes + fixedMinutes;
  const baseServiceMinutes = extraServiceMinutes > 0 && displayedServiceMinutes >= extraServiceMinutes
    ? displayedServiceMinutes - extraServiceMinutes
    : displayedServiceMinutes;
  const serviceLabel = formatDisplayedServiceDurationLabel(baseServiceMinutes);
  const totalServiceLabel = formatDisplayedServiceDurationLabel(displayedServiceMinutes);

  return {
    type: "formula",
    lines: [
      {
        label: isLoadingStop ? "Chargement" : isDeliveryStop ? "Déchargement" : "Service",
        value: serviceLabel,
      },
      meunerieExtraMinutes > 0
        ? {
            label: "Surtemps meunerie",
            value: `+${meunerieExtraMinutes} min`,
          }
        : null,
      fixedMinutes > 0
        ? {
            label: "Temps fixe",
            value: `+${fixedMinutes} min`,
          }
        : null,
      aspiratorServiceMinutes > 0
        ? {
            label: "Temps aspirateur",
            value: `+${aspiratorServiceMinutes} min`,
          }
        : null,
    ].filter(Boolean),
    note,
    total: totalServiceLabel,
    totalLabel,
  };
}

function buildRouteSegmentDurationHoverCard(segment, serviceMinutesOverride = null) {
  const note = buildRouteSegmentDurationTooltip(segment, serviceMinutesOverride);
  const distanceSourceLabel = getRouteHoverDistanceSourceLabel(segment?.distanceSource, segment?.distanceSourceLabel);
  const serviceContext = getRouteSegmentDurationServiceContext(segment, serviceMinutesOverride);
  const displayedDurationMinutes = resolveRouteSegmentDisplayDurationMinutes(segment);
  const scheduledDurationMinutes = normalizeTimelineDurationMinutes(segment?.scheduledDurationMinutes);

  if (displayedDurationMinutes == null) {
    return {
      type: "note",
      note,
    };
  }

  const durationLabel = displayedDurationMinutes === 0
    ? "0 min"
    : `≈ ${formatDurationShort(displayedDurationMinutes)}`;

  return {
    type: "formula",
    lines: segment?.speedKmh && segment.speedKmh > 0
      ? [
          {
            label: "Distance",
            value: segment.distanceLabel || "-",
          },
          distanceSourceLabel
            ? {
                label: "Source dist.",
                value: distanceSourceLabel,
              }
            : null,
          {
            label: "Vitesse moy.",
            operator: "÷",
            value: `${Math.round(segment.speedKmh)} km/h`,
          },
          segment.tripFixedMinutes > 0
            ? {
                label: "Temps fixe",
                operator: "+",
                value: `${segment.tripFixedMinutes} min`,
              }
            : null,
          scheduledDurationMinutes != null
            ? {
                label: segment?.distanceSource === "roadapi" ? "Temps HERE/planning" : "Temps planning",
                value: formatDurationShort(scheduledDurationMinutes) || "0 min",
              }
            : null,
          serviceContext
            ? {
                label: serviceContext.lineLabel,
                value: `Exclu (${serviceContext.value})`,
              }
            : null,
        ].filter(Boolean)
      : [
          {
            label: "Conduite",
            value: durationLabel,
          },
          serviceContext
            ? {
                label: serviceContext.lineLabel,
                value: `Exclu (${serviceContext.value})`,
              }
            : null,
        ].filter(Boolean),
    note,
    total: durationLabel,
    totalLabel: "Durée estimée",
  };
}

function buildRouteSegmentDistanceChipLabel(segment) {
  if (!segment?.distanceLabel) {
    return "Distance indisponible";
  }

  const distanceSourceLabel = segment.distanceSourceLabel || formatRouteDistanceSourceLabel(segment.distanceSource);

  return distanceSourceLabel ? `${segment.distanceLabel} · ${distanceSourceLabel}` : segment.distanceLabel;
}

function getRouteSegmentDistanceChipVariant(distanceSource) {
  const source = normalizeRouteDistanceSource(distanceSource);

  if (source === "geometrymeasured") {
    return "distance-measured";
  }

  if (source === "haversineestimate") {
    return "distance-estimated";
  }

  if (source === "unavailable") {
    return "distance-unavailable";
  }

  return "";
}

function getStopCompartmentAllocations(stop) {
  return (Array.isArray(stop?.compartment_allocations) ? stop.compartment_allocations : [])
    .filter(Boolean)
    .map((allocation) => ({ ...allocation }));
}

function getStopCompartmentLabels(stop) {
  const allocations = getStopCompartmentAllocations(stop);
  return allocations
    .map((allocation) => (allocation && typeof allocation.position !== "undefined" ? `C${allocation.position}` : ""))
    .filter(Boolean);
}

// Bulle du Chargé PM : une ligne par commande du passage (code, client du lendemain, compartiments, tonnage).
function buildPmOrderLines(stop, assignment) {
  const markerKeys = new Set([stop?.pm?.marker_key, stop?.end_of_day_load_marker_key, ...(stop?.end_of_day_load_marker_keys || [])].filter(Boolean));
  const items = (assignment?.end_of_day_loads || [])
    .filter((marker) => markerKeys.has(marker?.marker_key))
    .flatMap((marker) => marker?.items || []);
  return [...new Set(stop?.order_ids || [])].filter(Boolean).map((orderId) => {
    const allocations = getStopCompartmentAllocations(stop).filter((allocation) => allocation.order_id === orderId);
    const positions = allocations.map((allocation) => Number(allocation.position)).filter(Number.isInteger).sort((a, b) => a - b);
    const contiguous = positions.length > 1 && positions[positions.length - 1] - positions[0] === positions.length - 1;
    const item = items.find((candidate) => candidate?.order_id === orderId) || {};
    const quantityTons = allocations.reduce((total, allocation) => total + (Number(allocation.quantity_tons) || 0), 0)
      || Number(item.quantity_tons) || 0;
    return {
      orderId,
      clientName: typeof item.client_name === "string" ? item.client_name.trim() : "",
      compartmentLabel: contiguous ? `C${positions[0]}-C${positions[positions.length - 1]}` : positions.map((position) => `C${position}`).join(", "),
      quantityLabel: quantityTons > 0 ? `${formatTons(quantityTons)} t` : "",
    };
  });
}

function buildRouteSegmentChips(segment, chipStop, trip, { serviceMinutesOverride = null } = {}) {
  const compartmentLabels = getStopCompartmentLabels(chipStop);
  const distanceChipVariant = getRouteSegmentDistanceChipVariant(segment?.distanceSource);
  const productChip = resolveRouteSegmentProductChip(trip, chipStop);
  const displayedServiceMinutes = resolveDisplayedStopServiceMinutes(chipStop, serviceMinutesOverride);
  const displayedDurationMinutes = resolveRouteSegmentDisplayDurationMinutes(segment);

  return [
    compartmentLabels.length
      ? {
          key: "compartment",
          label: compartmentLabels.join(", "),
          kind: "compartment",
          hoverCardDetails: buildRouteSegmentCompartmentHoverCard(compartmentLabels),
          hoverCardTitle: "Compartiment(s)",
          // Occupation X/N : un compartiment partagé reste occupé tant qu'une commande y a du stock.
          occupantOrderIdsByToken: getStopCompartmentAllocations(chipStop).reduce((byToken, allocation) => {
            const token = typeof allocation.position !== "undefined" ? `C${allocation.position}` : "";
            if (token && allocation.order_id) byToken[token] = [...new Set([...(byToken[token] || []), allocation.order_id])];
            return byToken;
          }, {}),
          stopType: chipStop?.stop_type || "delivery",
          title: `Compartiment(s) : ${compartmentLabels.join(", ")}`,
        }
      : null,
    productChip
      ? {
          key: "grain",
          label: productChip.label,
          kind: "grain",
          hoverCardDetails: productChip.hoverCardDetails,
          hoverCardTitle: productChip.hoverCardTitle,
          title: productChip.title,
        }
      : null,
    typeof chipStop?.quantity_tons === "number"
      ? {
          key: "qty",
          label: `${formatTons(chipStop.quantity_tons)} t`,
          kind: "qty",
          hoverCardDetails: buildRouteSegmentQuantityHoverCard(chipStop),
          hoverCardTitle: "Quantité",
        }
      : null,
    {
      key: "dist",
      label: buildRouteSegmentDistanceChipLabel(segment),
      kind: "dist",
      variant: distanceChipVariant,
      hoverCardDetails: buildRouteSegmentDistanceHoverCard(segment),
      hoverCardTitle: "Distance",
      title: buildRouteSegmentDistanceTooltip(segment),
    },
    hasExplicitStopServiceDuration(chipStop)
      ? {
          key: "svc",
          label: formatDisplayedServiceDurationLabel(displayedServiceMinutes),
          kind: "svc",
          hoverCardDetails: buildRouteSegmentServiceHoverCard(chipStop, serviceMinutesOverride),
          hoverCardTitle: chipStop.stop_type === "loading"
            ? "Temps de chargement"
            : chipStop.stop_type === "delivery"
              ? "Temps de déchargement"
              : chipStop.stop_type === "depot_start"
                ? "Temps d'inspection"
                : "Temps à l'arrêt",
          title: buildRouteSegmentServiceTooltip(chipStop, serviceMinutesOverride),
        }
      : null,
    chipStop?.stop_type === "loading" && Number(chipStop?.meunerie_loading_extra_minutes) > 0 && !chipStop?.is_preloaded_previous_day
      ? {
          key: "meunerie",
          label: `+${Math.round(Number(chipStop.meunerie_loading_extra_minutes))} min`,
          kind: "meunerie",
          tone: "amber",
          hoverCardTitle: "Surtemps meunerie",
          title: `Surtemps meunerie : +${Math.round(Number(chipStop.meunerie_loading_extra_minutes))} min. Il se règle dans Data Manager > Clients. Il s'ajoute au temps de chargement calculé à partir du tonnage et du débit camion.`,
        }
      : null,
    chipStop?.stop_type === "delivery" && Number(chipStop?.aspirator_service_minutes) > 0
      ? {
          key: "aspirator_time",
          label: `+${Math.round(Number(chipStop.aspirator_service_minutes))} min`,
          kind: "aspirator_time",
          tone: "aspiration",
          hoverCardTitle: "Temps aspirateur",
          title: `Temps aspirateur : +${Math.round(Number(chipStop.aspirator_service_minutes))} min. Temps du camion (la base) plus les tonnes au débit d'aspiration (Paramètres système), quand la commande exige l'aspirateur.`,
        }
      : null,
    displayedDurationMinutes != null
      ? {
          key: "dur",
          label: displayedDurationMinutes === 0 ? "0 min" : `≈ ${formatDurationShort(displayedDurationMinutes)}`,
          kind: "dur",
          variant: distanceChipVariant,
          hoverCardDetails: buildRouteSegmentDurationHoverCard(segment, serviceMinutesOverride),
          hoverCardTitle: "Durée estimée",
          title: buildRouteSegmentDurationTooltip(segment, serviceMinutesOverride),
        }
      : null,
  ].filter(Boolean);
}

function resolveTimelineStopGroupKey(stop) {
  if (!stop || typeof stop !== "object") {
    return "";
  }

  const backendSameSiteGroupKey = typeof stop.same_site_group_key === "string"
    ? stop.same_site_group_key.trim()
    : "";

  if (backendSameSiteGroupKey) {
    return `backend:${backendSameSiteGroupKey}`;
  }

  const latitude = typeof stop.latitude === "number" ? stop.latitude.toFixed(6) : "";
  const longitude = typeof stop.longitude === "number" ? stop.longitude.toFixed(6) : "";
  if (latitude && longitude) {
    return `coords:${latitude}|${longitude}`;
  }

  const address = normalizeTimelineTextIdentity(stop.address);

  return address ? `address:${address}` : "";
}

function normalizeTimelineTextIdentity(value = "") {
  if (typeof value !== "string") {
    return "";
  }

  return value
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function isTimelineAspiratorRequirement(value) {
  const normalizedValue = normalizeTimelineTextIdentity(value);
  return normalizedValue === "oui" || normalizedValue === "long";
}

function resolveTimelineOrderRouteSignature(order) {
  const loadingAddress = normalizeTimelineTextIdentity(order?.loading_address);
  const deliveries = Array.isArray(order?.deliveries) ? order.deliveries : [];

  if (!loadingAddress || deliveries.length === 0) {
    return "";
  }

  const deliveryAddresses = [];
  for (const delivery of deliveries) {
    const deliveryAddress = normalizeTimelineTextIdentity(delivery?.address);
    if (!deliveryAddress) {
      return "";
    }
    deliveryAddresses.push(deliveryAddress);
  }

  const uniqueDeliveryAddresses = Array.from(new Set(deliveryAddresses)).sort();
  return uniqueDeliveryAddresses.length
    ? `loading:${loadingAddress}|deliveries:${uniqueDeliveryAddresses.join("||")}`
    : "";
}

function buildTimelineAspirationRouteContextIndex(trip = null) {
  const byDeliveryId = new Map();
  const byOrderId = new Map();
  const orders = Array.isArray(trip?.orders) ? trip.orders.filter(Boolean) : [];

  orders.forEach((order) => {
    const orderId = normalizeTimelineStopMatchValue(order?.order_id);
    const deliveries = Array.isArray(order?.deliveries) ? order.deliveries : [];
    const requiresAspirator = isTimelineAspiratorRequirement(order?.aspirator)
      || deliveries.some((delivery) => isTimelineAspiratorRequirement(delivery?.aspirator));
    const context = {
      orderId,
      requiresAspirator,
      routeSignature: resolveTimelineOrderRouteSignature(order),
    };

    if (orderId) {
      byOrderId.set(orderId, context);
    }

    deliveries.forEach((delivery) => {
      const deliveryId = normalizeTimelineStopMatchValue(delivery?.delivery_id);
      if (deliveryId) {
        byDeliveryId.set(deliveryId, context);
      }
    });
  });

  return { byDeliveryId, byOrderId };
}

function resolveTimelineAspirationRoutePartition(stop, segmentIndex, contextIndex) {
  const orderId = normalizeTimelineStopMatchValue(stop?.order_id);
  const deliveryId = normalizeTimelineStopMatchValue(stop?.delivery_id);
  const orderContext = (orderId && contextIndex?.byOrderId?.get(orderId))
    || (deliveryId && contextIndex?.byDeliveryId?.get(deliveryId))
    || null;
  const resolvedOrderId = orderContext?.orderId || orderId;
  const requiresAspirator = Boolean(
    orderContext?.requiresAspirator
    || isTimelineAspiratorRequirement(stop?.aspirator)
    || Number(stop?.aspirator_service_minutes) > 0,
  );
  const partitionKey = requiresAspirator
    ? (
        orderContext?.routeSignature && resolvedOrderId
          ? `signature:${orderContext.routeSignature}`
          : resolvedOrderId
            ? `order:${resolvedOrderId}`
            : `segment:${segmentIndex}`
      )
    : "ordinary";

  return { partitionKey, requiresAspirator };
}

const SAME_SITE_SEQUENCE_VISUAL_DURATION_MINUTES = 1;

function isSameSiteTimelineGroupStopType(stopType) {
  return stopType === "loading" || stopType === "delivery";
}

function buildTimelineVisualLayoutBySegment(routeSegments = [], trip = null) {
  const visualLayoutBySegment = Array.from({ length: routeSegments.length }, () => ({
    isSameSiteSequenceGroup: false,
    sameSiteGroupContainsMixedStopTypes: false,
    sameSiteGroupIndex: 0,
    sameSiteGroupRunStartIndex: 0,
    sameSiteGroupSize: 1,
    sameSiteGroupStopKey: "",
    visualServiceDurationMinutes: null,
    visualTimelineDurationMinutes: null,
  }));
  const aspirationRouteContextIndex = buildTimelineAspirationRouteContextIndex(trip);
  const assignVisualGroup = (groupStart, groupEnd, groupStopKey) => {
    const groupSize = groupEnd - groupStart;
    const firstSegment = routeSegments[groupStart] || null;
    const firstTargetStop = firstSegment?.stops?.[1] || firstSegment?.stops?.[0] || null;
    const firstStopType = firstTargetStop?.stop_type || "";
    const rawTravelDurationMinutes = normalizeTimelineDurationMinutes(firstSegment?.durationMinutes) || 0;
    const rawServiceDurationMinutes = normalizeTimelineDurationMinutes(firstTargetStop?.service_time_minutes) || 0;
    let sameSiteGroupContainsMixedStopTypes = false;

    for (let groupIndex = groupStart; groupIndex < groupEnd; groupIndex += 1) {
      const groupSegment = routeSegments[groupIndex] || null;
      const groupTargetStop = groupSegment?.stops?.[1] || groupSegment?.stops?.[0] || null;
      if ((groupTargetStop?.stop_type || "") !== firstStopType) {
        sameSiteGroupContainsMixedStopTypes = true;
        break;
      }
    }

    if (groupSize <= 1) {
      visualLayoutBySegment[groupStart] = {
        isSameSiteSequenceGroup: false,
        sameSiteGroupContainsMixedStopTypes: false,
        sameSiteGroupIndex: 0,
        sameSiteGroupRunStartIndex: groupStart,
        sameSiteGroupSize: 1,
        sameSiteGroupStopKey: groupStopKey,
        visualServiceDurationMinutes: rawServiceDurationMinutes,
        visualTimelineDurationMinutes: rawTravelDurationMinutes + rawServiceDurationMinutes,
      };
      return;
    }

    for (let groupIndex = 0; groupIndex < groupSize; groupIndex += 1) {
      visualLayoutBySegment[groupStart + groupIndex] = {
        isSameSiteSequenceGroup: true,
        sameSiteGroupContainsMixedStopTypes,
        sameSiteGroupIndex: groupIndex,
        sameSiteGroupRunStartIndex: groupStart,
        sameSiteGroupSize: groupSize,
        sameSiteGroupStopKey: groupStopKey,
        visualServiceDurationMinutes: 0,
        visualTimelineDurationMinutes: SAME_SITE_SEQUENCE_VISUAL_DURATION_MINUTES,
      };
    }
  };
  let cursor = 0;

  while (cursor < routeSegments.length) {
    const currentSegment = routeSegments[cursor] || null;
    const currentTargetStop = currentSegment?.stops?.[1] || currentSegment?.stops?.[0] || null;
    const currentStopType = currentTargetStop?.stop_type || "";
    const currentGroupKey = resolveTimelineStopGroupKey(currentTargetStop);

    if (!currentGroupKey || !isSameSiteTimelineGroupStopType(currentStopType)) {
      assignVisualGroup(cursor, cursor + 1, currentGroupKey);
      cursor += 1;
      continue;
    }

    let rawGroupEnd = cursor;

    while (rawGroupEnd < routeSegments.length) {
      const candidateSegment = routeSegments[rawGroupEnd] || null;
      const candidateTargetStop = candidateSegment?.stops?.[1] || candidateSegment?.stops?.[0] || null;
      const candidateStopType = candidateTargetStop?.stop_type || "";

      if (!isSameSiteTimelineGroupStopType(candidateStopType) || resolveTimelineStopGroupKey(candidateTargetStop) !== currentGroupKey) {
        break;
      }

      rawGroupEnd += 1;
    }

    const rawGroupPartitions = [];
    for (let groupIndex = cursor; groupIndex < rawGroupEnd; groupIndex += 1) {
      const groupSegment = routeSegments[groupIndex] || null;
      const groupTargetStop = groupSegment?.stops?.[1] || groupSegment?.stops?.[0] || null;
      rawGroupPartitions.push(resolveTimelineAspirationRoutePartition(
        groupTargetStop,
        groupIndex,
        aspirationRouteContextIndex,
      ));
    }
    const rawGroupContainsAspirator = rawGroupPartitions.some((partition) => partition.requiresAspirator);

    if (!rawGroupContainsAspirator) {
      assignVisualGroup(cursor, rawGroupEnd, currentGroupKey);
      cursor = rawGroupEnd;
      continue;
    }

    let partitionStart = cursor;
    while (partitionStart < rawGroupEnd) {
      const partitionKey = rawGroupPartitions[partitionStart - cursor]?.partitionKey || `segment:${partitionStart}`;
      let partitionEnd = partitionStart + 1;
      while (
        partitionEnd < rawGroupEnd
        && rawGroupPartitions[partitionEnd - cursor]?.partitionKey === partitionKey
      ) {
        partitionEnd += 1;
      }

      assignVisualGroup(
        partitionStart,
        partitionEnd,
        `${currentGroupKey}::aspiration-route:${partitionKey}`,
      );
      partitionStart = partitionEnd;
    }

    cursor = rawGroupEnd;
  }

  return visualLayoutBySegment;
}

function getRouteStopIconTooltip(stopType) {
  if (stopType === "loading") {
    return "Chargement";
  }
  if (stopType === "delivery") {
    return "Livraison";
  }
  if (stopType === "depot_return") {
    return "Retour entrepôt";
  }

  return "Départ entrepôt";
}

function getRouteStopBadgeTooltip(stopType) {
  if (stopType === "loading") {
    return "Étape de chargement";
  }
  if (stopType === "delivery") {
    return "Étape de livraison";
  }
  if (stopType === "depot_return") {
    return "Retour à l'entrepôt";
  }

  return "Départ de l'entrepôt";
}

function getAddressCityLabel(address) {
  if (!address || typeof address !== "string") {
    return "Ville non disponible";
  }

  if (address.includes(";")) {
    const firstSegment = address.split(";")[0] || "";
    const firstPart = firstSegment.split(",")[0] || "";
    return firstPart.trim() || "Ville non disponible";
  }

  const parts = address
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) {
    return "Ville non disponible";
  }

  if (parts.length >= 2) {
    return `${parts[0]}, ${parts[1]}`;
  }

  return parts[0];
}

function getRouteStopAddressLabel(address) {
  if (typeof address !== "string") {
    return "Adresse non disponible";
  }

  const trimmedAddress = address.trim();
  return trimmedAddress || "Adresse non disponible";
}

function getStopTypeLabel(stop) {
  if (
    stop?.is_end_of_day_load
    || stop?.isEndOfDayLoad
    || stop?.is_end_of_day_load_source_preload
    || stop?.isEndOfDayLoadSourcePreload
  ) {
    return END_OF_DAY_LOAD_LABEL;
  }
  if (stop?.is_initial_preload || stop?.isInitialPreload) {
    return "Chargé la veille";
  }
  if (stop?.stop_type === "depot_start") {
    return "Départ entrepôt";
  }
  if (stop?.stop_type === "loading") {
    return "Chargement";
  }
  if (stop?.stop_type === "depot_return") {
    return "Retour entrepôt";
  }

  return stop?.order_id ? "Déchargement" : "Livraison";
}

function isStopWarning(stop) {
  if (!stop || typeof stop !== "object") {
    return false;
  }

  return Boolean(
    stop.warning ||
    stop.has_warning ||
    stop.warning_message ||
    stop.address_action_required ||
    stop.severity === "warning"
  );
}

export function getStopDisplayColor(stop) {
  if (isStopWarning(stop)) {
    return "#d97706";
  }
  if (
    stop?.is_end_of_day_load
    || stop?.isEndOfDayLoad
    || stop?.is_end_of_day_load_source_preload
    || stop?.isEndOfDayLoadSourcePreload
  ) {
    return resolveEndOfDayLoadRouteColor(stop);
  }
  if (stop?.is_initial_preload || stop?.isInitialPreload) {
    return INITIAL_PRELOAD_ROUTE_COLOR;
  }
  if (stop && stop.stop_type === "loading") {
    return LOADING_ROUTE_COLOR;
  }
  if (stop && stop.stop_type === "delivery") {
    return DELIVERY_ROUTE_COLOR;
  }
  if (stop && stop.stop_type === "depot_return") {
    return DEPOT_ROUTE_COLOR;
  }

  return DEPOT_ROUTE_COLOR;
}

export function getSelectedClientStopType(trip, clientId) {
  if (!clientId) {
    return "";
  }

  const selectedStop = getTripRoutableStops(trip).find((stop) => getClientSelectionId(stop) === clientId);
  if (!selectedStop) {
    return "";
  }

  if (selectedStop.stop_type === "loading") {
    return "loading";
  }

  if (selectedStop.stop_type === "delivery") {
    return "delivery";
  }

  return "";
}

export function buildExactRoutePolylineSegments(segmentLatLngGroups, routeSegments) {
  if (!Array.isArray(segmentLatLngGroups) || !Array.isArray(routeSegments) || !segmentLatLngGroups.length) {
    return [];
  }

  if (routeSegments.length && segmentLatLngGroups.length !== routeSegments.length) {
    return [];
  }

  return segmentLatLngGroups
    .map((latlngs, segmentIndex) => {
      if (!Array.isArray(latlngs) || latlngs.length < 2) {
        return null;
      }

      const segment = routeSegments[segmentIndex] || null;
      const stops = Array.isArray(segment?.stops) ? segment.stops.filter(Boolean) : [];
      const originStop = stops[0] || null;
      return {
        index: segment?.index ?? segmentIndex + 1,
        routeSegmentIndex: segment?.index ?? segmentIndex + 1,
        color: getStopDisplayColor(originStop),
        latlngs,
      };
    })
    .filter(Boolean);
}

function findNearestRoutePointIndex(routeLatLngs, targetLatLng, startIndex = 0) {
  if (!routeLatLngs.length) {
    return 0;
  }

  let nearestIndex = Math.max(0, Math.min(startIndex, routeLatLngs.length - 1));
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (let index = nearestIndex; index < routeLatLngs.length; index += 1) {
    const candidate = routeLatLngs[index];
    const latitudeDelta = candidate[0] - targetLatLng[0];
    const longitudeDelta = candidate[1] - targetLatLng[1];
    const distance = (latitudeDelta * latitudeDelta) + (longitudeDelta * longitudeDelta);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = index;
      if (distance < 1e-12) {
        break;
      }
    }
  }

  return nearestIndex;
}

export function buildRoutePolylineSegments(routeLatLngs, routeSegments) {
  if (routeLatLngs.length < 2 || !Array.isArray(routeSegments) || routeSegments.length <= 1) {
    return [];
  }

  const coloredSegments = [];
  let cursorIndex = 0;

  routeSegments.forEach((segment, segmentIndex) => {
    const stopsWithCoordinates = Array.isArray(segment?.stops)
      ? segment.stops.filter(hasStopCoordinates)
      : [];
    if (!stopsWithCoordinates.length) {
      return;
    }

    const firstStop = stopsWithCoordinates[0];
    const lastStop = stopsWithCoordinates[stopsWithCoordinates.length - 1];
    const startIndex = segmentIndex === 0
      ? 0
      : findNearestRoutePointIndex(routeLatLngs, [firstStop.latitude, firstStop.longitude], cursorIndex);
    const requestedEndIndex = segmentIndex === routeSegments.length - 1
      ? routeLatLngs.length - 1
      : findNearestRoutePointIndex(routeLatLngs, [lastStop.latitude, lastStop.longitude], startIndex);
    const endIndex = Math.min(routeLatLngs.length - 1, Math.max(requestedEndIndex, startIndex + 1));
    if (endIndex <= startIndex) {
      return;
    }

    const originStop = stopsWithCoordinates[0];
    coloredSegments.push({
      index: segment?.index ?? segmentIndex + 1,
      routeSegmentIndex: segment?.index ?? segmentIndex + 1,
      color: getStopDisplayColor(originStop),
      latlngs: routeLatLngs.slice(startIndex, endIndex + 1),
      startIndex,
      endIndex,
    });
    cursorIndex = endIndex;
  });

  if (!coloredSegments.length) {
    return [];
  }

  const lastSegment = coloredSegments[coloredSegments.length - 1];
  if (lastSegment.endIndex < routeLatLngs.length - 1) {
    lastSegment.latlngs = routeLatLngs.slice(lastSegment.startIndex, routeLatLngs.length);
    lastSegment.endIndex = routeLatLngs.length - 1;
  }

  return coloredSegments;
}

export function selectSupportPanelsData(state, assignment, trip) {
  return {
    fleet_segment_hovercard_context: state?.resultsData && typeof state.resultsData.fleet_segment_hovercard_context === "object"
      ? state.resultsData.fleet_segment_hovercard_context
      : null,
    routeSequence: buildRouteSequenceData(state, assignment, trip),
    invalidStops: buildInvalidStopsData(trip),
  };
}

function buildInitialDepotTravelRouteSequenceItem(segment, {
  assignmentId = "",
  globalSequence = 1,
  trip = null,
  tripId = "",
} = {}) {
  const displayedRouteSegment = buildInitialDepotTravelDisplayedRouteSegment(segment);
  const fromStop = getRouteSegmentStartStop(displayedRouteSegment);
  const toStop = getRouteSegmentTargetStop(displayedRouteSegment) || fromStop;
  const anchorStop = segment?.anchorStop || null;
  const anchorStep = segment?.anchorStep || null;
  const priorityMetadata = resolveTimelineSegmentPriorityMetadata(anchorStop, anchorStep);
  const scheduledAt = resolveTimelineSegmentScheduledAt(anchorStop, anchorStep);
  const scheduledAtMinutes = parseTimelineClockMinutes(scheduledAt);
  const travelDurationMinutes = normalizeTimelineDurationMinutes(displayedRouteSegment?.durationMinutes) || 0;
  const displayTravelDurationMinutes = resolveRouteSegmentDisplayDurationMinutes(displayedRouteSegment) ?? travelDurationMinutes;
  const serviceDurationMinutes = normalizeTimelineDurationMinutes(fromStop?.service_time_minutes) || 0;
  const serviceDurationLabel = serviceDurationMinutes > 0
    ? formatDisplayedServiceDurationLabel(serviceDurationMinutes)
    : null;

  return {
    aspiratorRequirementLabel: "",
    key: `route-segment-${globalSequence}`,
    assignmentId,
    distanceLabel: displayedRouteSegment?.distanceLabel || null,
    dominantPriority: priorityMetadata.dominantPriority || "",
    globalSequence,
    hasPriorityTimeAnchor: Boolean(priorityMetadata.dominantPriority && scheduledAtMinutes != null),
    isInitialRouteSegment: true,
    isPreloadedPreviousDay: false,
    isSameSiteSequenceGroup: false,
    clientId: "",
    isClientSelectable: false,
    isSelected: false,
    orderLabel: "",
    operationAddress: getRouteStopAddressLabel(toStop?.address),
    operationDeliveryId: "",
    operationDisambiguatorLabel: "",
    operationKey: `${assignmentId || "assignment"}::${tripId || "trip"}::${globalSequence}::initial-travel`,
    operationLabel: "",
    operationOrderId: "",
    operationTypeLabel: "Déplacement",
    productLabel: "",
    requiresAspirator: false,
    requiresOntario: false,
    sameSiteGroupContainsMixedStopTypes: false,
    sameSiteGroupIndex: 0,
    sameSiteGroupKey: "",
    sameSiteGroupSize: 1,
    sameSiteGroupStartSequence: globalSequence,
    segmentColor: getStopDisplayColor(fromStop),
    segmentStartColor: getStopDisplayColor(fromStop),
    segmentEndColor: getStopDisplayColor(toStop),
    serviceDurationLabel,
    serviceDurationMinutes,
    stopType: "depot_start",
    from: fromStop
      ? {
          address: getRouteStopAddressLabel(fromStop.address),
          label: getRouteStopLabel(fromStop),
          city: getAddressCityLabel(fromStop.address),
          showHomeMarker: true,
        }
      : null,
    to: toStop
      ? {
          address: getRouteStopAddressLabel(toStop.address),
          stopType: "depot_start",
          typeLabel: "Déplacement",
          orderLabel: "",
          badgeTitle: "Déplacement initial",
          iconTitle: "Déplacement initial",
          label: getRouteStopLabel(toStop),
          city: getAddressCityLabel(toStop.address),
        }
      : null,
    routeFromAddress: getRouteStopAddressLabel(fromStop?.address),
    routeFromLabel: getRouteStopLabel(fromStop),
    routeToAddress: getRouteStopAddressLabel(toStop?.address),
    routeToLabel: getRouteStopLabel(toStop),
    routeDistanceSource: displayedRouteSegment?.distanceSource || "unavailable",
    routeDistanceSourceLabel: displayedRouteSegment?.distanceSourceLabel || null,
    routeEstimatedTravelDurationMinutes: normalizeTimelineDurationMinutes(displayedRouteSegment?.estimatedDurationMinutes),
    routeScheduledTravelDurationMinutes: normalizeTimelineDurationMinutes(displayedRouteSegment?.scheduledDurationMinutes),
    routeSpeedKmh: typeof displayedRouteSegment?.speedKmh === "number" ? displayedRouteSegment.speedKmh : null,
    mapAnchorStopKey: buildRouteMapStopKey(fromStop, { fallbackIndex: globalSequence }),
    scheduledAt,
    scheduledAtMinutes,
    toAddress: getRouteStopAddressLabel(toStop?.address),
    toLabel: getRouteStopLabel(toStop),
    displayTravelDurationMinutes,
    travelDurationMinutes,
    waitMinutes: null,
    tripId,
    typeLabel: "Déplacement",
    quantityTons: null,
    routeSegmentIndex: String(getTechnicalRouteSegmentIndex(displayedRouteSegment) ?? globalSequence),
    chips: buildRouteSegmentChips(displayedRouteSegment, fromStop, trip, { serviceMinutesOverride: serviceDurationMinutes }),
  };
}

function buildInitialPreloadCommonModel(preloadStop, trip, {
  assignmentId = "",
  compartmentFillContext = null,
  globalSequence = 1,
  sameSiteMetadata = null,
  tripId = "",
  truckLoadBeforeTons = null,
} = {}) {
  const displayedRouteSegment = buildInitialPreloadDisplayedRouteSegment(preloadStop);
  const isEndOfDayLoadSourcePreload = preloadStop?.isEndOfDayLoadSourcePreload === true
    || preloadStop?.is_end_of_day_load_source_preload === true;
  const segmentColor = getStopDisplayColor(preloadStop);
  const operationIdentity = buildOperationIdentity(preloadStop, {
    assignmentId,
    trip,
    tripId,
    tripSequence: globalSequence,
  });
  const productChip = resolveRouteSegmentProductChip(trip, preloadStop);
  const productData = resolveRouteSegmentProductData(trip, preloadStop);
  const sameSiteGroupStartSequence = globalSequence - (Number(sameSiteMetadata?.sameSiteGroupIndex) || 0);
  const sameSiteGroupKey = sameSiteMetadata?.isSameSiteSequenceGroup
    ? `${assignmentId || "assignment"}::${tripId || "trip"}::${sameSiteGroupStartSequence}::${sameSiteMetadata.sameSiteGroupKey}`
    : "";

  return {
    assignmentId,
    aspiratorRequirementLabel: operationIdentity.aspiratorRequirementLabel,
    chips: buildRouteSegmentChips(displayedRouteSegment, preloadStop, trip, { serviceMinutesOverride: 0 }),
    color: segmentColor,
    distanceLabel: displayedRouteSegment.distanceLabel || "0 km",
    dominantPriority: "",
    endOfDayLoadDeliveryIds: Array.isArray(preloadStop?.endOfDayLoadDeliveryIds) ? [...preloadStop.endOfDayLoadDeliveryIds] : [],
    endOfDayLoadDeliveryMode: preloadStop?.endOfDayLoadDeliveryMode || "",
    endOfDayLoadDisplayMode: preloadStop?.endOfDayLoadDisplayMode || "",
    endOfDayLoadMarkerKey: preloadStop?.endOfDayLoadMarkerKey || "",
    endOfDayLoadMarkerDispatchDate: preloadStop?.endOfDayLoadMarkerDispatchDate || "",
    endOfDayLoadOrderIds: Array.isArray(preloadStop?.endOfDayLoadOrderIds) ? [...preloadStop.endOfDayLoadOrderIds] : [],
    endOfDayLoadSourceDispatchDate: preloadStop?.endOfDayLoadSourceDispatchDate || "",
    endOfDayLoadSourceTruckId: preloadStop?.endOfDayLoadSourceTruckId || "",
    endOfDayLoadTargetDispatchDate: preloadStop?.endOfDayLoadTargetDispatchDate || "",
    endOfDayLoadTargetPeriod: preloadStop?.endOfDayLoadTargetPeriod || "",
    globalSequence,
    hasPriorityTimeAnchor: false,
    isEndOfDayLoad: false,
    isEndOfDayLoadSourcePreload,
    isInitialPreload: true,
    isInitialRouteSegment: false,
    isPreloadedPreviousDay: true,
    isSameSiteSequenceGroup: Boolean(sameSiteMetadata?.isSameSiteSequenceGroup),
    isTimelineInformational: isEndOfDayLoadSourcePreload,
    operationAddress: operationIdentity.operationAddress,
    aspiratorMissionGroupKey: operationIdentity.aspiratorMissionGroupKey,
    aspiratorMissionRouteSignature: operationIdentity.aspiratorMissionRouteSignature,
    operationDeliveryId: operationIdentity.operationDeliveryId,
    operationDisambiguatorLabel: operationIdentity.operationDisambiguatorLabel,
    operationKey: operationIdentity.operationKey,
    operationLabel: operationIdentity.operationLabel,
    operationOwnerLabel: operationIdentity.operationOwnerLabel,
    operationOrderId: operationIdentity.operationOrderId,
    operationTypeLabel: operationIdentity.operationTypeLabel,
    orderLabel: preloadStop?.order_id || "",
    siloLabel: resolveRouteSegmentSiloLabel(trip, preloadStop),
    productCodeLabel: productData.productCode || "",
    ...resolveRouteSegmentCompartmentFill(trip, preloadStop, compartmentFillContext),
    productLabel: productData.productLabel || productChip?.label || "",
    quantityLabel: typeof preloadStop?.quantity_tons === "number" ? `${formatTons(preloadStop.quantity_tons)} t` : null,
    quantityTons: typeof preloadStop?.quantity_tons === "number" ? preloadStop.quantity_tons : null,
    compartmentAllocations: getStopCompartmentAllocations(preloadStop),
    mapAnchorStopKey: buildRouteMapStopKey(preloadStop, { fallbackIndex: globalSequence }),
    requiresAspirator: operationIdentity.requiresAspirator,
    requiresOntario: operationIdentity.requiresOntario,
    routeDistanceSource: displayedRouteSegment.distanceSource || "unavailable",
    routeDistanceSourceLabel: displayedRouteSegment.distanceSourceLabel || null,
    routeEstimatedTravelDurationMinutes: 0,
    routeFromAddress: getRouteStopAddressLabel(preloadStop?.address),
    routeFromLabel: getRouteStopLabel(preloadStop),
    routeScheduledTravelDurationMinutes: 0,
    routeSegmentIndex: `preload-${globalSequence}`,
    routeSpeedKmh: null,
    routeToAddress: getRouteStopAddressLabel(preloadStop?.address),
    routeToLabel: getRouteStopLabel(preloadStop),
    sameSiteGroupContainsMixedStopTypes: false,
    sameSiteGroupIndex: Number.isInteger(sameSiteMetadata?.sameSiteGroupIndex) ? sameSiteMetadata.sameSiteGroupIndex : 0,
    sameSiteGroupKey,
    sameSiteGroupSize: Number.isInteger(sameSiteMetadata?.sameSiteGroupSize) ? sameSiteMetadata.sameSiteGroupSize : 1,
    sameSiteGroupStartSequence,
    scheduledAt: null,
    scheduledAtMinutes: null,
    segmentColor,
    segmentEndColor: segmentColor,
    segmentStartColor: segmentColor,
    serviceDurationLabel: "0 min",
    serviceDurationMinutes: 0,
    stopType: "loading",
    toAddress: getRouteStopAddressLabel(preloadStop?.address),
    toLabel: getRouteStopLabel(preloadStop),
    travelDurationLabel: "0 min",
    displayTravelDurationMinutes: 0,
    travelDurationMinutes: 0,
    truckLoadBeforeTons,
    typeLabel: getStopTypeLabel(preloadStop),
    waitMinutes: null,
    from: {
      address: getRouteStopAddressLabel(preloadStop?.address),
      label: getRouteStopLabel(preloadStop),
      city: getAddressCityLabel(preloadStop?.address),
      showHomeMarker: false,
    },
    to: {
      address: getRouteStopAddressLabel(preloadStop?.address),
      stopType: "loading",
      typeLabel: getStopTypeLabel(preloadStop),
      orderLabel: preloadStop?.order_id || "",
      badgeTitle: getRouteStopBadgeTooltip("loading"),
      iconTitle: getRouteStopIconTooltip("loading"),
      label: getRouteStopLabel(preloadStop),
      city: getAddressCityLabel(preloadStop?.address),
    },
  };
}

function shouldShowInitialPreloadStopInDispatchPeriod(preloadStop = null, dispatchPeriod = "day", dispatchDate = "") {
  return preloadStop?.isEndOfDayLoadSourcePreload !== true
    || shouldShowEndOfDayLoadInDispatchPeriod(preloadStop, dispatchPeriod, dispatchDate, { dateRole: "target" });
}

function buildInitialPreloadRouteSequenceItems(initialPreloadSteps, trip, assignment, {
  compartmentFillContext = null,
  dispatchDate = "",
  dispatchPeriod = "day",
  sequenceStart = 1,
} = {}) {
  const assignmentId = assignment?.truck_id || "";
  const tripId = trip?.trip_id || "";
  const sourcePreloadContextIndex = buildEndOfDayLoadSourcePreloadContextIndex(assignment?.end_of_day_load_source_preloads);
  const preloadStops = groupInitialPreloadStopsBySite((Array.isArray(initialPreloadSteps) ? initialPreloadSteps : [])
    .map((step) => buildInitialPreloadStopFromStep(step, sourcePreloadContextIndex))
    .filter((preloadStop) => shouldShowInitialPreloadStopInDispatchPeriod(preloadStop, dispatchPeriod, dispatchDate)));
  const groupMetadata = buildInitialPreloadGroupMetadata(preloadStops);
  let runningTruckLoadBeforeTons = 0;

  return preloadStops.map((preloadStop, preloadIndex) => {
    const globalSequence = sequenceStart + preloadIndex;
    const item = {
      key: `route-segment-${globalSequence}`,
      clientId: "",
      isClientSelectable: false,
      isSelected: false,
      tripId,
      ...buildInitialPreloadCommonModel(preloadStop, trip, {
        assignmentId,
        compartmentFillContext,
        globalSequence,
        sameSiteMetadata: groupMetadata[preloadIndex] || null,
        tripId,
        truckLoadBeforeTons: runningTruckLoadBeforeTons,
      }),
    };

    const quantityTons = Number(item.quantityTons);
    runningTruckLoadBeforeTons = Number.isFinite(quantityTons) && quantityTons > 0
      ? runningTruckLoadBeforeTons + quantityTons
      : runningTruckLoadBeforeTons;

    return item;
  });
}

function hasTimelineActivationHighlight(transientActivatedStepKeys, highlightKeys = []) {
  if (!(transientActivatedStepKeys instanceof Set)) {
    return false;
  }

  return (Array.isArray(highlightKeys) ? highlightKeys : [])
    .some((highlightKey) => typeof highlightKey === "string" && highlightKey && transientActivatedStepKeys.has(highlightKey));
}

function buildInitialPreloadActivationHighlightKeys(preloadStop = null) {
  const highlightKeys = [
    getInitialPreloadHighlightKey(preloadStop?.step_id),
    getInitialPreloadOrderHighlightKey(preloadStop?.order_id),
    getLoadingHighlightKey(preloadStop?.step_id),
    getLoadingOrderHighlightKey(preloadStop?.order_id),
    ...normalizeEndOfDayLoadValues(preloadStop?.endOfDayLoadOrderIds).map((orderId) => getInitialPreloadOrderHighlightKey(orderId)),
    ...normalizeEndOfDayLoadValues(preloadStop?.endOfDayLoadOrderIds).map((orderId) => getLoadingOrderHighlightKey(orderId)),
    ...normalizeEndOfDayLoadValues(preloadStop?.endOfDayLoadDeliveryIds).map((deliveryId) => getDeliveryHighlightKey(deliveryId)),
  ].filter(Boolean);

  return [...new Set(highlightKeys)];
}

function buildInitialPreloadTimelineSegments(initialPreloadSteps, trip, assignment, {
  compartmentFillContext = null,
  dispatchDate = "",
  dispatchPeriod = "day",
  sequenceStart = 1,
  transientActivatedStepKeys = new Set(),
} = {}) {
  const assignmentId = assignment?.truck_id || "";
  const tripId = trip?.trip_id || "";
  const sourcePreloadContextIndex = buildEndOfDayLoadSourcePreloadContextIndex(assignment?.end_of_day_load_source_preloads);
  const preloadStops = groupInitialPreloadStopsBySite((Array.isArray(initialPreloadSteps) ? initialPreloadSteps : [])
    .map((step) => buildInitialPreloadStopFromStep(step, sourcePreloadContextIndex))
    .filter((preloadStop) => shouldShowInitialPreloadStopInDispatchPeriod(preloadStop, dispatchPeriod, dispatchDate)));
  const groupMetadata = buildInitialPreloadGroupMetadata(preloadStops);
  let runningTruckLoadBeforeTons = 0;

  return preloadStops.map((preloadStop, preloadIndex) => {
    const globalSequence = sequenceStart + preloadIndex;
    const segment = {
      arrivalAt: null,
      arrivalAtMinutes: null,
      businessStep: null,
      clientId: "",
      isActivationHighlighted: hasTimelineActivationHighlight(
        transientActivatedStepKeys,
        buildInitialPreloadActivationHighlightKeys(preloadStop),
      ),
      isCurrentTrip: Boolean(tripId),
      isSelectable: false,
      isSelectedClient: false,
      reassignmentAvailability: null,
      reassignmentDrag: null,
      segmentReorder: null,
      segmentEndAt: null,
      segmentEndMinutes: null,
      segmentStartAt: null,
      segmentStartMinutes: null,
      statusMarkers: [],
      statusSummaryLabel: "",
      timeWindowOverride: null,
      timelineDurationLabel: "0 min",
      timelineDurationMinutes: 0,
      tripId,
      tripLabel: tripId || "Tournée",
      tripSequence: globalSequence,
      visualSegmentEndAt: null,
      visualSegmentEndMinutes: null,
      visualSegmentStartAt: null,
      visualSegmentStartMinutes: null,
      visualServiceDurationMinutes: 0,
      visualTimelineDurationMinutes: 0,
      ...buildInitialPreloadCommonModel(preloadStop, trip, {
        assignmentId,
        compartmentFillContext,
        globalSequence,
        sameSiteMetadata: groupMetadata[preloadIndex] || null,
        tripId,
        truckLoadBeforeTons: runningTruckLoadBeforeTons,
      }),
    };

    const quantityTons = Number(segment.quantityTons);
    runningTruckLoadBeforeTons = Number.isFinite(quantityTons) && quantityTons > 0
      ? runningTruckLoadBeforeTons + quantityTons
      : runningTruckLoadBeforeTons;

    return segment;
  });
}

function buildEndOfDayLoadStopFromMarkerItem(markerItem, marker) {
  const quantityTons = Number(markerItem?.quantity_tons);
  const isSourcePreload = marker?.is_end_of_day_load_source_preload === true
    || marker?.isEndOfDayLoadSourcePreload === true;
  const loadingClientName = normalizeEndOfDayLoadLabel(markerItem?.loading_client_name || "");
  const loadingLabel = normalizeEndOfDayLoadLabel(marker?.loading_label || "");
  return {
    address: markerItem?.loading_address || marker?.loading_address || "",
    client_name: loadingClientName || loadingLabel || "",
    compartment_allocations: Array.isArray(markerItem?.compartment_allocations) ? markerItem.compartment_allocations : [],
    delivery_id: markerItem?.delivery_id || "",
    delivery_mode: marker?.delivery_mode || "",
    grain_type: markerItem?.grain_type || (Array.isArray(marker?.grain_types) ? marker.grain_types[0] || "" : ""),
    isEndOfDayLoad: !isSourcePreload,
    isEndOfDayLoadSourcePreload: isSourcePreload,
    is_end_of_day_load: !isSourcePreload,
    is_end_of_day_load_source_preload: isSourcePreload,
    label: loadingClientName || loadingLabel || markerItem?.order_id || END_OF_DAY_LOAD_LABEL,
    marker_key: marker?.marker_key || "",
    order_id: markerItem?.order_id || "",
    quantity_tons: Number.isFinite(quantityTons) ? quantityTons : 0,
    service_time_minutes: 0,
    step_id: markerItem?.step_id || markerItem?.delivery_id || markerItem?.order_id || marker?.marker_key || "",
    stop_type: "loading",
    target_period: marker?.target_period || "",
  };
}

function buildEndOfDayLoadGroupMetadata(stops = []) {
  const metadata = Array.from({ length: Array.isArray(stops) ? stops.length : 0 }, () => ({
    isSameSiteSequenceGroup: false,
    sameSiteGroupContainsMixedStopTypes: false,
    sameSiteGroupIndex: 0,
    sameSiteGroupKey: "",
    sameSiteGroupSize: 1,
    sameSiteGroupStartOffset: 0,
  }));
  if (metadata.length <= 1) {
    return metadata;
  }

  const baseGroupKey = resolveTimelineStopGroupKey(stops[0])
    || `end-of-day:${normalizeTimelineStopMatchValue(stops[0]?.marker_key) || "load"}`;
  const sameSiteGroupKey = `end-of-day-load:${baseGroupKey}`;
  return metadata.map((item, index) => ({
    ...item,
    isSameSiteSequenceGroup: true,
    sameSiteGroupIndex: index,
    sameSiteGroupKey,
    sameSiteGroupSize: metadata.length,
    sameSiteGroupStartOffset: 0,
  }));
}

function buildEndOfDayLoadDisplayedRouteSegment(stop) {
  return {
    distanceKm: 0,
    distanceLabel: "0 km",
    distanceSource: "geometrymeasured",
    distanceSourceLabel: null,
    displayDurationMinutes: 0,
    durationMinutes: 0,
    estimatedDurationMinutes: 0,
    index: 0,
    isEndOfDayLoad: !stop?.isEndOfDayLoadSourcePreload,
    isEndOfDayLoadSourcePreload: stop?.isEndOfDayLoadSourcePreload === true,
    scheduledDurationMinutes: 0,
    stops: [stop, stop],
  };
}

function resolveEndOfDayLoadMarkerSourceTruckId(marker) {
  const sourceTruckIds = [];
  const markerItems = Array.isArray(marker?.items) ? marker.items : [];
  markerItems.forEach((item) => {
    const sourceTruckId = String(item?.source_truck_id || "").trim();
    if (sourceTruckId && !sourceTruckIds.includes(sourceTruckId)) {
      sourceTruckIds.push(sourceTruckId);
    }
  });
  return sourceTruckIds.length === 1 ? sourceTruckIds[0] : "";
}

function resolveEndOfDayLoadMarkerSourceTruckName(marker) {
  const sourceTruckId = resolveEndOfDayLoadMarkerSourceTruckId(marker);
  if (!sourceTruckId) {
    return "";
  }
  const markerItems = Array.isArray(marker?.items) ? marker.items : [];
  const matchingItem = markerItems.find((item) => String(item?.source_truck_id || "").trim() === sourceTruckId);
  return String(matchingItem?.source_truck_name || "").trim();
}

function buildEndOfDayLoadCommonModel(loadStop, marker, assignment, {
  compartmentFillContext = null,
  globalSequence = 1,
  sameSiteMetadata = null,
  sourcePreload = false,
  truckLoadBeforeTons = null,
} = {}) {
  const assignmentId = assignment?.truck_id || "";
  const endOfDayLoadTargetTruckId = String(marker?.target_truck_id || marker?.truck_id || "").trim();
  const endOfDayLoadSourceTruckId = resolveEndOfDayLoadMarkerSourceTruckId(marker);
  const endOfDayLoadSourceTruckName = resolveEndOfDayLoadMarkerSourceTruckName(marker);
  const endOfDayLoadVisualMode = resolveEndOfDayLoadVisualModeValue(marker);
  const endOfDayLoadDisplayMode = resolveEndOfDayLoadDisplayModeValue(marker, { sourcePreload });
  const tripId = sourcePreload
    ? `end-of-day-load-source:${marker?.marker_key || globalSequence}`
    : `end-of-day-load:${marker?.marker_key || globalSequence}`;
  const displayedRouteSegment = buildEndOfDayLoadDisplayedRouteSegment(loadStop);
  const operationIdentity = buildOperationIdentity(loadStop, {
    assignmentId,
    trip: null,
    tripId,
    tripSequence: globalSequence,
  });
  const productChip = resolveRouteSegmentProductChip(null, loadStop);
  const productData = resolveRouteSegmentProductData(null, loadStop);
  const endOfDayLoadRouteColor = resolveEndOfDayLoadRouteColor({
    ...(loadStop || {}),
    ...(marker || {}),
  });
  const sameSiteGroupStartSequence = globalSequence - (Number(sameSiteMetadata?.sameSiteGroupIndex) || 0);
  const sameSiteGroupKey = sameSiteMetadata?.isSameSiteSequenceGroup
    ? `${assignmentId || "assignment"}::${tripId}::${sameSiteGroupStartSequence}::${sameSiteMetadata.sameSiteGroupKey}`
    : "";
  const chips = buildRouteSegmentChips(displayedRouteSegment, loadStop, null, { serviceMinutesOverride: 0 })
    .filter((chip) => chip?.key !== "dist" && chip?.key !== "dur" && chip?.key !== "svc");

  return {
    assignmentId,
    aspiratorRequirementLabel: operationIdentity.aspiratorRequirementLabel,
    chips,
    color: endOfDayLoadRouteColor,
    distanceLabel: null,
    dominantPriority: "",
    endOfDayLoadDeliveryIds: Array.isArray(marker?.delivery_ids)
      ? marker.delivery_ids.map((value) => String(value || "").trim()).filter(Boolean)
      : [loadStop?.delivery_id || ""].filter(Boolean),
    endOfDayLoadMarkerKey: marker?.marker_key || "",
    endOfDayLoadMarkerDispatchDate: marker?.marker_dispatch_date || "",
    endOfDayLoadDeliveryMode: marker?.delivery_mode || endOfDayLoadVisualMode,
    endOfDayLoadDisplayMode,
    endOfDayLoadOrderIds: Array.isArray(marker?.order_ids)
      ? marker.order_ids.map((value) => String(value || "").trim()).filter(Boolean)
      : [loadStop?.order_id || ""].filter(Boolean),
    endOfDayLoadSourceDispatchDate: marker?.source_dispatch_date || "",
    endOfDayLoadSourceTruckId: endOfDayLoadSourceTruckId,
    endOfDayLoadSourceTruckName: endOfDayLoadSourceTruckName,
    endOfDayLoadTargetDispatchDate: marker?.target_dispatch_date || "",
    endOfDayLoadTargetPeriod: marker?.target_period || (endOfDayLoadVisualMode === "evening" ? "evening" : "day"),
    endOfDayLoadTargetTruckId: endOfDayLoadTargetTruckId,
    globalSequence,
    hasPriorityTimeAnchor: false,
    isEndOfDayLoad: !sourcePreload,
    isEndOfDayLoadSourcePreload: sourcePreload,
    isInitialPreload: false,
    isInitialRouteSegment: false,
    isPreloadedPreviousDay: false,
    isSameSiteSequenceGroup: Boolean(sameSiteMetadata?.isSameSiteSequenceGroup),
    isTimelineInformational: true,
    operationAddress: operationIdentity.operationAddress,
    aspiratorMissionGroupKey: operationIdentity.aspiratorMissionGroupKey,
    aspiratorMissionRouteSignature: operationIdentity.aspiratorMissionRouteSignature,
    operationDeliveryId: operationIdentity.operationDeliveryId,
    operationDisambiguatorLabel: operationIdentity.operationDisambiguatorLabel,
    operationKey: operationIdentity.operationKey,
    operationLabel: operationIdentity.operationLabel,
    operationOwnerLabel: operationIdentity.operationOwnerLabel,
    operationOrderId: operationIdentity.operationOrderId,
    operationTypeLabel: END_OF_DAY_LOAD_LABEL,
    orderLabel: loadStop?.order_id || "",
    siloLabel: resolveRouteSegmentSiloLabel(null, loadStop),
    productCodeLabel: productData.productCode || "",
    ...resolveRouteSegmentCompartmentFill(null, loadStop, compartmentFillContext),
    productLabel: productData.productLabel || productChip?.label || "",
    quantityLabel: typeof loadStop?.quantity_tons === "number" ? `${formatTons(loadStop.quantity_tons)} t` : null,
    quantityTons: typeof loadStop?.quantity_tons === "number" ? loadStop.quantity_tons : null,
    compartmentAllocations: getStopCompartmentAllocations(loadStop),
    mapAnchorStopKey: buildRouteMapStopKey(loadStop, { fallbackIndex: globalSequence }),
    requiresAspirator: operationIdentity.requiresAspirator,
    requiresOntario: operationIdentity.requiresOntario,
    routeDistanceSource: "unavailable",
    routeDistanceSourceLabel: null,
    routeEstimatedTravelDurationMinutes: 0,
    routeFromAddress: getRouteStopAddressLabel(loadStop?.address),
    routeFromLabel: getRouteStopLabel(loadStop),
    routeScheduledTravelDurationMinutes: 0,
    routeSegmentIndex: sourcePreload ? `end-of-day-load-source-${globalSequence}` : `end-of-day-load-${globalSequence}`,
    routeSpeedKmh: null,
    routeToAddress: getRouteStopAddressLabel(loadStop?.address),
    routeToLabel: getRouteStopLabel(loadStop),
    sameSiteGroupContainsMixedStopTypes: false,
    sameSiteGroupIndex: Number.isInteger(sameSiteMetadata?.sameSiteGroupIndex) ? sameSiteMetadata.sameSiteGroupIndex : 0,
    sameSiteGroupKey,
    sameSiteGroupSize: Number.isInteger(sameSiteMetadata?.sameSiteGroupSize) ? sameSiteMetadata.sameSiteGroupSize : 1,
    sameSiteGroupStartSequence,
    scheduledAt: null,
    scheduledAtMinutes: null,
    segmentColor: endOfDayLoadRouteColor,
    segmentEndColor: endOfDayLoadRouteColor,
    segmentStartColor: endOfDayLoadRouteColor,
    serviceDurationLabel: "",
    serviceDurationMinutes: 0,
    stopType: "loading",
    toAddress: getRouteStopAddressLabel(loadStop?.address),
    toLabel: getRouteStopLabel(loadStop),
    travelDurationLabel: "",
    displayTravelDurationMinutes: 0,
    travelDurationMinutes: 0,
    truckLoadBeforeTons: Number.isFinite(Number(truckLoadBeforeTons)) && Number(truckLoadBeforeTons) >= 0
      ? Number(truckLoadBeforeTons)
      : null,
    typeLabel: END_OF_DAY_LOAD_LABEL,
    waitMinutes: null,
    from: {
      address: getRouteStopAddressLabel(loadStop?.address),
      label: getRouteStopLabel(loadStop),
      city: getAddressCityLabel(loadStop?.address),
      showHomeMarker: false,
    },
    to: {
      address: getRouteStopAddressLabel(loadStop?.address),
      stopType: "loading",
      typeLabel: END_OF_DAY_LOAD_LABEL,
      orderLabel: loadStop?.order_id || "",
      badgeTitle: END_OF_DAY_LOAD_LABEL,
      iconTitle: END_OF_DAY_LOAD_LABEL,
      label: getRouteStopLabel(loadStop),
      city: getAddressCityLabel(loadStop?.address),
    },
  };
}

function buildEndOfDayLoadActivationHighlightKeys(loadStop = null, marker = null) {
  const highlightKeys = [
    getDeliveryHighlightKey(loadStop?.delivery_id),
    getLoadingOrderHighlightKey(loadStop?.order_id),
    ...normalizeEndOfDayLoadValues(marker?.delivery_ids).map((deliveryId) => getDeliveryHighlightKey(deliveryId)),
    ...normalizeEndOfDayLoadValues(marker?.order_ids).map((orderId) => getLoadingOrderHighlightKey(orderId)),
  ].filter(Boolean);

  return [...new Set(highlightKeys)];
}

function resolveRunningTruckLoadTonsAfterSegments(segments = []) {
  let runningLoadTons = 0;

  (Array.isArray(segments) ? segments : []).forEach((segment) => {
    const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
    if (stopType === "depot_start" && segment?.isTerminalRouteMarker !== true) {
      runningLoadTons = 0;
    }

    const truckLoadBeforeTons = Number(segment?.truckLoadBeforeTons);
    if (Number.isFinite(truckLoadBeforeTons) && truckLoadBeforeTons >= 0) {
      runningLoadTons = truckLoadBeforeTons;
    }

    const quantityTons = Number(segment?.quantityTons);
    if (!Number.isFinite(quantityTons) || quantityTons <= 0) {
      return;
    }

    if (stopType === "loading") {
      runningLoadTons += quantityTons;
      return;
    }

    if (stopType === "delivery") {
      runningLoadTons = Math.max(runningLoadTons - quantityTons, 0);
    }
  });

  return runningLoadTons;
}

function buildEndOfDayLoadTimelineSegments(markers, assignment, {
  compartmentFillContext = null,
  dispatchDate = "",
  dispatchPeriod = "day",
  sequenceStart = 1,
  sourcePreload = false,
  initialTruckLoadBeforeTons = 0,
  transientActivatedStepKeys = new Set(),
} = {}) {
  const safeMarkers = (Array.isArray(markers) ? markers.filter(Boolean) : [])
    .filter((marker) => shouldShowEndOfDayLoadInDispatchPeriod(
      marker,
      dispatchPeriod,
      dispatchDate,
      { dateRole: sourcePreload ? "target" : "source" },
    ));
  const segments = [];
  let sequenceCursor = sequenceStart;
  let runningTruckLoadBeforeTons = Math.max(Number(initialTruckLoadBeforeTons) || 0, 0);
  safeMarkers.forEach((marker) => {
    const markerModel = sourcePreload
      ? { ...marker, is_end_of_day_load_source_preload: true, isEndOfDayLoadSourcePreload: true }
      : marker;
    const markerVisualMode = resolveEndOfDayLoadVisualModeValue(markerModel);
    const isEveningTargetLoad = !sourcePreload && markerVisualMode === "evening";
    const eveningDepartureMinutes = parseTimelineClockMinutes(assignment?.start_time || assignment?.startTime || "18:00") ?? 1080;
    const eveningVisualStartMinutes = Math.max(eveningDepartureMinutes - 30, 0);
    const markerItems = Array.isArray(marker?.items) && marker.items.length
      ? marker.items
      : [{ order_id: (Array.isArray(marker?.order_ids) ? marker.order_ids[0] : ""), delivery_id: (Array.isArray(marker?.delivery_ids) ? marker.delivery_ids[0] : "") }];
    const stops = markerItems.map((item) => buildEndOfDayLoadStopFromMarkerItem(item, markerModel));
    const groupMetadata = buildEndOfDayLoadGroupMetadata(stops);
    stops.forEach((loadStop, stopIndex) => {
      const globalSequence = sequenceCursor;
      sequenceCursor += 1;
      segments.push({
        arrivalAt: null,
        arrivalAtMinutes: null,
        businessStep: null,
        clientId: "",
        isActivationHighlighted: hasTimelineActivationHighlight(
          transientActivatedStepKeys,
          buildEndOfDayLoadActivationHighlightKeys(loadStop, markerModel),
        ),
        isCurrentTrip: false,
        isSelectable: false,
        isSelectedClient: false,
        reassignmentAvailability: null,
        reassignmentDrag: null,
        segmentReorder: null,
        segmentEndAt: null,
        segmentEndMinutes: isEveningTargetLoad ? eveningVisualStartMinutes : null,
        segmentStartAt: null,
        segmentStartMinutes: isEveningTargetLoad ? eveningVisualStartMinutes : null,
        statusMarkers: [],
        statusSummaryLabel: "",
        timeWindowOverride: null,
        timelineDurationLabel: "",
        timelineDurationMinutes: 0,
        tripId: sourcePreload ? `end-of-day-load-source:${marker?.marker_key || globalSequence}` : `end-of-day-load:${marker?.marker_key || globalSequence}`,
        tripLabel: END_OF_DAY_LOAD_LABEL,
        tripSequence: globalSequence,
        visualSegmentEndAt: null,
        visualSegmentEndMinutes: isEveningTargetLoad ? eveningDepartureMinutes : null,
        visualSegmentStartAt: null,
        visualSegmentStartMinutes: isEveningTargetLoad ? eveningVisualStartMinutes : null,
        visualServiceDurationMinutes: 0,
        visualTimelineDurationMinutes: isEveningTargetLoad ? 30 : 0,
        ...buildEndOfDayLoadCommonModel(loadStop, markerModel, assignment, {
          compartmentFillContext,
          globalSequence,
          sameSiteMetadata: groupMetadata[stopIndex] || null,
          sourcePreload,
          truckLoadBeforeTons: runningTruckLoadBeforeTons,
        }),
      });
      const quantityTons = Number(loadStop?.quantity_tons);
      runningTruckLoadBeforeTons = Number.isFinite(quantityTons) && quantityTons > 0
        ? runningTruckLoadBeforeTons + quantityTons
        : runningTruckLoadBeforeTons;
    });
  });
  return segments;
}

function replaceRouteSequenceMetricChips(baseChips = [], metricChips = []) {
  const resolvedBaseChips = Array.isArray(baseChips) ? baseChips.filter(Boolean) : [];
  const resolvedMetricChips = Array.isArray(metricChips)
    ? metricChips.filter((chip) => chip && (chip.key === "dist" || chip.key === "dur"))
    : [];

  if (!resolvedMetricChips.length) {
    return resolvedBaseChips;
  }

  const metricChipByKey = new Map(resolvedMetricChips.map((chip) => [chip.key, chip]));
  const mergedChips = resolvedBaseChips.map((chip) => metricChipByKey.get(chip.key) || chip);

  resolvedMetricChips.forEach((chip) => {
    if (!mergedChips.some((existingChip) => existingChip?.key === chip.key)) {
      mergedChips.push(chip);
    }
  });

  return mergedChips;
}

function rebalanceTerminalReturnRouteSequenceItems(items = []) {
  const resolvedItems = Array.isArray(items) ? items.filter(Boolean) : [];

  if (resolvedItems.length < 2) {
    return resolvedItems;
  }

  const terminalItemIndex = resolvedItems.length - 1;
  const terminalItem = resolvedItems[terminalItemIndex] || null;

  if (terminalItem?.stopType !== "depot_return") {
    return resolvedItems;
  }

  const previousItem = resolvedItems[terminalItemIndex - 1] || null;

  if (!previousItem) {
    return resolvedItems;
  }

  const terminalTravelDurationMinutes = normalizeTimelineDurationMinutes(terminalItem.travelDurationMinutes) ?? 0;
  const terminalDisplayTravelDurationMinutes = normalizeTimelineDurationMinutes(terminalItem.displayTravelDurationMinutes)
    ?? terminalTravelDurationMinutes;

  return [
    ...resolvedItems.slice(0, terminalItemIndex - 1),
    {
      ...previousItem,
      chips: replaceRouteSequenceMetricChips(previousItem.chips, terminalItem.chips),
      distanceLabel: terminalItem.distanceLabel || previousItem.distanceLabel,
      routeSegmentIndex: terminalItem.routeSegmentIndex ?? previousItem.routeSegmentIndex,
      routeDistanceSource: terminalItem.routeDistanceSource || previousItem.routeDistanceSource || "unavailable",
      routeDistanceSourceLabel: terminalItem.routeDistanceSourceLabel || previousItem.routeDistanceSourceLabel || null,
      routeEstimatedTravelDurationMinutes: terminalItem.routeEstimatedTravelDurationMinutes ?? previousItem.routeEstimatedTravelDurationMinutes ?? null,
      routeFromAddress: terminalItem.routeFromAddress || previousItem.routeFromAddress,
      routeFromLabel: terminalItem.routeFromLabel || previousItem.routeFromLabel,
      routeScheduledTravelDurationMinutes: terminalItem.routeScheduledTravelDurationMinutes ?? previousItem.routeScheduledTravelDurationMinutes ?? null,
      routeSpeedKmh: terminalItem.routeSpeedKmh ?? previousItem.routeSpeedKmh ?? null,
      routeToAddress: terminalItem.routeToAddress || previousItem.routeToAddress,
      routeToLabel: terminalItem.routeToLabel || previousItem.routeToLabel,
      displayTravelDurationMinutes: terminalDisplayTravelDurationMinutes,
      travelDurationMinutes: terminalTravelDurationMinutes,
    },
    {
      ...terminalItem,
      chips: [],
      displayTravelDurationMinutes: 0,
      distanceLabel: null,
      isTerminalRouteMarker: true,
      serviceDurationLabel: null,
      serviceDurationMinutes: 0,
      travelDurationMinutes: 0,
    },
  ];
}

function normalizeVisibleRouteSequenceStopType(stopType = "") {
  const normalizedStopType = typeof stopType === "string" ? stopType.trim() : "";
  return normalizedStopType === "loading" || normalizedStopType === "delivery"
    ? normalizedStopType
    : "";
}

function normalizeVisibleRouteSequenceTextValue(value = "") {
  return normalizeTimelineTextIdentity(value);
}

function normalizeVisibleRouteSequenceOrderKey(item = null) {
  const operationOrderId = typeof item?.operationOrderId === "string"
    ? item.operationOrderId.trim()
    : "";
  if (operationOrderId) {
    return operationOrderId.toLowerCase();
  }

  const orderLabel = typeof item?.orderLabel === "string"
    ? item.orderLabel.trim()
    : "";
  return orderLabel ? orderLabel.toLowerCase() : "";
}

function hasAbsorbedVisibleRouteSequenceIdentity(leftItem = null, rightItem = null) {
  const leftOrderKey = normalizeVisibleRouteSequenceOrderKey(leftItem);
  const rightOrderKey = normalizeVisibleRouteSequenceOrderKey(rightItem);

  if (!leftOrderKey || !rightOrderKey || leftOrderKey !== rightOrderKey) {
    return false;
  }

  const leftAddressKey = normalizeVisibleRouteSequenceTextValue(
    leftItem?.operationAddress || leftItem?.toAddress || leftItem?.from?.address,
  );
  const rightAddressKey = normalizeVisibleRouteSequenceTextValue(
    rightItem?.operationAddress || rightItem?.toAddress || rightItem?.from?.address,
  );

  if (leftAddressKey && rightAddressKey && leftAddressKey !== rightAddressKey) {
    return false;
  }

  const leftStopType = normalizeVisibleRouteSequenceStopType(leftItem?.stopType);
  const rightStopType = normalizeVisibleRouteSequenceStopType(rightItem?.stopType);

  if (leftStopType === "loading" && rightStopType === "delivery") {
    return true;
  }

  const leftRouteFromKey = normalizeVisibleRouteSequenceTextValue(leftItem?.routeFromLabel || leftItem?.from?.label);
  const rightRouteFromKey = normalizeVisibleRouteSequenceTextValue(rightItem?.routeFromLabel || rightItem?.from?.label);

  if (leftRouteFromKey && rightRouteFromKey && leftRouteFromKey !== rightRouteFromKey) {
    return false;
  }

  const leftRouteToKey = normalizeVisibleRouteSequenceTextValue(leftItem?.routeToLabel || leftItem?.to?.label);
  const rightRouteToKey = normalizeVisibleRouteSequenceTextValue(rightItem?.routeToLabel || rightItem?.to?.label);

  if (leftRouteToKey && rightRouteToKey && leftRouteToKey !== rightRouteToKey) {
    return false;
  }

  return true;
}

function findAbsorbingVisibleRouteSequenceGroup(items = [], startIndex = 0) {
  const item = items[startIndex] || null;
  if (
    !item
    || item?.isSameSiteSequenceGroup
    || item?.requiresAspirator !== true
    || normalizeVisibleRouteSequenceStopType(item?.stopType) !== "loading"
  ) {
    return null;
  }

  for (let candidateIndex = startIndex + 1; candidateIndex < items.length; candidateIndex += 1) {
    const candidateItem = items[candidateIndex] || null;
    const candidateGroupKey = typeof candidateItem?.sameSiteGroupKey === "string"
      ? candidateItem.sameSiteGroupKey.trim()
      : "";
    const candidateGroupSize = Number.isInteger(candidateItem?.sameSiteGroupSize) ? candidateItem.sameSiteGroupSize : 1;

    if (
      !candidateItem?.isSameSiteSequenceGroup
      || !candidateGroupKey
      || candidateGroupSize <= 1
      || candidateItem?.sameSiteGroupIndex !== 0
    ) {
      continue;
    }

    for (let memberIndex = candidateIndex; memberIndex < items.length; memberIndex += 1) {
      const candidateMember = items[memberIndex] || null;
      const candidateMemberGroupKey = typeof candidateMember?.sameSiteGroupKey === "string"
        ? candidateMember.sameSiteGroupKey.trim()
        : "";

      if (candidateMemberGroupKey !== candidateGroupKey) {
        break;
      }

      if (
        candidateMember?.requiresAspirator === true
        && normalizeVisibleRouteSequenceStopType(candidateMember?.stopType) === "delivery"
        && hasAbsorbedVisibleRouteSequenceIdentity(item, candidateMember)
      ) {
        return candidateItem;
      }
    }
  }

  return null;
}

function resolveVisibleRouteSequenceDisplayStride(item = null) {
  if (item?.isAspiratorMissionMerged !== true && item?.isSameAddressLoadingDeliveryMerged !== true) {
    return item?.isPmOperationalLoading === true
      ? Math.max(new Set((item.endOfDayLoadOrderIds || []).filter(Boolean)).size, 1)
      : 1;
  }

  const missionOrderIds = Array.isArray(item?.missionOrderIds)
    ? item.missionOrderIds.filter((orderId) => typeof orderId === "string" && orderId.trim())
    : [];
  if (missionOrderIds.length > 0) {
    return missionOrderIds.length;
  }

  const orderKeys = new Set(
    (Array.isArray(item?.mergedMemberSegments) ? item.mergedMemberSegments : [])
      .map((segment) => String(segment?.operationOrderId || segment?.orderLabel || "").trim().toLowerCase())
      .filter(Boolean),
  );
  return Math.max(orderKeys.size, 1);
}

export function resolveVisibleRouteSequenceItems(items = []) {
  const resolvedItems = Array.isArray(items) ? items.filter(Boolean) : [];
  const visibleItems = [];
  let nextDisplaySequence = 1;

  resolvedItems.forEach((item, itemIndex) => {
    if (findAbsorbingVisibleRouteSequenceGroup(resolvedItems, itemIndex)) {
      return;
    }

    const displayStride = resolveVisibleRouteSequenceDisplayStride(item);
    const sequenceLabel = displayStride > 1
      ? `${nextDisplaySequence}-${nextDisplaySequence + displayStride - 1}`
      : String(nextDisplaySequence);

    visibleItems.push({
      ...item,
      ...(item?.isAspiratorMissionMerged === true || item?.isSameAddressLoadingDeliveryMerged === true || displayStride > 1
        ? { mergedOrderSequenceLabel: sequenceLabel }
        : {}),
      routeSequenceDisplaySequence: nextDisplaySequence,
    });
    nextDisplaySequence += displayStride;
  });

  const groupDisplayRangeByKey = new Map();

  visibleItems.forEach((item) => {
    const groupKey = typeof item?.sameSiteGroupKey === "string" ? item.sameSiteGroupKey.trim() : "";
    const displaySequence = Number(item?.routeSequenceDisplaySequence);

    if (!groupKey || !Number.isInteger(displaySequence) || displaySequence <= 0) {
      return;
    }

    // Un Chargé PM dans un groupe du même site occupe ses N numéros jusqu'au bout du groupe.
    const endSequence = displaySequence + (item?.isPmOperationalLoading === true ? resolveVisibleRouteSequenceDisplayStride(item) - 1 : 0);
    const existingRange = groupDisplayRangeByKey.get(groupKey);
    if (existingRange) {
      existingRange.endSequence = endSequence;
      return;
    }

    groupDisplayRangeByKey.set(groupKey, {
      endSequence,
      startSequence: displaySequence,
    });
  });

  return visibleItems.map((item) => {
    const groupKey = typeof item?.sameSiteGroupKey === "string" ? item.sameSiteGroupKey.trim() : "";
    const groupDisplayRange = groupKey ? groupDisplayRangeByKey.get(groupKey) || null : null;
    const displaySequence = Number(item?.routeSequenceDisplaySequence);
    const resolvedDisplaySequence = Number.isInteger(displaySequence) && displaySequence > 0
      ? displaySequence
      : null;

    return {
      ...item,
      routeSequenceDisplaySequence: resolvedDisplaySequence,
      sameSiteGroupDisplayEndSequence: groupDisplayRange?.endSequence || resolvedDisplaySequence,
      sameSiteGroupDisplayStartSequence: groupDisplayRange?.startSequence || resolvedDisplaySequence,
    };
  });
}

export function buildRouteSequenceData(state, assignment, trip) {
  const routeSegments = getTripRouteSegments(trip, assignment, buildRouteDurationOptionsFromState(state));
  const initialPreloadSteps = getTripInitialPreloadSteps(trip);
  const dispatchPeriod = normalizeDispatchPeriodValue(
    state?.resultsData?.ui_dispatch_period
    || state?.resultsData?.dispatch_period
    || state?.ui_dispatch_period
    || state?.dispatch_period,
  );
  const dispatchDate = normalizeEndOfDayLoadDateValue(
    state?.resultsData?.dispatch_date
    || state?.resultsData?.planning_summary?.dispatch_date
    || state?.dispatch_date,
  );
  const compartmentFillContext = buildCompartmentFillContext(state);
  const initialPreloadItems = buildInitialPreloadRouteSequenceItems(initialPreloadSteps, trip, assignment, {
    compartmentFillContext,
    dispatchDate,
    dispatchPeriod,
    sequenceStart: 1,
  });
  const initialPreloadCount = initialPreloadItems.length;
  const preloadMetadataBySegment = buildStartOfDayPreloadMetadata(routeSegments);
  const { entries: visibleRouteSegments, visibleSequenceBySegmentIndex } = buildVisibleRouteSegmentEntries(
    routeSegments,
    preloadMetadataBySegment,
  );
  const initialDepotTravelSegment = getInitialDepotTravelSegment(routeSegments, preloadMetadataBySegment, initialPreloadSteps);
  const sequenceOffset = initialDepotTravelSegment ? 1 : 0;
  const visualLayoutBySegment = buildTimelineVisualLayoutBySegment(routeSegments, trip);
  const serviceAggregationBySegment = buildTimelineServiceAggregationBySegment(routeSegments, assignment, trip);
  const operationRepetitionMetadata = buildOperationRepetitionMetadata(routeSegments);
  const statusIndex = buildTripTimelineStatusIndex(trip);
  const assignmentId = assignment?.truck_id || "";
  const tripId = trip?.trip_id || "";
  let lastAttributedOutboundRouteIndex = null;
  const operationItems = visibleRouteSegments.map(({ segment, segmentIndex }, visibleSegmentIndex) => {
    const globalSequence = visibleSegmentIndex + 1 + initialPreloadCount + sequenceOffset;
    const visualLayout = visualLayoutBySegment[segmentIndex] || null;
    const displayContext = resolveRouteSegmentDisplayContext(routeSegments, segmentIndex, visualLayout);
    const routeFromStop = displayContext.routeFromStop || null;
    const routeToStop = displayContext.routeToStop || null;
    const fromStop = getRouteSegmentStartStop(segment) || routeFromStop || null;
    const toStop = displayContext.currentStop || getRouteSegmentTargetStop(segment) || routeToStop || fromStop;
    const chipStop = toStop || routeToStop || fromStop;
    let displayedRouteSegment = initialDepotTravelSegment && segmentIndex === initialDepotTravelSegment.segmentIndex && !displayContext.usesOutboundSegment
      ? buildZeroDistanceDisplayedRouteSegment(segment, toStop)
      : displayContext.displayedRouteSegment || segment;
    const travelAttribution = resolveTimelineRouteTravelAttribution({
      displayedRouteSegment,
      displayContext,
      lastAttributedOutboundRouteIndex,
      segment,
      targetStop: chipStop,
    });
    lastAttributedOutboundRouteIndex = travelAttribution.lastAttributedOutboundRouteIndex;
    displayedRouteSegment = travelAttribution.displayedRouteSegment;
    const operationIdentity = buildOperationIdentity(chipStop, {
      assignmentId,
      repetitionMetadata: operationRepetitionMetadata.get(segment.index) || null,
      trip,
      tripId,
      tripSequence: globalSequence,
    });
    const destinationType = chipStop?.stop_type || toStop?.stop_type || fromStop?.stop_type || "delivery";
    const matchedStep = resolveTimelineSegmentOperationalStep(chipStop, statusIndex);
    const priorityMetadata = resolveTimelineSegmentPriorityMetadata(chipStop, matchedStep);
    const scheduledAt = resolveTimelineSegmentScheduledAt(chipStop, matchedStep);
    const scheduledAtMinutes = parseTimelineClockMinutes(scheduledAt);
    const hasPriorityTimeAnchor = Boolean(priorityMetadata.dominantPriority && scheduledAtMinutes != null);
    const isPreloadedPreviousDay = resolveTimelineSegmentPreloadedPreviousDay(chipStop, matchedStep);
    const clientId = getClientSelectionId(chipStop);
    const serviceMinutesOverride = normalizeTimelineDurationMinutes(
      serviceAggregationBySegment[segment.index - 1]?.serviceDurationMinutes,
    );
    const serviceDurationMinutes = serviceMinutesOverride ?? normalizeTimelineDurationMinutes(chipStop?.service_time_minutes) ?? 0;
    const waitMinutes = normalizeTimelineDurationMinutes(chipStop?.wait_minutes);
    const travelDurationMinutes = travelAttribution.travelDurationMinutes;
    const displayTravelDurationMinutes = travelAttribution.displayTravelDurationMinutes;
    const mapHighlightSegmentIndex = resolveRouteSequenceMapHighlightSegmentIndex(
      routeSegments,
      segmentIndex,
      visualLayout,
      displayContext,
    );
    const productChip = resolveRouteSegmentProductChip(trip, chipStop);
    const siloLabel = resolveRouteSegmentSiloLabel(trip, chipStop);
    const sameSiteGroupRunStartIndex = Number.isInteger(visualLayout?.sameSiteGroupRunStartIndex)
      ? visualLayout.sameSiteGroupRunStartIndex
      : segmentIndex;
    const sameSiteGroupStartSequence = (visibleSequenceBySegmentIndex.get(sameSiteGroupRunStartIndex) || (visibleSegmentIndex + 1)) + initialPreloadCount + sequenceOffset;
    const sameSiteGroupKey = Boolean(visualLayout?.isSameSiteSequenceGroup)
      ? `${assignmentId || "assignment"}::${tripId || "trip"}::${sameSiteGroupStartSequence}::${visualLayout?.sameSiteGroupStopKey || "same-site"}`
      : "";

    return {
      key: `route-segment-${globalSequence}`,
      assignmentId,
      aspiratorRequirementLabel: operationIdentity.aspiratorRequirementLabel,
      distanceLabel: displayedRouteSegment.distanceLabel || null,
      globalSequence,
      isSameSiteSequenceGroup: Boolean(visualLayout?.isSameSiteSequenceGroup),
      routeSegmentIndex: String(mapHighlightSegmentIndex ?? segment.index ?? globalSequence),
      clientId: clientId || "",
      isClientSelectable: Boolean(clientId),
      isSelected: Boolean(clientId && state?.selectedClientId === clientId),
      orderLabel: chipStop?.order_id || "",
      siloLabel,
      operationAddress: operationIdentity.operationAddress,
      aspiratorMissionGroupKey: operationIdentity.aspiratorMissionGroupKey,
      aspiratorMissionRouteSignature: operationIdentity.aspiratorMissionRouteSignature,
      operationDeliveryId: operationIdentity.operationDeliveryId,
      operationDisambiguatorLabel: operationIdentity.operationDisambiguatorLabel,
      operationKey: operationIdentity.operationKey,
      operationLabel: operationIdentity.operationLabel,
      operationOwnerLabel: operationIdentity.operationOwnerLabel,
      operationOrderId: operationIdentity.operationOrderId,
      operationTypeLabel: operationIdentity.operationTypeLabel,
      dominantPriority: priorityMetadata.dominantPriority || "",
      hasPriorityTimeAnchor,
      isStartOfDayPreload: false,
      isPreloadedPreviousDay,
      // Onglet Route : le Chargé PM de plusieurs commandes y est numéroté comme dans le Gantt (« 28-30 »).
      ...(chipStop?.is_pm_operational_loading ? {
        isPmOperationalLoading: true,
        endOfDayLoadOrderIds: chipStop.order_ids || [chipStop.order_id].filter(Boolean),
      } : {}),
      loading_client_name: typeof chipStop?.loading_client_name === "string" ? chipStop.loading_client_name.trim() : "",
      meunerie_loading_extra_minutes: destinationType === "loading" && !isPreloadedPreviousDay
        ? Math.max(0, Number(chipStop?.meunerie_loading_extra_minutes) || 0)
        : 0,
      aspirator_service_minutes: destinationType === "delivery"
        ? Math.max(0, Math.round(Number(chipStop?.aspirator_service_minutes) || 0))
        : 0,
      aspiratorServiceMinutes: destinationType === "delivery"
        ? Math.max(0, Math.round(Number(chipStop?.aspirator_service_minutes) || 0))
        : 0,
      delivery_fixed_minutes: destinationType === "delivery"
        ? Math.max(0, Math.round(Number(chipStop?.delivery_fixed_minutes) || 0))
        : 0,
      loading_fixed_minutes: destinationType === "loading" && !isPreloadedPreviousDay
        ? Math.max(0, Math.round(Number(chipStop?.loading_fixed_minutes) || 0))
        : 0,
      client_loading_opening_time: destinationType === "loading" && !isPreloadedPreviousDay && typeof chipStop?.client_loading_opening_time === "string"
        ? chipStop.client_loading_opening_time
        : null,
      client_loading_closing_time: destinationType === "loading" && !isPreloadedPreviousDay && typeof chipStop?.client_loading_closing_time === "string"
        ? chipStop.client_loading_closing_time
        : null,
      mapAnchorStopKey: buildRouteMapStopKey(chipStop || toStop || fromStop, { fallbackIndex: globalSequence }),
      requiresAspirator: operationIdentity.requiresAspirator,
      requiresOntario: operationIdentity.requiresOntario,
      ...resolveRouteSegmentCompartmentFill(trip, chipStop, compartmentFillContext),
      productLabel: productChip?.label || "",
      sameSiteGroupContainsMixedStopTypes: Boolean(visualLayout?.sameSiteGroupContainsMixedStopTypes),
      sameSiteGroupIndex: Number.isInteger(visualLayout?.sameSiteGroupIndex) ? visualLayout.sameSiteGroupIndex : 0,
      sameSiteGroupKey,
      sameSiteGroupSize: Number.isInteger(visualLayout?.sameSiteGroupSize) ? visualLayout.sameSiteGroupSize : 1,
      sameSiteGroupStartSequence,
      segmentColor: getStopDisplayColor(chipStop || toStop || fromStop || null),
      segmentStartColor: getStopDisplayColor(fromStop || toStop || null),
      segmentEndColor: getStopDisplayColor(toStop || fromStop || null),
      serviceDurationLabel: hasExplicitStopServiceDuration(chipStop)
        ? formatDisplayedServiceDurationLabel(serviceDurationMinutes)
        : null,
      serviceDurationMinutes,
      stopType: destinationType,
      from: fromStop
        ? {
            address: getRouteStopAddressLabel(fromStop.address),
            label: getRouteStopLabel(fromStop),
            city: getAddressCityLabel(fromStop.address),
            showHomeMarker: fromStop.stop_type === "depot_start",
          }
        : null,
      to: toStop
        ? {
            address: getRouteStopAddressLabel(toStop.address),
            stopType: destinationType,
            typeLabel: getStopTypeLabel(toStop),
            orderLabel: toStop.order_id || "",
            badgeTitle: getRouteStopBadgeTooltip(destinationType),
            iconTitle: getRouteStopIconTooltip(destinationType),
            label: getRouteStopLabel(toStop),
            city: getAddressCityLabel(toStop.address),
          }
        : null,
      routeFromAddress: getRouteStopAddressLabel(routeFromStop?.address),
      routeFromLabel: getRouteStopLabel(routeFromStop),
      routeToAddress: getRouteStopAddressLabel(routeToStop?.address),
      routeToLabel: getRouteStopLabel(routeToStop),
      routeDistanceSource: displayedRouteSegment?.distanceSource || "unavailable",
      routeDistanceSourceLabel: displayedRouteSegment?.distanceSourceLabel || null,
      routeEstimatedTravelDurationMinutes: normalizeTimelineDurationMinutes(displayedRouteSegment?.estimatedDurationMinutes),
      routeScheduledTravelDurationMinutes: normalizeTimelineDurationMinutes(displayedRouteSegment?.scheduledDurationMinutes),
      routeSpeedKmh: typeof displayedRouteSegment?.speedKmh === "number" ? displayedRouteSegment.speedKmh : null,
      scheduledAt,
      scheduledAtMinutes,
      toAddress: getRouteStopAddressLabel(toStop?.address),
      toLabel: getRouteStopLabel(toStop),
      displayTravelDurationMinutes,
      travelDurationMinutes,
      waitMinutes,
      tripId,
      typeLabel: getStopTypeLabel(chipStop || toStop || fromStop),
      quantityTons: typeof chipStop?.quantity_tons === "number" ? chipStop.quantity_tons : null,
      compartmentAllocations: getStopCompartmentAllocations(chipStop),
      chips: buildRouteSegmentChips(displayedRouteSegment, chipStop, trip, { serviceMinutesOverride }),
    };
  });
  const items = rebalanceTerminalReturnRouteSequenceItems([
    ...initialPreloadItems,
    initialDepotTravelSegment
      ? buildInitialDepotTravelRouteSequenceItem(initialDepotTravelSegment, {
          assignmentId,
          globalSequence: initialPreloadCount + 1,
          trip,
          tripId,
        })
      : null,
    ...operationItems,
  ].filter(Boolean));
  const rawTimelineSegments = buildTripTimelineSegments(trip, assignment, { state });
  const useBackendDisplayProjection = usesBackendTimelineDisplayProjection(state);
  const preserveBackendDisplayProjection = hasCompleteBackendTimelineDisplayProjection(
    assignment,
    state,
  );
  const anchoredTimelineSegments = resolveAnchoredSegmentTimeline(
    rawTimelineSegments,
    resolveRowTimelineStartMinutes(
      assignment,
      rawTimelineSegments,
      useBackendDisplayProjection,
    ),
    { preserveAbsoluteTimelinePositions: preserveBackendDisplayProjection },
  );
  const timelineSegmentsBySequence = new Map(
    anchoredTimelineSegments
      .filter((segment) => Number.isInteger(Number(segment?.globalSequence)))
      .map((segment) => [Number(segment.globalSequence), segment]),
  );
  const itemsWithTimelineAnchors = items.map((item) => {
    const timelineSegment = timelineSegmentsBySequence.get(Number(item?.globalSequence));

    if (!timelineSegment) {
      return item;
    }

    return {
      ...item,
      arrivalAt: timelineSegment.arrivalAt ?? item.arrivalAt ?? null,
      arrivalAtMinutes: timelineSegment.arrivalAtMinutes ?? item.arrivalAtMinutes ?? null,
      hasManualMixBoundaryBefore: timelineSegment.hasManualMixBoundaryBefore === true,
      manualMixBlockIndex: Number.isInteger(Number(timelineSegment.manualMixBlockIndex))
        ? Number(timelineSegment.manualMixBlockIndex)
        : (Number.isInteger(Number(item?.manualMixBlockIndex)) ? Number(item.manualMixBlockIndex) : null),
      manualMixBoundaryTitle: timelineSegment.manualMixBoundaryTitle ?? item.manualMixBoundaryTitle ?? null,
      manualMixCurrentProductLabel: timelineSegment.manualMixCurrentProductLabel ?? item.manualMixCurrentProductLabel ?? null,
      manualMixPreviousProductLabel: timelineSegment.manualMixPreviousProductLabel ?? item.manualMixPreviousProductLabel ?? null,
      scheduledAt: timelineSegment.scheduledAt ?? item.scheduledAt ?? null,
      scheduledAtMinutes: timelineSegment.scheduledAtMinutes ?? item.scheduledAtMinutes ?? null,
      segmentStartAt: timelineSegment.segmentStartAt ?? item.segmentStartAt ?? null,
      segmentStartMinutes: timelineSegment.segmentStartMinutes ?? item.segmentStartMinutes ?? null,
      segmentEndAt: timelineSegment.segmentEndAt ?? item.segmentEndAt ?? null,
      segmentEndMinutes: timelineSegment.segmentEndMinutes ?? item.segmentEndMinutes ?? null,
      visualSegmentStartAt: timelineSegment.visualSegmentStartAt ?? item.visualSegmentStartAt ?? null,
      visualSegmentStartMinutes: timelineSegment.visualSegmentStartMinutes ?? item.visualSegmentStartMinutes ?? null,
      visualSegmentEndAt: timelineSegment.visualSegmentEndAt ?? item.visualSegmentEndAt ?? null,
      visualSegmentEndMinutes: timelineSegment.visualSegmentEndMinutes ?? item.visualSegmentEndMinutes ?? null,
      waitMinutes: timelineSegment.waitMinutes ?? item.waitMinutes ?? null,
    };
  });
  const segmentTotalDurationMinutes = itemsWithTimelineAnchors.reduce((total, item) => {
    const displayTravelDurationMinutes = normalizeTimelineDurationMinutes(item?.displayTravelDurationMinutes);
    const travelDurationMinutes = normalizeTimelineDurationMinutes(item?.travelDurationMinutes);
    const serviceDurationMinutes = normalizeTimelineDurationMinutes(item?.serviceDurationMinutes) ?? 0;

    return total + (displayTravelDurationMinutes ?? travelDurationMinutes ?? 0) + serviceDurationMinutes;
  }, 0);
  const visibleItemsWithDisplaySequence = resolveVisibleRouteSequenceItems(itemsWithTimelineAnchors);
  const backendDisplayedTotalDurationMinutes = [
    trip?.route?.estimated_duration_minutes,
    trip?.estimated_duration_minutes,
  ].map((candidate) => normalizeTimelineDurationMinutes(candidate)).find((candidate) => candidate != null);
  const displayedTotalDurationMinutes = backendDisplayedTotalDurationMinutes ?? segmentTotalDurationMinutes;
  const totalCompartments = Math.max(Number(assignment?.total_compartments) || 0, 0);

  return {
    countLabel: visibleItemsWithDisplaySequence.length ? `${visibleItemsWithDisplaySequence.length} route(s)` : "Aucune route",
    dispatchDate: typeof state?.resultsData?.dispatch_date === "string" && state.resultsData.dispatch_date.trim()
      ? state.resultsData.dispatch_date.trim()
      : "",
    displayedTotalDurationLabel: formatDurationShort(displayedTotalDurationMinutes) || "0 min",
    displayedTotalDurationMinutes,
    driverName: typeof assignment?.driver_name === "string" ? assignment.driver_name.trim() : "",
    truckName: typeof assignment?.truck_name === "string" ? assignment.truck_name.trim() : "",
    showEmptyState: visibleItemsWithDisplaySequence.length === 0,
    emptyTitle: "Aucune tournée sélectionnée",
    emptyMessage: "Choisissez un camion puis une tournée pour afficher les routes successives.",
    items: visibleItemsWithDisplaySequence,
    totalCompartments,
  };
}

function normalizeTimelineDurationMinutes(value) {
  const durationMinutes = Number(value);
  return Number.isFinite(durationMinutes) && durationMinutes >= 0 ? durationMinutes : null;
}

const DEFAULT_TIMELINE_START_MINUTES = 7 * 60;

function usesBackendTimelineDisplayProjection(state = null) {
  const payload = state?.resultsData && typeof state.resultsData === "object"
    ? state.resultsData
    : state;
  return payload?.display_projection_version === 1;
}

function hasCompleteBackendTimelineDisplayProjection(assignment = null, state = null) {
  if (!usesBackendTimelineDisplayProjection(state)) {
    return false;
  }

  const displayStartMinutes = normalizeTimelineClockAnchorMinutes(assignment?.display_start_minutes);
  const displayEndMinutes = normalizeTimelineClockAnchorMinutes(assignment?.display_end_minutes);
  return displayStartMinutes != null
    && displayEndMinutes != null
    && displayEndMinutes >= displayStartMinutes;
}

function resolveTimelineFieldText(sources = [], fieldNames = [], useBackendDisplayProjection = false) {
  const resolvedSources = Array.isArray(sources) ? sources.filter(Boolean) : [];
  const resolvedFieldNames = Array.isArray(fieldNames) ? fieldNames.filter(Boolean) : [];

  if (useBackendDisplayProjection) {
    for (const fieldName of resolvedFieldNames) {
      for (const source of resolvedSources) {
        const candidate = source?.[`display_${fieldName}_at`];
        if (typeof candidate === "string" && candidate.trim()) {
          return candidate.trim();
        }
      }
    }
  }

  for (const fieldName of resolvedFieldNames) {
    for (const source of resolvedSources) {
      const candidate = source?.[`${fieldName}_at`];
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }
  }

  return null;
}

function resolveTimelineFieldMinutes(sources = [], fieldNames = [], useBackendDisplayProjection = false) {
  const resolvedSources = Array.isArray(sources) ? sources.filter(Boolean) : [];
  const resolvedFieldNames = Array.isArray(fieldNames) ? fieldNames.filter(Boolean) : [];

  if (useBackendDisplayProjection) {
    for (const fieldName of resolvedFieldNames) {
      for (const source of resolvedSources) {
        const rawCandidate = source?.[`display_${fieldName}_minutes`];
        if (rawCandidate == null || rawCandidate === "") {
          continue;
        }
        const candidate = Number(rawCandidate);
        if (Number.isFinite(candidate) && candidate >= 0) {
          return candidate;
        }
      }
    }
  }

  return parseTimelineClockMinutes(resolveTimelineFieldText(
    resolvedSources,
    resolvedFieldNames,
    false,
  ));
}

function parseTimelineClockMinutes(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (value == null) {
    return null;
  }

  const text = String(value).trim();
  if (!text) {
    return null;
  }

  const hourOnlyMatch = text.match(/^(\d{1,2})$/);
  if (hourOnlyMatch) {
    const hours = Number(hourOnlyMatch[1]);
    if (Number.isInteger(hours) && hours >= 0 && hours < 24) {
      return hours * 60;
    }
  }

  const directClockMatch = text.match(/(?:^|[T\s])(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (directClockMatch) {
    const hours = Number(directClockMatch[1]);
    const minutes = Number(directClockMatch[2]);
    if (Number.isInteger(hours) && Number.isInteger(minutes) && hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
      return (hours * 60) + minutes;
    }
  }

  const parsedDate = new Date(text);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return (parsedDate.getHours() * 60) + parsedDate.getMinutes();
}

function parseTimelineTimestampDate(value) {
  if (typeof value !== "string") {
    return null;
  }

  const text = value.trim();
  if (!text) {
    return null;
  }

  const parsedDate = new Date(text);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function formatTimelineTimestampValue(value) {
  const parsedDate = value instanceof Date ? value : null;
  if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  const hours = String(parsedDate.getHours()).padStart(2, "0");
  const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
  const seconds = String(parsedDate.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function normalizeTimelineTimestamp(value) {
  const parsedDate = parseTimelineTimestampDate(value);
  return parsedDate ? formatTimelineTimestampValue(parsedDate) : null;
}

function shiftTimelineTimestamp(value, minutesDelta = 0) {
  const parsedDate = parseTimelineTimestampDate(value);
  const normalizedMinutesDelta = Number(minutesDelta);

  if (!parsedDate || !Number.isFinite(normalizedMinutesDelta)) {
    return null;
  }

  parsedDate.setTime(parsedDate.getTime() + (normalizedMinutesDelta * 60 * 1000));
  return formatTimelineTimestampValue(parsedDate);
}

function shiftTimelineClockMinutesValue(value, minutesDelta = 0) {
  const normalizedValue = Number(value);
  const normalizedMinutesDelta = Number(minutesDelta);

  if (!Number.isFinite(normalizedValue)) {
    return null;
  }

  if (!Number.isFinite(normalizedMinutesDelta) || normalizedMinutesDelta === 0) {
    return normalizedValue;
  }

  return normalizedValue + normalizedMinutesDelta;
}

function shiftTimelineTextValue(value, minutesDelta = 0) {
  const normalizedValue = typeof value === "string" ? value.trim() : "";
  const normalizedMinutesDelta = Number(minutesDelta);

  if (!normalizedValue) {
    return null;
  }

  if (!Number.isFinite(normalizedMinutesDelta) || normalizedMinutesDelta === 0) {
    return normalizedValue;
  }

  return shiftTimelineTimestamp(normalizedValue, normalizedMinutesDelta);
}

function applyManualBufferTimelineOffsets(segments = []) {
  const resolvedSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];
  if (!resolvedSegments.length) {
    return [];
  }

  let accumulatedBufferMinutes = 0;

  return resolvedSegments.map((segment) => {
    const manualBufferMinutes = normalizeTimelineDurationMinutes(segment?.manualBufferMinutes) || 0;
    const baseTimelineDurationMinutes = normalizeTimelineDurationMinutes(segment?.baseTimelineDurationMinutes)
      ?? normalizeTimelineDurationMinutes(segment?.timelineDurationMinutes)
      ?? 0;
    const totalTimelineDurationMinutes = baseTimelineDurationMinutes + manualBufferMinutes;
    const rawVisualTimelineDurationMinutes = normalizeTimelineDurationMinutes(segment?.visualTimelineDurationMinutes);
    const visualTimelineDurationMinutes = (rawVisualTimelineDurationMinutes != null && rawVisualTimelineDurationMinutes > 0)
      ? rawVisualTimelineDurationMinutes
      : totalTimelineDurationMinutes;
    const shiftedSegment = {
      ...segment,
      arrivalAt: shiftTimelineTextValue(segment?.arrivalAt, accumulatedBufferMinutes),
      arrivalAtMinutes: shiftTimelineClockMinutesValue(segment?.arrivalAtMinutes, accumulatedBufferMinutes),
      baseTimelineDurationLabel: formatDurationShort(baseTimelineDurationMinutes) || "0 min",
      baseTimelineDurationMinutes,
      manualBufferLabel: manualBufferMinutes > 0
        ? `+${formatDurationShort(manualBufferMinutes) || `${manualBufferMinutes} min`}`
        : null,
      manualBufferMinutes,
      scheduledAt: shiftTimelineTextValue(segment?.scheduledAt, accumulatedBufferMinutes),
      scheduledAtMinutes: shiftTimelineClockMinutesValue(segment?.scheduledAtMinutes, accumulatedBufferMinutes),
      segmentEndAt: shiftTimelineTextValue(segment?.segmentEndAt, accumulatedBufferMinutes),
      segmentEndMinutes: shiftTimelineClockMinutesValue(segment?.segmentEndMinutes, accumulatedBufferMinutes),
      segmentStartAt: shiftTimelineTextValue(segment?.segmentStartAt, accumulatedBufferMinutes),
      segmentStartMinutes: shiftTimelineClockMinutesValue(segment?.segmentStartMinutes, accumulatedBufferMinutes),
      timelineDurationLabel: formatDurationShort(totalTimelineDurationMinutes) || "0 min",
      timelineDurationMinutes: totalTimelineDurationMinutes,
      visualSegmentEndAt: shiftTimelineTextValue(segment?.visualSegmentEndAt, accumulatedBufferMinutes),
      visualSegmentEndMinutes: shiftTimelineClockMinutesValue(segment?.visualSegmentEndMinutes, accumulatedBufferMinutes),
      visualSegmentStartAt: shiftTimelineTextValue(segment?.visualSegmentStartAt, accumulatedBufferMinutes),
      visualSegmentStartMinutes: shiftTimelineClockMinutesValue(segment?.visualSegmentStartMinutes, accumulatedBufferMinutes),
      visualTimelineDurationMinutes,
    };

    accumulatedBufferMinutes += manualBufferMinutes;
    return shiftedSegment;
  });
}

function resolveTimelineSegmentScheduledAt(stop, step, useBackendDisplayProjection = false) {
  return resolveTimelineFieldText(
    [stop, step],
    useBackendDisplayProjection ? ["scheduled", "start"] : ["scheduled"],
    useBackendDisplayProjection,
  );
}

function resolveTimelineSegmentScheduledAtMinutes(stop, step, useBackendDisplayProjection = false) {
  return resolveTimelineFieldMinutes(
    [stop, step],
    useBackendDisplayProjection ? ["scheduled", "start"] : ["scheduled"],
    useBackendDisplayProjection,
  );
}

function resolveTimelinePriorityTextValue(...candidates) {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return null;
}

function resolveTimelineSegmentPriorityMetadata(stop, step, useBackendDisplayProjection = false) {
  const arrivalAt = resolveTimelineFieldText(
    [stop, step],
    useBackendDisplayProjection ? ["arrival", "start"] : ["arrival"],
    useBackendDisplayProjection,
  );
  const dominantPriority = resolveTimelinePriorityTextValue(stop?.dominant_priority, step?.dominant_priority);
  const waitCandidates = [stop?.wait_minutes, step?.wait_minutes];
  let waitMinutes = null;

  for (const candidate of waitCandidates) {
    const parsedCandidate = Number(candidate);
    if (Number.isFinite(parsedCandidate) && parsedCandidate >= 0) {
      waitMinutes = parsedCandidate;
      break;
    }
  }

  return {
    arrivalAt,
    arrivalAtMinutes: resolveTimelineFieldMinutes(
      [stop, step],
      useBackendDisplayProjection ? ["arrival", "start"] : ["arrival"],
      useBackendDisplayProjection,
    ),
    dominantPriority,
    waitMinutes,
  };
}

function resolveTimelineSegmentPreloadedPreviousDay(stop, step) {
  return Boolean(stop?.is_preloaded_previous_day || step?.is_preloaded_previous_day);
}

function resolveTimelineSegmentStartMinutesFromSchedule(scheduledAtMinutes, travelDurationMinutes) {
  return scheduledAtMinutes != null
    ? Math.max(scheduledAtMinutes - travelDurationMinutes, 0)
    : null;
}

function clampTimelineSegmentEndMinutes(segmentStartMinutes, scheduledAtMinutes, rawSegmentEndMinutes, nextSegmentStartMinutes) {
  if (segmentStartMinutes == null || rawSegmentEndMinutes == null) {
    return rawSegmentEndMinutes;
  }

  return Math.max(rawSegmentEndMinutes, segmentStartMinutes);
}

function resolveAssignmentTimelineStartMinutes(assignment, useBackendDisplayProjection = false) {
  if (useBackendDisplayProjection) {
    const displayStartMinutes = normalizeTimelineClockAnchorMinutes(assignment?.display_start_minutes);
    if (displayStartMinutes != null) {
      return displayStartMinutes;
    }
  }

  const explicitStartMinutes = parseTimelineClockMinutes(
    assignment?.runtime_start_time ?? assignment?.start_time,
  );
  return explicitStartMinutes != null ? explicitStartMinutes : DEFAULT_TIMELINE_START_MINUTES;
}

function resolveRowTimelineStartMinutes(assignment, segments = [], useBackendDisplayProjection = false) {
  const assignmentStartMinutes = resolveAssignmentTimelineStartMinutes(
    assignment,
    useBackendDisplayProjection,
  );
  const visibleStartOverrideMinutes = (Array.isArray(segments) ? segments : [])
    .map((segment) => normalizeTimelineClockAnchorMinutes(segment?.timelineStartOverrideMinutes))
    .filter((value) => value != null);

  return visibleStartOverrideMinutes.length
    ? Math.max(assignmentStartMinutes, ...visibleStartOverrideMinutes)
    : assignmentStartMinutes;
}

function resolveRowTimelineEndMinutes(rowStartMinutes, segments = [], rowDurationMinutes = null) {
  const durationDrivenTerminalEndMinutes = resolveDurationDrivenTerminalRouteDisplayEndMinutes(segments);
  if (durationDrivenTerminalEndMinutes != null) {
    return Math.max(rowStartMinutes, durationDrivenTerminalEndMinutes);
  }

  const scheduledSegmentEnds = (Array.isArray(segments) ? segments : [])
    .filter((segment) => segment && segment.isEndOfDayLoad !== true && segment.isEndOfDayLoadSourcePreload !== true)
    .map((segment) => {
      if (segment?.isTerminalRouteMarker === true) {
        const displayedSegmentEndMinutes = Number(segment?.displayedSegmentEndMinutes);
        if (Number.isFinite(displayedSegmentEndMinutes)) {
          return displayedSegmentEndMinutes;
        }
      }

      const visualSegmentEndMinutes = Number(segment?.visualSegmentEndMinutes);
      if (Number.isFinite(visualSegmentEndMinutes)) {
        return visualSegmentEndMinutes;
      }

      return segment?.segmentEndMinutes;
    })
    .filter((value) => typeof value === "number" && Number.isFinite(value));

  if (scheduledSegmentEnds.length) {
    return Math.max(rowStartMinutes, ...scheduledSegmentEnds);
  }

  const fallbackDurationMinutes = normalizeTimelineDurationMinutes(rowDurationMinutes);
  if (fallbackDurationMinutes != null) {
    return rowStartMinutes + fallbackDurationMinutes;
  }

  return rowStartMinutes;
}

function normalizeTimelineClockAnchorMinutes(value) {
  if (value == null) {
    return null;
  }

  const minutes = Number(value);
  return Number.isFinite(minutes) && minutes >= 0 ? minutes : null;
}

function resolveDurationDrivenTimelineGeometryMinutes(segment = null) {
  if (!segment || segment.usesDurationDrivenTimelineGeometry !== true) {
    return null;
  }

  const displayTravelDurationMinutes = normalizeTimelineDurationMinutes(segment.displayTravelDurationMinutes);
  const travelDurationMinutes = normalizeTimelineDurationMinutes(segment.travelDurationMinutes);
  const serviceDurationMinutes = normalizeTimelineDurationMinutes(segment.serviceDurationMinutes) || 0;
  const manualBufferMinutes = normalizeTimelineDurationMinutes(segment.manualBufferMinutes) || 0;
  const drivingDurationMinutes = displayTravelDurationMinutes ?? travelDurationMinutes;

  if (drivingDurationMinutes == null && serviceDurationMinutes <= 0 && manualBufferMinutes <= 0) {
    return null;
  }

  return (drivingDurationMinutes ?? 0) + serviceDurationMinutes + manualBufferMinutes;
}

export function resolveAnchoredSegmentTimeline(
  segments = [],
  rowStartMinutes = null,
  { preserveAbsoluteTimelinePositions = false } = {},
) {
  const resolvedRowStartMinutes = normalizeTimelineClockAnchorMinutes(rowStartMinutes);
  let cursorMinutes = resolvedRowStartMinutes;
  let visualCursorMinutes = resolvedRowStartMinutes;

  return (Array.isArray(segments) ? segments : []).map((segment) => {
    if (segment?.isEndOfDayLoad === true || segment?.isEndOfDayLoadSourcePreload === true) {
      const endOfDayVisualDurationMinutes = normalizeTimelineDurationMinutes(segment?.visualTimelineDurationMinutes);
      if (endOfDayVisualDurationMinutes == null || endOfDayVisualDurationMinutes <= 0) {
        return {
          ...segment,
          displayedSegmentEndAt: null,
          displayedSegmentEndMinutes: null,
          displayedSegmentStartAt: null,
          displayedSegmentStartMinutes: null,
          segmentEndAt: null,
          segmentEndMinutes: null,
          segmentStartAt: null,
          segmentStartMinutes: null,
          visualSegmentEndAt: null,
          visualSegmentEndMinutes: null,
          visualSegmentStartAt: null,
          visualSegmentStartMinutes: null,
        };
      }
    }

    const durationDrivenTimelineGeometryMinutes = resolveDurationDrivenTimelineGeometryMinutes(segment);
    const segmentDurationMinutes = durationDrivenTimelineGeometryMinutes
      ?? normalizeTimelineDurationMinutes(segment?.timelineDurationMinutes)
      ?? 0;
    const usesDurationDrivenTimelineGeometry = durationDrivenTimelineGeometryMinutes != null;
    const hasExplicitZeroTimelineDuration = segmentDurationMinutes === 0;
    const visualTimelineDurationMinutes = normalizeTimelineDurationMinutes(segment?.visualTimelineDurationMinutes);
    const hasPositiveExplicitVisualDuration = visualTimelineDurationMinutes != null && visualTimelineDurationMinutes > 0;
    const isVisualOnlyEndOfDayAnchor = (segment?.isEndOfDayLoad === true || segment?.isEndOfDayLoadSourcePreload === true)
      && hasPositiveExplicitVisualDuration;
    const travelDurationMinutes = normalizeTimelineDurationMinutes(segment?.travelDurationMinutes);
    const serviceDurationMinutes = normalizeTimelineDurationMinutes(segment?.serviceDurationMinutes);
    const anchoredVisualDurationMinutes = Boolean(segment?.hasPriorityTimeAnchor)
      && (travelDurationMinutes != null || serviceDurationMinutes != null)
      ? (travelDurationMinutes || 0) + (serviceDurationMinutes || 0)
      : null;
    let segmentStartMinutes = normalizeTimelineClockAnchorMinutes(segment?.segmentStartMinutes);
    let segmentEndMinutes = normalizeTimelineClockAnchorMinutes(segment?.segmentEndMinutes);
    let segmentStartAt = normalizeTimelineTimestamp(segment?.segmentStartAt);
    let segmentEndAt = normalizeTimelineTimestamp(segment?.segmentEndAt);
    let visualSegmentStartMinutes = normalizeTimelineClockAnchorMinutes(segment?.visualSegmentStartMinutes);
    let visualSegmentEndMinutes = normalizeTimelineClockAnchorMinutes(segment?.visualSegmentEndMinutes);
    let visualSegmentStartAt = normalizeTimelineTimestamp(segment?.visualSegmentStartAt);
    let visualSegmentEndAt = normalizeTimelineTimestamp(segment?.visualSegmentEndAt);
    let didNormalizeTimelineGeometry = false;

    if (segmentStartMinutes == null && segmentEndMinutes != null && segmentDurationMinutes > 0) {
      segmentStartMinutes = Math.max(segmentEndMinutes - segmentDurationMinutes, 0);

      if (segmentStartAt == null && segmentEndAt) {
        segmentStartAt = shiftTimelineTimestamp(segmentEndAt, -segmentDurationMinutes);
      }
    }

    if (segmentStartMinutes == null) {
      segmentStartMinutes = cursorMinutes;
    }

    if (!preserveAbsoluteTimelinePositions && !isVisualOnlyEndOfDayAnchor && cursorMinutes != null && segmentStartMinutes != null && segmentStartMinutes < cursorMinutes) {
      const overlapShiftMinutes = cursorMinutes - segmentStartMinutes;

      didNormalizeTimelineGeometry = didNormalizeTimelineGeometry || overlapShiftMinutes > 0;
      segmentStartMinutes = cursorMinutes;

      if (segmentEndMinutes != null) {
        segmentEndMinutes += overlapShiftMinutes;
      }

      segmentStartAt = shiftTimelineTimestamp(segmentStartAt, overlapShiftMinutes) ?? segmentStartAt;
      segmentEndAt = shiftTimelineTimestamp(segmentEndAt, overlapShiftMinutes) ?? segmentEndAt;
    }

    if (hasExplicitZeroTimelineDuration && segmentStartMinutes != null && segmentEndMinutes !== segmentStartMinutes) {
      didNormalizeTimelineGeometry = true;
      segmentEndMinutes = segmentStartMinutes;
      segmentEndAt = segmentStartAt ?? segmentEndAt;
    }

    if (usesDurationDrivenTimelineGeometry && segmentStartMinutes != null) {
      const durationDrivenSegmentEndMinutes = segmentStartMinutes + segmentDurationMinutes;
      didNormalizeTimelineGeometry = didNormalizeTimelineGeometry
        || segmentEndMinutes == null
        || Math.abs(segmentEndMinutes - durationDrivenSegmentEndMinutes) > 1e-9;
      segmentEndMinutes = durationDrivenSegmentEndMinutes;
      segmentEndAt = shiftTimelineTimestamp(segmentStartAt, segmentDurationMinutes) ?? null;
    } else if ((segmentEndMinutes == null || segmentEndMinutes < segmentStartMinutes) && segmentStartMinutes != null) {
      didNormalizeTimelineGeometry = true;
      segmentEndMinutes = segmentStartMinutes + segmentDurationMinutes;
    }

    const normalizedTimelineDurationMinutes = segmentStartMinutes != null && segmentEndMinutes != null
      ? Math.max(segmentEndMinutes - segmentStartMinutes, 0)
      : segmentDurationMinutes;
    const normalizedVisualDurationMinutes = anchoredVisualDurationMinutes
      ?? (
        isVisualOnlyEndOfDayAnchor
          ? visualTimelineDurationMinutes
          : usesDurationDrivenTimelineGeometry
            ? segmentDurationMinutes
            : normalizedTimelineDurationMinutes
      );

    if (visualSegmentStartMinutes == null && visualSegmentEndMinutes != null && normalizedVisualDurationMinutes > 0) {
      visualSegmentStartMinutes = Math.max(visualSegmentEndMinutes - normalizedVisualDurationMinutes, 0);

      if (visualSegmentStartAt == null && visualSegmentEndAt) {
        visualSegmentStartAt = shiftTimelineTimestamp(visualSegmentEndAt, -normalizedVisualDurationMinutes);
      }
    }

    if (visualSegmentStartMinutes == null) {
      visualSegmentStartMinutes = segmentStartMinutes ?? visualCursorMinutes;
    }

    if (!preserveAbsoluteTimelinePositions && !isVisualOnlyEndOfDayAnchor && visualCursorMinutes != null && visualSegmentStartMinutes != null && visualSegmentStartMinutes < visualCursorMinutes) {
      const visualOverlapShiftMinutes = visualCursorMinutes - visualSegmentStartMinutes;
      visualSegmentStartMinutes = visualCursorMinutes;
      visualSegmentStartAt = shiftTimelineTimestamp(visualSegmentStartAt, visualOverlapShiftMinutes) ?? visualSegmentStartAt;
      visualSegmentEndAt = shiftTimelineTimestamp(visualSegmentEndAt, visualOverlapShiftMinutes) ?? visualSegmentEndAt;
    }

    if (hasExplicitZeroTimelineDuration && !isVisualOnlyEndOfDayAnchor && visualSegmentStartMinutes != null) {
      visualSegmentEndMinutes = visualSegmentStartMinutes;
      visualSegmentEndAt = visualSegmentStartAt ?? visualSegmentEndAt;
    }

    if (visualSegmentStartMinutes != null) {
      visualSegmentEndMinutes = visualSegmentStartMinutes + normalizedVisualDurationMinutes;
    }

    const nextCursorMinutes = segmentEndMinutes
      ?? (segmentStartMinutes != null ? segmentStartMinutes + normalizedTimelineDurationMinutes : null);

    const nextVisualCursorMinutes = visualSegmentEndMinutes
      ?? (visualSegmentStartMinutes != null ? visualSegmentStartMinutes + normalizedVisualDurationMinutes : null);

    if (segmentStartAt == null && segmentEndAt != null && normalizedTimelineDurationMinutes > 0) {
      segmentStartAt = shiftTimelineTimestamp(segmentEndAt, -normalizedTimelineDurationMinutes);
    }

    if (segmentEndAt == null && segmentStartAt != null) {
      segmentEndAt = shiftTimelineTimestamp(segmentStartAt, normalizedTimelineDurationMinutes);
    }

    if (visualSegmentStartAt == null && visualSegmentEndAt != null && normalizedVisualDurationMinutes > 0) {
      visualSegmentStartAt = shiftTimelineTimestamp(visualSegmentEndAt, -normalizedVisualDurationMinutes);
    }

    if (visualSegmentStartAt != null) {
      visualSegmentEndAt = shiftTimelineTimestamp(visualSegmentStartAt, normalizedVisualDurationMinutes);
    } else if (visualSegmentEndAt == null && visualSegmentStartMinutes != null) {
      visualSegmentEndAt = shiftTimelineTimestamp(visualSegmentStartAt ?? segmentStartAt, normalizedVisualDurationMinutes) ?? visualSegmentEndAt;
    }

    const existingTerminalMergedStopEndMinutes = normalizeTimelineClockAnchorMinutes(segment?.terminalMergedStopEndMinutes);
    const terminalMergedStopEndCandidates = segment?.isTerminalRouteMerged === true
      ? [existingTerminalMergedStopEndMinutes, visualSegmentEndMinutes, segmentEndMinutes].filter((value) => value != null)
      : [];
    const terminalMergedStopEndMinutes = terminalMergedStopEndCandidates.length
      ? Math.max(...terminalMergedStopEndCandidates)
      : segment?.terminalMergedStopEndMinutes;
    const terminalMergedStopEndAt = segment?.isTerminalRouteMerged === true
      ? (
          terminalMergedStopEndMinutes !== existingTerminalMergedStopEndMinutes
            ? (visualSegmentEndAt || segmentEndAt || segment?.terminalMergedStopEndAt || null)
            : segment?.terminalMergedStopEndAt
        )
      : segment?.terminalMergedStopEndAt;

    if (nextCursorMinutes != null) {
      cursorMinutes = cursorMinutes == null
        ? nextCursorMinutes
        : Math.max(cursorMinutes, nextCursorMinutes);
    }

    if (nextVisualCursorMinutes != null) {
      visualCursorMinutes = visualCursorMinutes == null
        ? nextVisualCursorMinutes
        : Math.max(visualCursorMinutes, nextVisualCursorMinutes);
    }

    return {
      ...segment,
      segmentEndAt,
      segmentEndMinutes,
      segmentStartAt,
      segmentStartMinutes,
      ...(segment?.isTerminalRouteMerged === true ? {
        terminalMergedStopEndAt,
        terminalMergedStopEndMinutes,
      } : {}),
      visualSegmentEndAt,
      visualSegmentEndMinutes,
      visualSegmentStartAt,
      visualSegmentStartMinutes,
      visualTimelineDurationMinutes: anchoredVisualDurationMinutes != null
        ? normalizedVisualDurationMinutes
        : didNormalizeTimelineGeometry
          && !segment?.isSameSiteSequenceGroup
          && normalizedVisualDurationMinutes > 0
          ? normalizedVisualDurationMinutes
          : visualTimelineDurationMinutes,
    };
  });
}

function resolveAssignmentServiceRateTonsPerHour(assignment, stopType) {
  const rawRate = stopType === "loading"
    ? assignment?.loading_rate_tons_per_hour ?? assignment?.loading_time_minutes
    : stopType === "delivery"
      ? assignment?.unloading_rate_tons_per_hour ?? assignment?.unloading_time_minutes
      : null;
  const tonsPerHour = Number(rawRate);

  return Number.isFinite(tonsPerHour) && tonsPerHour > 0 ? tonsPerHour : null;
}

// Temps fixes par livraison et par chargement (Paramètres système) : une fois par visite, comme le backend.
function resolveAssignmentFixedMinutes(assignment, stopType) {
  const minutes = stopType === "loading" ? assignment?.loading_fixed_minutes : assignment?.delivery_fixed_minutes;
  return Math.max(0, Math.round(Number(minutes) || 0));
}

function resolveServiceMinutesForQuantityTons(quantityTons, tonsPerHour) {
  const normalizedQuantityTons = Number(quantityTons);
  const normalizedTonsPerHour = Number(tonsPerHour);

  if (!Number.isFinite(normalizedQuantityTons) || normalizedQuantityTons <= 0) {
    return 0;
  }

  if (!Number.isFinite(normalizedTonsPerHour) || normalizedTonsPerHour <= 0) {
    return 0;
  }

  return Math.ceil((((normalizedQuantityTons / normalizedTonsPerHour) * 60) - 1e-9));
}

function buildTimelineServiceAggregationBySegment(routeSegments = [], assignment = null, trip = null) {
  const aggregationBySegment = Array.from({ length: routeSegments.length }, () => null);
  const aspirationRouteContextIndex = buildTimelineAspirationRouteContextIndex(trip);
  const aggregateAspiratorServiceRun = (runStart, runEnd, stopType) => {
    const serviceRunSegments = routeSegments.slice(runStart, runEnd);
    const aspiratorServiceValues = serviceRunSegments
      .map((groupSegment) => {
        const targetStop = groupSegment?.stops?.[1] || groupSegment?.stops?.[0] || null;
        const fixedMinutes = Number(targetStop?.aspirator_service_minutes);
        return Number.isFinite(fixedMinutes) && fixedMinutes > 0 ? fixedMinutes : null;
      })
      .filter((value) => value != null);
    const backendServiceValues = serviceRunSegments
      .map((groupSegment) => {
        const targetStop = groupSegment?.stops?.[1] || groupSegment?.stops?.[0] || null;
        const backendServiceMinutes = Number(targetStop?.service_time_minutes);
        return Number.isFinite(backendServiceMinutes) && backendServiceMinutes > 0
          ? backendServiceMinutes
          : null;
      })
      .filter((value) => value != null);
    const assignmentAspiratorServiceMinutes = Number(assignment?.aspirator_service_minutes);
    const fallbackServiceValues = Number.isFinite(assignmentAspiratorServiceMinutes)
      && assignmentAspiratorServiceMinutes > 0
      ? [assignmentAspiratorServiceMinutes]
      : backendServiceValues;
    const aspiratorServiceMinutes = stopType === "delivery"
      ? Math.max(
          0,
          ...(aspiratorServiceValues.length ? aspiratorServiceValues : fallbackServiceValues),
        )
      : 0;

    serviceRunSegments.forEach((_groupSegment, groupOffset) => {
      aggregationBySegment[runStart + groupOffset] = {
        serviceDurationMinutes: groupOffset === 0 ? aspiratorServiceMinutes : 0,
      };
    });
  };
  const aggregateServiceRun = (runStart, runEnd, stopType, serviceRateTonsPerHour) => {
    const serviceRunSegments = routeSegments.slice(runStart, runEnd);
    const serviceRunQuantityTons = serviceRunSegments.reduce((total, groupSegment) => {
      const targetStop = groupSegment?.stops?.[1] || groupSegment?.stops?.[0] || null;
      const quantityTons = Number(targetStop?.quantity_tons);
      return Number.isFinite(quantityTons) && quantityTons > 0 ? total + quantityTons : total;
    }, 0);
    let aggregatedServiceMinutes = resolveServiceMinutesForQuantityTons(
      serviceRunQuantityTons,
      serviceRateTonsPerHour,
    );

    if (stopType === "loading") {
      if (serviceRunSegments.some((groupSegment) => {
        const targetStop = groupSegment?.stops?.[1] || groupSegment?.stops?.[0] || null;
        return !targetStop?.is_preloaded_previous_day && Number(targetStop?.quantity_tons) > 0;
      })) {
        aggregatedServiceMinutes += resolveAssignmentFixedMinutes(assignment, "loading");
      }
      const seenClients = new Set();
      serviceRunSegments.forEach((groupSegment) => {
        const targetStop = groupSegment?.stops?.[1] || groupSegment?.stops?.[0] || null;
        if (targetStop?.is_preloaded_previous_day) {
          return;
        }
        const extraMinutes = Number(targetStop?.meunerie_loading_extra_minutes);
        if (Number.isFinite(extraMinutes) && extraMinutes > 0) {
          const clientKey = (targetStop?.loading_client_name || targetStop?.client_name || targetStop?.order_id || "").trim();
          if (clientKey && !seenClients.has(clientKey)) {
            seenClients.add(clientKey);
            aggregatedServiceMinutes += extraMinutes;
          }
        }
      });
    } else if (stopType === "delivery") {
      if (serviceRunQuantityTons > 0) {
        aggregatedServiceMinutes += resolveAssignmentFixedMinutes(assignment, "delivery");
      }
      const seenDeliveries = new Set();
      serviceRunSegments.forEach((groupSegment) => {
        const targetStop = groupSegment?.stops?.[1] || groupSegment?.stops?.[0] || null;
        const extraMinutes = Number(targetStop?.aspirator_service_minutes);
        if (!Number.isFinite(extraMinutes) || extraMinutes <= 0) {
          return;
        }
        const deliveryKey = (targetStop?.order_id || targetStop?.delivery_id || targetStop?.label || "").trim();
        if (deliveryKey && seenDeliveries.has(deliveryKey)) {
          return;
        }
        if (deliveryKey) {
          seenDeliveries.add(deliveryKey);
        }
        aggregatedServiceMinutes += extraMinutes;
      });
    }

    serviceRunSegments.forEach((_groupSegment, groupOffset) => {
      aggregationBySegment[runStart + groupOffset] = {
        serviceDurationMinutes: groupOffset === 0 ? aggregatedServiceMinutes : 0,
      };
    });
  };

  let cursor = 0;

  while (cursor < routeSegments.length) {
    const currentSegment = routeSegments[cursor] || null;
    const currentTargetStop = currentSegment?.stops?.[1] || currentSegment?.stops?.[0] || null;
    const currentStopType = currentTargetStop?.stop_type || "";
    const currentStopGroupKey = resolveTimelineStopGroupKey(currentTargetStop);
    const serviceRateTonsPerHour = resolveAssignmentServiceRateTonsPerHour(assignment, currentStopType);

    if (currentStopType !== "loading" && currentStopType !== "delivery") {
      cursor += 1;
      continue;
    }

    let rawRunEnd = cursor;
    while (rawRunEnd < routeSegments.length) {
      const runSegment = routeSegments[rawRunEnd] || null;
      const runTargetStop = runSegment?.stops?.[1] || runSegment?.stops?.[0] || null;
      if ((runTargetStop?.stop_type || "") !== currentStopType) {
        break;
      }

      const runStopGroupKey = resolveTimelineStopGroupKey(runTargetStop);
      if (currentStopGroupKey && runStopGroupKey && runStopGroupKey !== currentStopGroupKey) {
        break;
      }

      rawRunEnd += 1;
    }

    const rawRunPartitions = [];
    for (let runIndex = cursor; runIndex < rawRunEnd; runIndex += 1) {
      const runSegment = routeSegments[runIndex] || null;
      const runTargetStop = runSegment?.stops?.[1] || runSegment?.stops?.[0] || null;
      rawRunPartitions.push(resolveTimelineAspirationRoutePartition(
        runTargetStop,
        runIndex,
        aspirationRouteContextIndex,
      ));
    }
    const rawRunContainsAspirator = rawRunPartitions.some((partition) => partition.requiresAspirator);

    if (!rawRunContainsAspirator) {
      if (serviceRateTonsPerHour == null) {
        cursor = rawRunEnd;
        continue;
      }
      aggregateServiceRun(cursor, rawRunEnd, currentStopType, serviceRateTonsPerHour);
      cursor = rawRunEnd;
      continue;
    }

    let partitionStart = cursor;
    while (partitionStart < rawRunEnd) {
      const partitionKey = rawRunPartitions[partitionStart - cursor]?.partitionKey || `segment:${partitionStart}`;
      let partitionEnd = partitionStart + 1;
      while (
        partitionEnd < rawRunEnd
        && rawRunPartitions[partitionEnd - cursor]?.partitionKey === partitionKey
      ) {
        partitionEnd += 1;
      }

      const partitionRequiresAspirator = rawRunPartitions[
        partitionStart - cursor
      ]?.requiresAspirator === true;
      if (partitionRequiresAspirator) {
        aggregateAspiratorServiceRun(partitionStart, partitionEnd, currentStopType);
      } else if (serviceRateTonsPerHour != null) {
        aggregateServiceRun(partitionStart, partitionEnd, currentStopType, serviceRateTonsPerHour);
      }
      partitionStart = partitionEnd;
    }

    cursor = rawRunEnd;
  }

  return aggregationBySegment;
}

function getRouteStopLabel(stop) {
  if (!stop || typeof stop !== "object") {
    return "Arrêt";
  }

  return normalizeClientDisplayLabel(stop.label || stop.client_name) || stop.order_id || stop.delivery_id || "Arrêt";
}

function resolveTripTimelineDurationMinutes(trip, timelineSegments) {
  const routeDurationMinutes = normalizeTimelineDurationMinutes(trip?.route?.estimated_duration_minutes);
  if (routeDurationMinutes != null) {
    return routeDurationMinutes;
  }

  const tripDurationMinutes = normalizeTimelineDurationMinutes(trip?.estimated_duration_minutes);
  if (tripDurationMinutes != null) {
    return tripDurationMinutes;
  }

  return timelineSegments.reduce((total, segment) => total + (segment.timelineDurationMinutes || 0), 0);
}

function resolveAssignmentTimelineDurationMinutes(assignment, tripGroups) {
  const assignmentDurationMinutes = normalizeTimelineDurationMinutes(assignment?.total_estimated_duration_minutes);
  if (assignmentDurationMinutes != null) {
    return assignmentDurationMinutes;
  }

  return tripGroups.reduce((total, tripGroup) => total + (tripGroup.totalDurationMinutes || 0), 0);
}

function resolveSegmentTimelineEndMinutes(segment) {
  return normalizeTimelineClockAnchorMinutes(segment?.visualSegmentEndMinutes)
    ?? normalizeTimelineClockAnchorMinutes(segment?.segmentEndMinutes);
}

function resolveTerminalMergedSegmentEndMinutes(segment) {
  const timelineEndMinutes = resolveSegmentTimelineEndMinutes(segment);
  if (segment?.isTerminalRouteMerged !== true) {
    return timelineEndMinutes;
  }

  const terminalMergedEndMinutes = normalizeTimelineClockAnchorMinutes(segment?.terminalMergedStopEndMinutes);
  const candidates = [terminalMergedEndMinutes, timelineEndMinutes].filter((value) => value != null);
  return candidates.length ? Math.max(...candidates) : null;
}

function resolveDurationDrivenTerminalRouteDisplayEndMinutes(segments = []) {
  const resolvedSegments = Array.isArray(segments) ? segments : [];
  let terminalIndex = -1;

  for (let index = resolvedSegments.length - 1; index >= 0; index -= 1) {
    if (resolvedSegments[index]?.stopType === "depot_return") {
      terminalIndex = index;
      break;
    }
  }

  if (terminalIndex <= 0) {
    return null;
  }

  const terminalSegment = resolvedSegments[terminalIndex] || null;
  const previousSegment = resolvedSegments[terminalIndex - 1] || null;

  if (previousSegment?.isTerminalRouteMerged === true) {
    return resolveTerminalMergedSegmentEndMinutes(previousSegment)
      ?? resolveTerminalRouteDisplayEndMinutes(terminalSegment);
  }

  if (
    previousSegment?.usesDurationDrivenTimelineGeometry !== true
    && terminalSegment?.usesDurationDrivenTimelineGeometry !== true
  ) {
    return null;
  }

  const previousStartMinutes = normalizeTimelineClockAnchorMinutes(previousSegment?.visualSegmentStartMinutes)
    ?? normalizeTimelineClockAnchorMinutes(previousSegment?.segmentStartMinutes);

  if (previousStartMinutes == null) {
    return null;
  }

  const terminalTravelMinutes = normalizeTimelineDurationMinutes(terminalSegment?.displayTravelDurationMinutes)
    ?? normalizeTimelineDurationMinutes(terminalSegment?.travelDurationMinutes)
    ?? 0;
  const previousServiceMinutes = normalizeTimelineDurationMinutes(previousSegment?.serviceDurationMinutes) || 0;
  const previousManualBufferMinutes = normalizeTimelineDurationMinutes(previousSegment?.manualBufferMinutes) || 0;

  return previousStartMinutes + terminalTravelMinutes + previousServiceMinutes + previousManualBufferMinutes;
}

export function resolveTerminalRouteDisplayEndMinutes(segment) {
  if (segment?.isTerminalRouteMarker === true) {
    return normalizeTimelineClockAnchorMinutes(segment?.displayedSegmentEndMinutes)
      ?? parseTimelineClockMinutes(segment?.displayedSegmentEndAt)
      ?? resolveSegmentTimelineEndMinutes(segment)
      ?? normalizeTimelineClockAnchorMinutes(segment?.scheduledAtMinutes)
      ?? normalizeTimelineClockAnchorMinutes(segment?.arrivalAtMinutes);
  }

  return resolveSegmentTimelineEndMinutes(segment)
    ?? normalizeTimelineClockAnchorMinutes(segment?.scheduledAtMinutes)
    ?? normalizeTimelineClockAnchorMinutes(segment?.arrivalAtMinutes);
}

function resolveTerminalRouteTimelineDurationMinutes(rowStartMinutes, segments = []) {
  const normalizedRowStartMinutes = normalizeTimelineClockAnchorMinutes(rowStartMinutes);
  if (normalizedRowStartMinutes == null) {
    return null;
  }

  const durationDrivenTerminalEndMinutes = resolveDurationDrivenTerminalRouteDisplayEndMinutes(segments);
  if (durationDrivenTerminalEndMinutes != null) {
    return Math.max(durationDrivenTerminalEndMinutes - normalizedRowStartMinutes, 0);
  }

  const terminalEndMinutes = (Array.isArray(segments) ? segments : [])
    .filter((segment) => segment?.stopType === "depot_return")
    .map((segment) => resolveTerminalRouteDisplayEndMinutes(segment))
    .filter((value) => value != null);

  if (!terminalEndMinutes.length) {
    return null;
  }

  return Math.max(Math.max(...terminalEndMinutes) - normalizedRowStartMinutes, 0);
}

function resolveEffectiveAssignmentTimelineDurationMinutes(rowStartMinutes, segments = [], fallbackDurationMinutes = null) {
  const normalizedFallbackDurationMinutes = normalizeTimelineDurationMinutes(fallbackDurationMinutes);
  const terminalTimelineDurationMinutes = resolveTerminalRouteTimelineDurationMinutes(rowStartMinutes, segments);

  if (terminalTimelineDurationMinutes == null) {
    return normalizedFallbackDurationMinutes;
  }

  if (normalizedFallbackDurationMinutes == null) {
    return terminalTimelineDurationMinutes;
  }

  return Math.max(normalizedFallbackDurationMinutes, terminalTimelineDurationMinutes);
}

function mergeTerminalDepotReturnBeforeEndOfDayLoadSegments(segments = []) {
  const resolvedSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];

  if (resolvedSegments.length < 2) {
    return resolvedSegments;
  }

  const terminalItemIndex = resolvedSegments.length - 1;
  const terminalItem = resolvedSegments[terminalItemIndex] || null;

  if (terminalItem?.stopType !== "depot_return") {
    return resolvedSegments;
  }

  const previousItem = resolvedSegments[terminalItemIndex - 1] || null;
  if (!previousItem) {
    return resolvedSegments;
  }

  const previousTravelDurationMinutes = normalizeTimelineDurationMinutes(previousItem.travelDurationMinutes) ?? 0;
  const terminalTravelDurationMinutes = normalizeTimelineDurationMinutes(terminalItem.travelDurationMinutes) ?? 0;
  const mergedTravelDurationMinutes = previousTravelDurationMinutes + terminalTravelDurationMinutes;
  const terminalDisplayTravelDurationMinutes = normalizeTimelineDurationMinutes(terminalItem.displayTravelDurationMinutes)
    ?? terminalTravelDurationMinutes;
  const serviceDurationMinutes = normalizeTimelineDurationMinutes(previousItem.serviceDurationMinutes) ?? 0;
  const manualBufferMinutes = normalizeTimelineDurationMinutes(previousItem.manualBufferMinutes) ?? 0;
  const waitMinutes = normalizeTimelineDurationMinutes(previousItem.waitMinutes) ?? 0;
  const mergedBaseTimelineDurationMinutes = serviceDurationMinutes + mergedTravelDurationMinutes;
  const mergedTimelineDurationMinutes = mergedBaseTimelineDurationMinutes + manualBufferMinutes + waitMinutes;
  const usesDurationDrivenTerminalGeometry = previousItem?.usesDurationDrivenTimelineGeometry === true
    || terminalItem?.usesDurationDrivenTimelineGeometry === true;
  const previousStartMinutes = normalizeTimelineClockAnchorMinutes(previousItem?.visualSegmentStartMinutes)
    ?? normalizeTimelineClockAnchorMinutes(previousItem?.segmentStartMinutes);
  const durationDrivenTerminalEndMinutes = usesDurationDrivenTerminalGeometry && previousStartMinutes != null
    ? previousStartMinutes + serviceDurationMinutes + manualBufferMinutes + terminalDisplayTravelDurationMinutes
    : null;
  const previousEndMinutes = normalizeTimelineClockAnchorMinutes(previousItem?.visualSegmentEndMinutes)
    ?? normalizeTimelineClockAnchorMinutes(previousItem?.segmentEndMinutes);
  const terminalTimelineDurationMinutes = normalizeTimelineDurationMinutes(terminalItem?.visualTimelineDurationMinutes)
    ?? normalizeTimelineDurationMinutes(terminalItem?.timelineDurationMinutes)
    ?? terminalTravelDurationMinutes;
  const durationBasedTerminalEndMinutes = previousEndMinutes != null && terminalTimelineDurationMinutes > 0
    ? previousEndMinutes + terminalTimelineDurationMinutes
    : null;
  const terminalMergedStopEndAt = durationDrivenTerminalEndMinutes != null || durationBasedTerminalEndMinutes != null
    ? null
    : terminalItem.visualSegmentEndAt || terminalItem.segmentEndAt || terminalItem.scheduledAt || null;
  const terminalMergedStopEndMinutes = durationDrivenTerminalEndMinutes
    ?? durationBasedTerminalEndMinutes
    ?? resolveTerminalRouteDisplayEndMinutes(terminalItem);

  return [
    ...resolvedSegments.slice(0, terminalItemIndex - 1),
    {
      ...previousItem,
      baseTimelineDurationLabel: formatDurationShort(mergedBaseTimelineDurationMinutes) || "0 min",
      baseTimelineDurationMinutes: mergedBaseTimelineDurationMinutes,
      chips: replaceRouteSequenceMetricChips(previousItem.chips, terminalItem.chips),
      displayTravelDurationMinutes: terminalDisplayTravelDurationMinutes,
      distanceLabel: terminalItem.distanceLabel || previousItem.distanceLabel,
      isTerminalRouteMerged: true,
      routeDistanceSource: terminalItem.routeDistanceSource || previousItem.routeDistanceSource || "unavailable",
      routeDistanceSourceLabel: terminalItem.routeDistanceSourceLabel || previousItem.routeDistanceSourceLabel || null,
      routeEstimatedTravelDurationMinutes: terminalItem.routeEstimatedTravelDurationMinutes ?? previousItem.routeEstimatedTravelDurationMinutes ?? null,
      routeFromAddress: terminalItem.routeFromAddress || previousItem.routeFromAddress,
      routeFromLabel: terminalItem.routeFromLabel || previousItem.routeFromLabel,
      routeScheduledTravelDurationMinutes: terminalItem.routeScheduledTravelDurationMinutes ?? previousItem.routeScheduledTravelDurationMinutes ?? null,
      routeSegmentIndex: terminalItem.routeSegmentIndex ?? previousItem.routeSegmentIndex,
      routeSpeedKmh: terminalItem.routeSpeedKmh ?? previousItem.routeSpeedKmh ?? null,
      routeToAddress: terminalItem.routeToAddress || previousItem.routeToAddress,
      routeToLabel: terminalItem.routeToLabel || previousItem.routeToLabel,
      terminalMergedStopEndAt,
      terminalMergedStopEndMinutes,
      timelineDurationLabel: formatDurationShort(mergedTimelineDurationMinutes) || "0 min",
      timelineDurationMinutes: mergedTimelineDurationMinutes,
      travelDurationLabel: mergedTravelDurationMinutes > 0 ? formatDurationShort(mergedTravelDurationMinutes) : (mergedTravelDurationMinutes === 0 ? "0 min" : null),
      travelDurationMinutes: mergedTravelDurationMinutes,
      visualTimelineDurationMinutes: mergedTimelineDurationMinutes,
    },
    {
      ...terminalItem,
      chips: [],
      displayTravelDurationMinutes: 0,
      displayedSegmentEndAt: terminalMergedStopEndAt,
      displayedSegmentEndMinutes: terminalMergedStopEndMinutes,
      distanceLabel: null,
      isTerminalRouteMarker: true,
      serviceDurationLabel: null,
      serviceDurationMinutes: 0,
      timelineDurationLabel: null,
      timelineDurationMinutes: 0,
      travelDurationLabel: null,
      travelDurationMinutes: 0,
      visualSegmentEndAt: terminalMergedStopEndAt,
      visualSegmentEndMinutes: terminalMergedStopEndMinutes,
      visualTimelineDurationMinutes: 0,
    },
  ];
}

function normalizeTimelineStopMatchValue(value) {
  return typeof value === "string" ? value.trim() : "";
}

function countTripDeliveryStepsByOrderId(trip, orderId = "") {
  const normalizedOrderId = normalizeTimelineStopMatchValue(orderId);
  if (!normalizedOrderId) {
    return 0;
  }

  const operationalSteps = Array.isArray(trip?.operational_steps) ? trip.operational_steps : [];
  return operationalSteps.reduce((count, step) => {
    return normalizeTimelineStopMatchValue(step?.step_type) === "delivery"
      && normalizeTimelineStopMatchValue(step?.order_id) === normalizedOrderId
      ? count + 1
      : count;
  }, 0);
}

function buildDeliveryBlockId(tripId = "", blockIndex = 0) {
  const safeTripId = normalizeTimelineStopMatchValue(tripId) || "trip";
  const safeBlockIndex = Number.isInteger(blockIndex) && blockIndex > 0 ? blockIndex : 0;
  return safeBlockIndex > 0 ? `${safeTripId}:delivery-block:${safeBlockIndex}` : "";
}

function buildDeliveryBlockMetadataByStepId(trip) {
  const operationalSteps = Array.isArray(trip?.operational_steps) ? trip.operational_steps.filter(Boolean) : [];
  const metadataByStepId = new Map();
  let currentDeliveryBlock = [];
  let blockIndex = 0;

  const flushDeliveryBlock = () => {
    if (currentDeliveryBlock.length === 0) {
      return;
    }

    blockIndex += 1;
    if (currentDeliveryBlock.length > 1) {
      const deliveryBlockId = buildDeliveryBlockId(trip?.trip_id || "", blockIndex);
      currentDeliveryBlock.forEach((step) => {
        const stepId = normalizeTimelineStopMatchValue(step?.step_id);
        if (stepId) {
          metadataByStepId.set(stepId, {
            deliveryBlockId,
            deliveryBlockIndex: blockIndex,
            deliveryBlockSize: currentDeliveryBlock.length,
          });
        }
      });
    }

    currentDeliveryBlock = [];
  };

  operationalSteps.forEach((step) => {
    if (normalizeTimelineStopMatchValue(step?.step_type) === "delivery") {
      currentDeliveryBlock.push(step);
      return;
    }

    flushDeliveryBlock();
  });
  flushDeliveryBlock();

  return metadataByStepId;
}

function buildManualBufferStepKeyFromOperationalStep(step, trip) {
  const stepType = normalizeTimelineStopMatchValue(step?.step_type);
  const orderId = normalizeTimelineStopMatchValue(step?.order_id);
  if (!orderId) {
    return "";
  }

  if (stepType === "loading") {
    return `loading:${orderId}`;
  }

  if (stepType !== "delivery") {
    return "";
  }

  const deliveryId = normalizeTimelineStopMatchValue(step?.delivery_id);
  if (deliveryId) {
    return `delivery:${orderId}:${deliveryId}`;
  }

  return countTripDeliveryStepsByOrderId(trip, orderId) === 1 ? `delivery:${orderId}` : "";
}

function appendTimelineGroupedItem(groupedItems, key, item) {
  if (!key || !item) {
    return;
  }

  if (!groupedItems.has(key)) {
    groupedItems.set(key, []);
  }

  groupedItems.get(key).push(item);
}

function buildTripTimelineStatusIndex(trip) {
  const clients = Array.isArray(trip?.clients) ? trip.clients.filter(Boolean) : [];
  const operationalSteps = Array.isArray(trip?.operational_steps) ? trip.operational_steps.filter(Boolean) : [];
  const clientsByDeliveryId = new Map();
  const clientsByOrderId = new Map();
  const loadingStepsById = new Map();
  const loadingStepsByOrderId = new Map();
  const deliveryStepsByDeliveryId = new Map();
  const deliveryStepsByOrderId = new Map();

  clients.forEach((client) => {
    const deliveryId = normalizeTimelineStopMatchValue(client?.delivery_id);
    const orderId = normalizeTimelineStopMatchValue(client?.order_id);

    if (deliveryId) {
      clientsByDeliveryId.set(deliveryId, client);
    }

    appendTimelineGroupedItem(clientsByOrderId, orderId, client);
  });

  operationalSteps.forEach((step) => {
    const stepType = normalizeTimelineStopMatchValue(step?.step_type);
    const stepId = normalizeTimelineStopMatchValue(step?.step_id);
    const orderId = normalizeTimelineStopMatchValue(step?.order_id);
    const deliveryId = normalizeTimelineStopMatchValue(step?.delivery_id);

    if (stepType === "loading") {
      if (stepId) {
        loadingStepsById.set(stepId, step);
      }

      if (
        orderId
        && (
          !loadingStepsByOrderId.has(orderId)
          || (loadingStepsByOrderId.get(orderId)?.is_initial_preload && !step?.is_initial_preload)
        )
      ) {
        loadingStepsByOrderId.set(orderId, step);
      }

      return;
    }

    if (stepType === "delivery") {
      if (deliveryId) {
        deliveryStepsByDeliveryId.set(deliveryId, step);
      }

      if (orderId && !deliveryStepsByOrderId.has(orderId)) {
        deliveryStepsByOrderId.set(orderId, step);
      }
    }
  });

  return {
    clientsByDeliveryId,
    clientsByOrderId,
    deliveryStepsByDeliveryId,
    deliveryStepsByOrderId,
    loadingStepsById,
    loadingStepsByOrderId,
  };
}

function resolveTimelineSegmentOperationalStep(stop, statusIndex) {
  if (!stop || typeof stop !== "object") {
    return null;
  }

  const resolvedStatusIndex = statusIndex || { clientsByDeliveryId: new Map(), clientsByOrderId: new Map() };
  const orderId = normalizeTimelineStopMatchValue(stop.order_id);
  const deliveryId = normalizeTimelineStopMatchValue(stop.delivery_id);

  if (stop.stop_type === "delivery") {
    return deliveryId
      ? resolvedStatusIndex.deliveryStepsByDeliveryId.get(deliveryId) || null
      : resolvedStatusIndex.deliveryStepsByOrderId.get(orderId) || null;
  }

  if (stop.stop_type !== "loading") {
    return null;
  }

  const relatedClients = deliveryId
    ? [resolvedStatusIndex.clientsByDeliveryId.get(deliveryId)].filter(Boolean)
    : (resolvedStatusIndex.clientsByOrderId.get(orderId) || []).filter(Boolean);
  const loadingStepId = normalizeTimelineStopMatchValue(
    relatedClients.find((client) => normalizeTimelineStopMatchValue(client?.loading_step_id))?.loading_step_id,
  );

  return (loadingStepId && resolvedStatusIndex.loadingStepsById.get(loadingStepId))
    || resolvedStatusIndex.loadingStepsByOrderId.get(orderId)
    || null;
}

function isTimelinePendingApprovalReason(reason) {
  if (typeof reason !== "string") {
    return false;
  }

  const normalizedReason = reason.trim().toLowerCase();
  return normalizedReason.includes("attente d'approbation")
    || normalizedReason.includes("a approuver")
    || normalizedReason.includes("non approuv");
}

function buildReassignedStatusMarker() {
  return {
    description: "Commande assignée manuellement et conservée sur ce camion tant qu'elle n'est pas libérée.",
    icon: "shuffle",
    key: "reassigned",
    label: "Réaffectée",
    tone: "manual",
  };
}

function resolveTimelineSegmentLoadingStep(trip, stop, statusIndex, matchedStep = null) {
  if (!stop || typeof stop !== "object") {
    return null;
  }

  const resolvedStatusIndex = statusIndex || buildTripTimelineStatusIndex(trip);
  const orderId = normalizeTimelineStopMatchValue(stop.order_id);
  const deliveryId = normalizeTimelineStopMatchValue(stop.delivery_id);
  const matchedStepType = normalizeTimelineStopMatchValue(matchedStep?.step_type);

  if (matchedStepType === "loading") {
    return matchedStep;
  }

  const relatedClients = deliveryId
    ? [resolvedStatusIndex.clientsByDeliveryId.get(deliveryId)].filter(Boolean)
    : (resolvedStatusIndex.clientsByOrderId.get(orderId) || []).filter(Boolean);
  const loadingStepId = normalizeTimelineStopMatchValue(matchedStep?.loading_step_id)
    || normalizeTimelineStopMatchValue(
      relatedClients.find((client) => normalizeTimelineStopMatchValue(client?.loading_step_id))?.loading_step_id,
    );

  if (loadingStepId) {
    return resolvedStatusIndex.loadingStepsById.get(loadingStepId) || null;
  }

  return resolvedStatusIndex.loadingStepsByOrderId.get(orderId) || null;
}

function isPortalDriverLoadingStepConfirmed(loadingStep) {
  if (!loadingStep || typeof loadingStep !== "object") {
    return false;
  }

  const isConfirmed = Boolean(loadingStep.is_confirmed || loadingStep.is_loading_confirmed);
  if (!isConfirmed) {
    return false;
  }

  const confirmationPortail = typeof loadingStep.confirmation_portail === "string"
    ? loadingStep.confirmation_portail.trim().toLowerCase()
    : "";
  const confirmationSource = typeof loadingStep.confirmation_source === "string"
    ? loadingStep.confirmation_source.trim().toLowerCase()
    : "";

  return confirmationPortail === "confirmée"
    || confirmationPortail === "confirmee"
    || confirmationSource === "driver_portal";
}

function isPortalDriverLoadingReassignmentLocked(trip, stop, statusIndex, matchedStep = null) {
  return isPortalDriverLoadingStepConfirmed(
    resolveTimelineSegmentLoadingStep(trip, stop, statusIndex, matchedStep),
  );
}

function buildTimelineSegmentStatusMarkers(trip, stop, statusIndex) {
  if (!stop || typeof stop !== "object") {
    return [];
  }

  const resolvedStatusIndex = statusIndex || buildTripTimelineStatusIndex(trip);
  const orderId = normalizeTimelineStopMatchValue(stop.order_id);
  const deliveryId = normalizeTimelineStopMatchValue(stop.delivery_id);
  const relatedClients = deliveryId
    ? [resolvedStatusIndex.clientsByDeliveryId.get(deliveryId)].filter(Boolean)
    : (resolvedStatusIndex.clientsByOrderId.get(orderId) || []).filter(Boolean);
  const deliveryStep = deliveryId
    ? resolvedStatusIndex.deliveryStepsByDeliveryId.get(deliveryId) || null
    : resolvedStatusIndex.deliveryStepsByOrderId.get(orderId) || null;
  const loadingStepId = normalizeTimelineStopMatchValue(
    relatedClients.find((client) => normalizeTimelineStopMatchValue(client?.loading_step_id))?.loading_step_id,
  );
  const loadingStep = (loadingStepId && resolvedStatusIndex.loadingStepsById.get(loadingStepId))
    || resolvedStatusIndex.loadingStepsByOrderId.get(orderId)
    || null;
  const markers = [];
  const clientHasHardBlockingReason = (client) => {
    const blockingReason = client?.blocking_reason;
    return Boolean(
      (client?.is_delivery_blocked || blockingReason)
      && !isTimelinePendingApprovalReason(blockingReason),
    );
  };
  const stepHasHardBlockingReason = (step) => {
    const blockingReason = step?.blocking_reason;
    return Boolean(
      (step?.is_blocked || blockingReason)
      && !isTimelinePendingApprovalReason(blockingReason),
    );
  };
  const protectedClient = stop.stop_type === "delivery"
    ? relatedClients.find((client) => (
      client?.manual_status_code === "delivery_confirmed"
      || clientHasHardBlockingReason(client)
    )) || null
    : null;
  const candidateProtectedStep = stop.stop_type === "loading"
    ? loadingStep
    : stop.stop_type === "delivery"
      ? deliveryStep
      : null;
  const protectedStep = candidateProtectedStep && (
    candidateProtectedStep?.manual_status_code === "loading_confirmed"
    || candidateProtectedStep?.manual_status_code === "delivery_confirmed"
    || (stop.stop_type === "loading" && Boolean(candidateProtectedStep?.is_confirmed))
    || stepHasHardBlockingReason(candidateProtectedStep)
  )
    ? candidateProtectedStep
    : null;
  const hasProtectedDeliveryForLoadingOrder = stop.stop_type === "loading" && relatedClients.some((client) => (
    client?.manual_status_code === "delivery_confirmed"
    || client?.is_delivery_confirmed
  ));
  const isSuspended = relatedClients.some((client) => (
    client?.manual_status_code === "delivery_suspended"
    || client?.is_delivery_suspended
  ));
  const isReassigned = relatedClients.some((client) => client?.manual_status_code === "delivery_reassigned");
  const hasLoadScopedManualReorder = relatedClients.some((client) => (
    client?.is_manually_reordered
    && client?.manual_reorder_scope === "loads"
  ));
  const hasDeliveryScopedManualReorder = relatedClients.some((client) => client?.is_manually_reordered);
  const isManuallyReordered = stop.stop_type === "loading"
    ? Boolean(candidateProtectedStep?.is_manually_reordered || hasLoadScopedManualReorder)
    : Boolean(hasDeliveryScopedManualReorder || candidateProtectedStep?.is_manually_reordered);
  const isProtected = Boolean(protectedClient || protectedStep || hasProtectedDeliveryForLoadingOrder);
  const isApproved = stop.stop_type === "loading"
    ? Boolean(loadingStep?.is_confirmed)
    : Boolean(
      relatedClients.some((client) => client?.is_delivery_confirmed)
      || deliveryStep?.is_confirmed,
    );

  // A completed delivery also completes its loading, regardless of confirmation source.
  const hasDeliveredLoadingOrder = stop.stop_type === "loading"
    && [...relatedClients, deliveryStep].some((delivery) => (
      delivery?.dispatcher_execution_status === "delivered"
      && (!delivery.loading_step_id || !loadingStep?.step_id || delivery.loading_step_id === loadingStep.step_id)
    ));
  if (hasDeliveredLoadingOrder) {
    markers.push({
      description: "Commande livrée : son chargement est également terminé.",
      icon: "check",
      key: "delivered",
      label: "Livrée",
      tone: "success",
    });
  }

  if (isSuspended) {
    markers.push({
      description: "Commande retirée des tournées actives jusqu'à une réactivation explicite.",
      icon: "pause",
      key: "suspended",
      label: "Suspendue",
      tone: "warning",
    });
  }

  if (isManuallyReordered) {
    markers.push(buildProtectionStatusMarker({
      manualAction: "reordered",
      variant: PROTECTION_VARIANT_REORDERED,
    }));
  } else if (isReassigned) {
    markers.push(buildReassignedStatusMarker());
  } else if (isProtected) {
    const blockingReason = protectedClient?.blocking_reason || protectedStep?.blocking_reason || "";
    const isBlocked = Boolean(
      (protectedClient?.is_delivery_blocked || protectedStep?.is_blocked || blockingReason)
      && !isTimelinePendingApprovalReason(blockingReason),
    );
    markers.push(buildProtectionStatusMarker({
      blocked: isBlocked,
      blockingReason,
      variant: PROTECTION_VARIANT_STANDARD,
    }));
  }

  if (isApproved && !isProtected && stop.stop_type !== "loading") {
    markers.push({
      description: stop.stop_type === "loading"
        ? "Chargement confirmé dans l'état courant du planning."
        : "Livraison confirmée dans l'état courant du planning.",
      icon: "shield",
      key: "approved",
      label: stop.stop_type === "loading" ? "Chargement approuvé" : "Livraison approuvée",
      tone: "success",
    });
  }

  const isPortalLoadingConfirmed = stop.stop_type === "loading"
    && isPortalDriverLoadingStepConfirmed(loadingStep);

  if (isPortalLoadingConfirmed) {
    const withoutProtected = markers.filter((marker) => marker?.key !== "protected");
    if (!withoutProtected.some((marker) => marker?.icon === "smartphone" || marker?.key === "portal")) {
      withoutProtected.unshift({
        description: "Chargement confirmé par le portail chauffeur.",
        icon: "smartphone",
        key: "portal",
        label: "Portail",
        tone: "info",
        portalHighlight: true,
      });
    }
    return withoutProtected;
  }

  return markers;
}

function mergeBusinessStepStatusMarkers(markers = [], businessStep = null) {
  const dispatcherStatusCode = typeof businessStep?.dispatcherStatus?.code === "string"
    ? businessStep.dispatcherStatus.code
    : "";
  const gelOrigine = typeof businessStep?.dispatcherStatus?.gelOrigine === "string"
    ? businessStep.dispatcherStatus.gelOrigine.trim().toLowerCase()
    : "";
  const confirmationPortail = typeof businessStep?.confirmationPortail === "string"
    ? businessStep.confirmationPortail.trim().toLowerCase()
    : "";
  const manualStatusCode = typeof businessStep?.manualStatus?.code === "string"
    ? businessStep.manualStatus.code
    : "";

  let resolvedMarkers = Array.isArray(markers) ? [...markers] : [];
  const hasMarker = (key) => resolvedMarkers.some((marker) => marker?.key === key);
  const hasProtectionMarker = () => resolvedMarkers.some((marker) => (
    marker?.category === "protected"
    || marker?.key === "protected"
    || marker?.key === "protected-reordered"
    || marker?.key === "blocked"
  ));

  const isFrozenStatus = isFrozenDispatcherStatusCode(dispatcherStatusCode);
  const isSharedLoadingFreeze = dispatcherStatusCode === "frozen_shared_loading";
  const isLoadingStep = businessStep?.stepType === "loading";
  const isDeliveredLoading = isLoadingStep && (dispatcherStatusCode === "delivered" || hasMarker("delivered"));
  const freezeChain = businessStep?.sharedLoadingFreezeChain || null;
  const isChainMember = freezeChain?.isMember === true;
  const isChainTail = freezeChain?.isTail === true;
  const hasSharedLoadingMember = freezeChain?.hasSharedLoadingMember === true;
  const isPortalFreeze = resolvePortalDriverConfirmationHighlight({
    confirmationPortail,
    gelOrigine,
    dispatcherStatusCode,
  });
  const isPortalTailWithoutSharedLoading = isChainTail
    && !isLoadingStep
    && dispatcherStatusCode === "frozen"
    && isPortalFreeze
    && !hasSharedLoadingMember;

  if (isFrozenStatus || isDeliveredLoading) {
    resolvedMarkers = resolvedMarkers.filter((marker) => marker?.key !== "protected");
  }

  if (isSharedLoadingFreeze || (isChainMember && !isLoadingStep)) {
    resolvedMarkers = resolvedMarkers.filter((marker) => (
      marker?.key !== "reassigned"
      && marker?.key !== "protected"
      && marker?.key !== "protected-reordered"
    ));
  }

  if (isChainMember && !isChainTail && !isLoadingStep) {
    resolvedMarkers = resolvedMarkers
      .filter((marker) => marker?.key !== "portal" && marker?.icon !== "smartphone")
      .map((marker) => (
        marker?.key === "frozen" || marker?.icon === "lock" || marker?.icon === "pause"
          ? { ...marker, icon: "", key: "frozen", portalHighlight: false }
          : marker
      ));
  }

  if (isChainTail && !isLoadingStep && !isPortalTailWithoutSharedLoading) {
    resolvedMarkers = resolvedMarkers
      .filter((marker) => marker?.key !== "portal" && marker?.icon !== "smartphone")
      .map((marker) => (
        marker?.key === "frozen"
          ? {
              ...marker,
              icon: "lock",
              label: "Gelée",
              portalHighlight: false,
              tone: "warning",
            }
          : marker
      ));
  }

  const hasPortalSmartphoneMarker = () => resolvedMarkers.some((marker) => (
    marker?.key === "portal"
    || marker?.portalHighlight === true
    || marker?.icon === "smartphone"
  ));

  if ((isFrozenStatus || (isChainMember && !isLoadingStep)) && !hasMarker("frozen")) {
    if (isSharedLoadingFreeze && isLoadingStep) {
      // Les chargements du lot gardent le smartphone portail, jamais de cadenas.
    } else if (isChainMember && !isChainTail && !isLoadingStep) {
      resolvedMarkers.unshift({
        description: isSharedLoadingFreeze
          ? "Commande implicitement gelée parce que son chargement est encore dans le camion après une livraison gelée du même lot."
          : "Commande verrouillée dans le lot gelé. Le cadenas et le dégel restent sur la dernière livraison encore à bord.",
        icon: "",
        key: "frozen",
        label: "Gelée",
        tone: "warning",
        portalHighlight: false,
      });
    } else if ((isChainTail && !isLoadingStep && !isPortalTailWithoutSharedLoading) || (isSharedLoadingFreeze && !isLoadingStep)) {
      resolvedMarkers.unshift({
        description: "Dernière livraison du lot encore à bord. Le dégel cible les ancres explicitement gelées.",
        icon: "lock",
        key: "frozen",
        label: "Gelée",
        tone: "warning",
        portalHighlight: false,
      });
    } else if (!(isPortalFreeze && hasPortalSmartphoneMarker())) {
      resolvedMarkers.push({
        description: dispatcherStatusCode === "frozen_prefix"
          ? (isPortalFreeze
            ? "Implicitement gelée (préfixe d'une commande gelée par le portail chauffeur)."
            : "Gelée avec une commande plus loin dans la tournée : actions masquées jusqu'à son dégel.")
          : (isPortalFreeze
            ? "Commande gelée suite à une confirmation du portail chauffeur."
            : "Toutes les commandes précédentes ne se déplaceront plus sur le camion."),
        icon: isPortalFreeze ? "smartphone" : "pause",
        key: "frozen",
        label: isPortalFreeze ? "Gelée (portail)" : "Gelée",
        tone: isPortalFreeze ? "info" : "warning",
        portalHighlight: isPortalFreeze,
      });
    }
  }

  if (dispatcherStatusCode === "delivered" && !hasMarker("delivered")) {
    resolvedMarkers.push({
      description: "Commande livrée. Elle ne sera plus déplacée.",
      icon: "check",
      key: "delivered",
      label: "Livrée",
      tone: "success",
    });
  }

  if (manualStatusCode === "suspended" && !hasMarker("suspended")) {
    resolvedMarkers.push({
      description: "Commande retirée des tournées actives jusqu'à une réactivation explicite.",
      icon: "pause",
      key: "suspended",
      label: "Suspendue",
      tone: "warning",
    });
  }

  if (manualStatusCode === "reordered" && !isSharedLoadingFreeze) {
    const withoutSupersededProtectionMarkers = resolvedMarkers.filter((marker) => (
      marker?.key !== "reassigned"
      && marker?.key !== "protected"
    ));
    if (!withoutSupersededProtectionMarkers.some((marker) => marker?.key === "protected-reordered")) {
      withoutSupersededProtectionMarkers.push(buildProtectionStatusMarker({
        manualAction: "reordered",
        variant: PROTECTION_VARIANT_REORDERED,
      }));
    }
    return withoutSupersededProtectionMarkers;
  }

  if (manualStatusCode === "reassigned" && !hasMarker("reassigned") && !isSharedLoadingFreeze) {
    resolvedMarkers.push(buildReassignedStatusMarker());
  }

  if (manualStatusCode === "protected" && !hasProtectionMarker()) {
    if (!isFrozenStatus && !isDeliveredLoading) {
      resolvedMarkers.push(buildProtectionStatusMarker({
        variant: PROTECTION_VARIANT_STANDARD,
      }));
    }
  }

  const isPortalConfirmation = resolvePortalDriverConfirmationHighlight({
    confirmationPortail,
    gelOrigine,
    dispatcherStatusCode,
  });
  if (
    dispatcherStatusCode === "delivered"
    && isPortalConfirmation
    && !hasMarker("portal")
    && !(isChainMember && !isChainTail && !isLoadingStep)
  ) {
    resolvedMarkers.push({
      description: "Livraison confirmée par le portail chauffeur.",
      icon: "smartphone",
      key: "portal",
      label: "Portail",
      tone: "info",
      portalHighlight: true,
    });
  }

  const portalMarkerIndex = resolvedMarkers.findIndex((marker) => (
    marker?.portalHighlight === true
    || marker?.icon === "smartphone"
  ));
  if (portalMarkerIndex >= 0) {
    resolvedMarkers = resolvedMarkers.filter((marker) => marker?.key !== "approved" && marker?.key !== "protected");
    if (portalMarkerIndex > 0) {
      const [portalMarker] = resolvedMarkers.splice(resolvedMarkers.findIndex((marker) => (
        marker?.portalHighlight === true
        || marker?.icon === "smartphone"
      )), 1);
      resolvedMarkers.unshift(portalMarker);
    }
  }

  return resolvedMarkers;
}

function buildInitialDepotTravelTimelineSegment(segment, assignment, trip, {
  sequenceStart = 1,
  truckLoadBeforeTons = null,
  useBackendDisplayProjection = false,
  useDurationDrivenTimelineGeometry = false,
} = {}) {
  const displayedRouteSegment = buildInitialDepotTravelDisplayedRouteSegment(segment);
  const fromStop = getRouteSegmentStartStop(displayedRouteSegment);
  const toStop = getRouteSegmentTargetStop(displayedRouteSegment) || fromStop;
  const anchorStop = segment?.anchorStop || null;
  const anchorStep = segment?.anchorStep || null;
  const priorityMetadata = resolveTimelineSegmentPriorityMetadata(
    anchorStop,
    anchorStep,
    useBackendDisplayProjection,
  );
  const scheduledAt = resolveTimelineSegmentScheduledAt(
    anchorStop,
    anchorStep,
    useBackendDisplayProjection,
  );
  const scheduledAtMinutes = resolveTimelineSegmentScheduledAtMinutes(
    anchorStop,
    anchorStep,
    useBackendDisplayProjection,
  );
  const assignmentId = assignment?.truck_id || "";
  const tripId = trip?.trip_id || "";
  const travelDurationMinutes = normalizeTimelineDurationMinutes(displayedRouteSegment?.durationMinutes) || 0;
  const displayTravelDurationMinutes = resolveRouteSegmentDisplayDurationMinutes(displayedRouteSegment) ?? travelDurationMinutes;
  const serviceDurationMinutes = normalizeTimelineDurationMinutes(fromStop?.service_time_minutes) || 0;
  const serviceDurationLabel = serviceDurationMinutes > 0
    ? formatDisplayedServiceDurationLabel(serviceDurationMinutes)
    : null;
  const startAt = resolveTimelineFieldText(
    [fromStop],
    useBackendDisplayProjection ? ["arrival", "scheduled", "start"] : ["arrival", "scheduled"],
    useBackendDisplayProjection,
  );
  const departureAt = resolveTimelineFieldText(
    [fromStop],
    useBackendDisplayProjection
      ? ["departure", "service_end", "scheduled", "end"]
      : ["departure", "service_end", "scheduled"],
    useBackendDisplayProjection,
  );
  const endAt = resolveTimelineFieldText(
    [toStop],
    useBackendDisplayProjection ? ["arrival", "scheduled", "start"] : ["arrival", "scheduled"],
    useBackendDisplayProjection,
  );
  const startAtMinutes = resolveTimelineFieldMinutes(
    [fromStop],
    useBackendDisplayProjection ? ["arrival", "scheduled", "start"] : ["arrival", "scheduled"],
    useBackendDisplayProjection,
  );
  const departureAtMinutes = resolveTimelineFieldMinutes(
    [fromStop],
    useBackendDisplayProjection
      ? ["departure", "service_end", "scheduled", "end"]
      : ["departure", "service_end", "scheduled"],
    useBackendDisplayProjection,
  );
  const endAtMinutes = resolveTimelineFieldMinutes(
    [toStop],
    useBackendDisplayProjection ? ["arrival", "scheduled", "start"] : ["arrival", "scheduled"],
    useBackendDisplayProjection,
  );
  const segmentStartAt = scheduledAt != null
    ? normalizeTimelineTimestamp(scheduledAt)
    : startAt != null
      ? normalizeTimelineTimestamp(startAt)
    : departureAt != null
      ? shiftTimelineTimestamp(departureAt, -serviceDurationMinutes)
    : endAt != null
      ? shiftTimelineTimestamp(endAt, -(travelDurationMinutes + serviceDurationMinutes))
      : null;
  const segmentStartMinutes = scheduledAtMinutes != null
    ? scheduledAtMinutes
    : startAtMinutes != null
      ? startAtMinutes
    : departureAtMinutes != null
      ? Math.max(departureAtMinutes - serviceDurationMinutes, 0)
      : resolveTimelineSegmentStartMinutesFromSchedule(endAtMinutes, travelDurationMinutes + serviceDurationMinutes);
  const segmentEndAt = endAt != null
    ? normalizeTimelineTimestamp(endAt)
    : departureAt != null
      ? shiftTimelineTimestamp(departureAt, travelDurationMinutes)
    : segmentStartAt != null
      ? shiftTimelineTimestamp(segmentStartAt, travelDurationMinutes + serviceDurationMinutes)
      : null;
  const segmentEndMinutes = endAtMinutes != null
    ? endAtMinutes
    : departureAtMinutes != null
      ? departureAtMinutes + travelDurationMinutes
    : segmentStartMinutes != null
      ? segmentStartMinutes + travelDurationMinutes + serviceDurationMinutes
      : null;
  const displayedTimelineDurationMinutes = segmentStartMinutes != null && segmentEndMinutes != null
    ? Math.max(segmentEndMinutes - segmentStartMinutes, 0)
    : travelDurationMinutes + serviceDurationMinutes;
  const durationDrivenTimelineDurationMinutes = displayTravelDurationMinutes + serviceDurationMinutes;
  const timelineDisplayDurationMinutes = useDurationDrivenTimelineGeometry
    ? durationDrivenTimelineDurationMinutes
    : displayedTimelineDurationMinutes;
  const hasPriorityTimeAnchor = Boolean(priorityMetadata.dominantPriority && scheduledAtMinutes != null);
  const mapHighlightSegmentIndex = getTechnicalRouteSegmentIndex(displayedRouteSegment)
    ?? getTechnicalRouteSegmentIndex(segment);

  return {
    assignmentId,
    ...(useDurationDrivenTimelineGeometry
      ? {
          baseTimelineDurationLabel: formatDurationShort(durationDrivenTimelineDurationMinutes) || "0 min",
          baseTimelineDurationMinutes: durationDrivenTimelineDurationMinutes,
        }
      : {}),
    businessStep: null,
    clientId: "",
    chips: buildRouteSegmentChips(displayedRouteSegment, fromStop, trip, { serviceMinutesOverride: serviceDurationMinutes }),
    color: getStopDisplayColor(fromStop),
    distanceLabel: displayedRouteSegment?.distanceLabel || null,
    fromAddress: getRouteStopAddressLabel(fromStop?.address),
    fromLabel: getRouteStopLabel(fromStop),
    globalSequence: sequenceStart,
    isInitialRouteSegment: true,
    isActivationHighlighted: false,
    isSelectable: false,
    isSelectedClient: false,
    isCurrentTrip: Boolean(tripId),
    orderLabel: "",
    operationAddress: getRouteStopAddressLabel(toStop?.address),
    operationDeliveryId: "",
    operationDisambiguatorLabel: "",
    operationKey: `${assignmentId || "assignment"}::${tripId || "trip"}::${sequenceStart}::initial-travel`,
    operationLabel: "",
    operationOrderId: "",
    operationTypeLabel: "Déplacement",
    productCodeLabel: "",
    productLabel: "",
    quantityLabel: null,
    quantityTons: null,
    arrivalAt: endAt,
    arrivalAtMinutes: endAtMinutes,
    dominantPriority: priorityMetadata.dominantPriority || "",
    hasPriorityTimeAnchor,
    isPreloadedPreviousDay: false,
    reassignmentAvailability: null,
    reassignmentDrag: null,
    segmentReorder: null,
    routeSegmentIndex: mapHighlightSegmentIndex ?? sequenceStart,
    routeFromAddress: getRouteStopAddressLabel(fromStop?.address),
    routeFromLabel: getRouteStopLabel(fromStop),
    routeScheduledTravelDurationMinutes: normalizeTimelineDurationMinutes(displayedRouteSegment?.scheduledDurationMinutes),
    routeToAddress: getRouteStopAddressLabel(toStop?.address),
    routeToLabel: getRouteStopLabel(toStop),
    serviceDurationLabel,
    serviceDurationMinutes,
    isSameSiteSequenceGroup: false,
    sameSiteGroupContainsMixedStopTypes: false,
    sameSiteGroupIndex: 0,
    sameSiteGroupKey: "",
    sameSiteGroupStartSequence: sequenceStart,
    sameSiteGroupSize: 1,
    scheduledAt,
    scheduledAtMinutes,
    segmentEndAt,
    segmentEndMinutes,
    segmentStartAt,
    segmentStartMinutes,
    stopType: "depot_start",
    statusMarkers: [],
    statusSummaryLabel: "",
    timeWindowOverride: null,
    timelineStartOverrideMinutes: scheduledAtMinutes,
    timelineDurationLabel: formatDurationShort(timelineDisplayDurationMinutes) || "0 min",
    timelineDurationMinutes: timelineDisplayDurationMinutes,
    toAddress: getRouteStopAddressLabel(toStop?.address),
    toLabel: getRouteStopLabel(toStop),
    displayTravelDurationMinutes,
    travelDurationLabel: travelDurationMinutes > 0 ? formatDurationShort(travelDurationMinutes) : (travelDurationMinutes === 0 ? "0 min" : null),
    travelDurationMinutes,
    truckLoadBeforeTons: Number.isFinite(Number(truckLoadBeforeTons)) ? Number(truckLoadBeforeTons) : null,
    tripId,
    tripLabel: tripId || "Tournée",
    tripSequence: sequenceStart,
    typeLabel: "Déplacement",
    visualSegmentEndAt: segmentEndAt,
    visualSegmentEndMinutes: segmentEndMinutes,
    visualSegmentStartAt: hasPriorityTimeAnchor ? normalizeTimelineTimestamp(scheduledAt) : segmentStartAt,
    visualSegmentStartMinutes: hasPriorityTimeAnchor ? scheduledAtMinutes : segmentStartMinutes,
    visualServiceDurationMinutes: serviceDurationMinutes,
    visualTimelineDurationMinutes: timelineDisplayDurationMinutes,
    usesDurationDrivenTimelineGeometry: useDurationDrivenTimelineGeometry,
    waitMinutes: null,
  };
}

export function buildTripTimelineSegments(trip, assignment, {
  openOperationalReassignmentKey = "",
  pendingDeliveryIds = new Set(),
  pendingLoadingIds = new Set(),
  selectedClientId = "",
  sequenceStart = 1,
  state = null,
  transientActivatedStepKeys = new Set(),
} = {}) {
  const useBackendDisplayProjection = usesBackendTimelineDisplayProjection(state);
  const routeDurationOptions = buildRouteDurationOptionsFromState(state);
  const routeSegments = getTripRouteSegments(trip, assignment, routeDurationOptions);
  const useDurationDrivenTimelineGeometry = !useBackendDisplayProjection
    && routeDurationOptions.preferScheduledDuration !== true;
  const initialPreloadSteps = getTripInitialPreloadSteps(trip);
  const dispatchPeriod = normalizeDispatchPeriodValue(
    state?.resultsData?.ui_dispatch_period
    || state?.resultsData?.dispatch_period
    || state?.ui_dispatch_period
    || state?.dispatch_period,
  );
  const dispatchDate = normalizeEndOfDayLoadDateValue(
    state?.resultsData?.dispatch_date
    || state?.resultsData?.planning_summary?.dispatch_date
    || state?.dispatch_date,
  );
  const compartmentFillContext = buildCompartmentFillContext(state);
  const initialPreloadSegments = buildInitialPreloadTimelineSegments(initialPreloadSteps, trip, assignment, {
    compartmentFillContext,
    dispatchDate,
    dispatchPeriod,
    sequenceStart,
    transientActivatedStepKeys,
  });
  const initialPreloadCount = initialPreloadSegments.length;
  const preloadMetadataBySegment = buildStartOfDayPreloadMetadata(routeSegments);
  const { entries: visibleRouteSegments, visibleSequenceBySegmentIndex } = buildVisibleRouteSegmentEntries(
    routeSegments,
    preloadMetadataBySegment,
  );
  const initialDepotTravelSegment = getInitialDepotTravelSegment(routeSegments, preloadMetadataBySegment, initialPreloadSteps);
  const sequenceOffset = initialDepotTravelSegment ? 1 : 0;
  const initialTruckLoadBeforeTons = resolveInitialPreloadLoadTons(initialPreloadSteps);
  const visualLayoutBySegment = buildTimelineVisualLayoutBySegment(routeSegments, trip);
  const serviceAggregationBySegment = buildTimelineServiceAggregationBySegment(routeSegments, assignment, trip);
  const operationRepetitionMetadata = buildOperationRepetitionMetadata(routeSegments);
  const statusIndex = buildTripTimelineStatusIndex(trip);
  const assignmentId = assignment?.truck_id || "";
  const tripRecords = Array.isArray(trip?.operational_steps)
    ? trip.operational_steps.filter(Boolean).map((step) => ({ step }))
    : [];
  const deliveryBlockMetadataByStepId = buildDeliveryBlockMetadataByStepId(trip);
  const manualMixGrainTypes = Array.isArray(assignment?.manual_mix_grain_types)
    ? assignment.manual_mix_grain_types.filter((value) => typeof value === "string" && value.trim())
    : [];
  const resolveManualMixProductLabel = (step, blockIndex) => {
    const stepGrainType = typeof step?.grain_type === "string" ? step.grain_type.trim() : "";

    if (stepGrainType) {
      return stepGrainType;
    }

    return Number.isInteger(blockIndex) && typeof manualMixGrainTypes[blockIndex] === "string"
      ? manualMixGrainTypes[blockIndex].trim()
      : "";
  };
  let previousManualMixBlockIndex = null;
  let previousManualMixProductLabel = "";
  let previousStepProductLabel = "";
  let lastAttributedOutboundRouteIndex = null;

  const operationSegments = visibleRouteSegments.map(({ segment, segmentIndex }, visibleSegmentIndex) => {
    const globalSequence = sequenceStart + initialPreloadCount + sequenceOffset + visibleSegmentIndex;
    const visualLayout = visualLayoutBySegment[segmentIndex] || null;
    const displayContext = resolveRouteSegmentDisplayContext(routeSegments, segmentIndex, visualLayout);
    const fromStop = displayContext.routeFromStop || getRouteSegmentStartStop(segment) || null;
    const toStop = displayContext.routeToStop || getRouteSegmentTargetStop(segment) || fromStop;
    const targetStop = displayContext.currentStop || getRouteSegmentTargetStop(segment) || toStop || fromStop || null;
    const operationIdentity = buildOperationIdentity(targetStop, {
      assignmentId,
      repetitionMetadata: operationRepetitionMetadata.get(segment.index) || null,
      trip,
      tripId: trip?.trip_id || "",
      tripSequence: globalSequence,
    });
    let displayedRouteSegment = initialDepotTravelSegment && segmentIndex === initialDepotTravelSegment.segmentIndex && !displayContext.usesOutboundSegment
      ? buildZeroDistanceDisplayedRouteSegment(segment, targetStop)
      : displayContext.displayedRouteSegment || segment;
    const travelAttribution = resolveTimelineRouteTravelAttribution({
      displayedRouteSegment,
      displayContext,
      lastAttributedOutboundRouteIndex,
      segment,
      targetStop,
    });
    lastAttributedOutboundRouteIndex = travelAttribution.lastAttributedOutboundRouteIndex;
    displayedRouteSegment = travelAttribution.displayedRouteSegment;
    const mapHighlightSegmentIndex = resolveRouteSequenceMapHighlightSegmentIndex(
      routeSegments,
      segmentIndex,
      visualLayout,
      displayContext,
    );
    const routeAlternativeSegmentIndexes = collectRouteAlternativeSegmentIndexes(
      routeSegments,
      segmentIndex,
      visualLayout,
      displayContext,
      mapHighlightSegmentIndex,
    );
    const routeAlternativeIndexedSegments = routeAlternativeSegmentIndexes
      .map((routeSegmentIndex) => routeSegments[routeSegmentIndex - 1] || null)
      .filter(Boolean);
    const routeAlternativeGroupApplied = routeAlternativeIndexedSegments.length > 0
      && routeAlternativeIndexedSegments.every((candidateSegment) => candidateSegment?.routeAlternativeApplied === true);
    const routeAlternativeGroupAvailable = Boolean(
      displayedRouteSegment?.routeAlternativeAvailable
      || displayedRouteSegment?.routeAlternativeApplied
      || routeAlternativeIndexedSegments.some((candidateSegment) => hasRouteAlternativeSignal(candidateSegment)),
    );
    const routeAlternativeDisplaySegment = routeAlternativeIndexedSegments.find((candidateSegment) => (
      candidateSegment?.routeAlternativeCurrent?.geometry
      && candidateSegment?.routeAlternativeSummary
    ))
      || routeAlternativeIndexedSegments.find((candidateSegment) => candidateSegment?.routeAlternativeSummary)
      || routeAlternativeIndexedSegments.find((candidateSegment) => (
        candidateSegment?.routeAlternativeApplied
        || candidateSegment?.routeAlternativeAvailable
      ))
      || displayedRouteSegment;
    const productChip = resolveRouteSegmentProductChip(trip, targetStop);
    const productData = resolveRouteSegmentProductData(trip, targetStop);
    const matchedStep = resolveTimelineSegmentOperationalStep(targetStop, statusIndex);
    const priorityMetadata = resolveTimelineSegmentPriorityMetadata(
      targetStop,
      matchedStep,
      useBackendDisplayProjection,
    );
    const waitMinutes = normalizeTimelineDurationMinutes(priorityMetadata.waitMinutes) ?? 0;
    const manualBufferSourceSegment = resolveTimelineManualBufferSourceSegment(routeSegments, segmentIndex, displayContext);
    const manualBufferMinutes = normalizeTimelineDurationMinutes(manualBufferSourceSegment?.manual_buffer_minutes) || 0;
    const manualBufferReason = normalizeTimelineStopMatchValue(manualBufferSourceSegment?.manual_buffer_reason) || null;
    const manualBufferNote = normalizeTimelineStopMatchValue(manualBufferSourceSegment?.manual_buffer_note) || null;
    const manualBufferUpdatedAt = normalizeTimelineTimestamp(manualBufferSourceSegment?.manual_buffer_updated_at) || null;
    const manualBufferUpdatedBy = normalizeTimelineStopMatchValue(manualBufferSourceSegment?.manual_buffer_updated_by) || null;
    const manualBufferStepKey = buildManualBufferStepKeyFromOperationalStep(matchedStep, trip);
    const travelDurationMinutes = travelAttribution.travelDurationMinutes;
    const displayTravelDurationMinutes = travelAttribution.displayTravelDurationMinutes;
    const rawServiceDurationMinutes = normalizeTimelineDurationMinutes(targetStop?.service_time_minutes);
    const serviceDurationOverrideMinutes = normalizeTimelineDurationMinutes(
      serviceAggregationBySegment[segmentIndex]?.serviceDurationMinutes,
    );
    const serviceDurationMinutes = serviceDurationOverrideMinutes ?? rawServiceDurationMinutes ?? 0;
    const geometryTimelineDurationMinutes = travelDurationMinutes + serviceDurationMinutes;
    const baseTimelineDurationMinutes = geometryTimelineDurationMinutes + waitMinutes;
    const timelineDurationMinutes = geometryTimelineDurationMinutes + manualBufferMinutes;
    const visualServiceDurationMinutes = normalizeTimelineDurationMinutes(visualLayout?.visualServiceDurationMinutes);
    const rawVisualTimelineDurationMinutes = normalizeTimelineDurationMinutes(visualLayout?.visualTimelineDurationMinutes);
    const visualTimelineDurationMinutes = ((rawVisualTimelineDurationMinutes != null && rawVisualTimelineDurationMinutes > 0)
      ? rawVisualTimelineDurationMinutes
      : geometryTimelineDurationMinutes) + manualBufferMinutes;
    const sameSiteGroupRunStartIndex = Number.isInteger(visualLayout?.sameSiteGroupRunStartIndex)
      ? visualLayout.sameSiteGroupRunStartIndex
      : segmentIndex;
    const sameSiteGroupStartSequence = sequenceStart + initialPreloadCount + sequenceOffset + ((visibleSequenceBySegmentIndex.get(sameSiteGroupRunStartIndex) || (visibleSegmentIndex + 1)) - 1);
    const sameSiteGroupKey = Boolean(visualLayout?.isSameSiteSequenceGroup)
      ? `${assignmentId}::${trip?.trip_id || "trip"}::${sameSiteGroupStartSequence}::${visualLayout?.sameSiteGroupStopKey || "same-site"}`
      : "";
    const clientId = getClientSelectionId(targetStop);
    const baseStatusMarkers = buildTimelineSegmentStatusMarkers(trip, targetStop, statusIndex);
    const scheduledAt = resolveTimelineSegmentScheduledAt(
      targetStop,
      matchedStep,
      useBackendDisplayProjection,
    );
    const scheduledAtMinutes = resolveTimelineSegmentScheduledAtMinutes(
      targetStop,
      matchedStep,
      useBackendDisplayProjection,
    );
    const inboundStartStop = getRouteSegmentStartStop(segment);
    const fromMatchedStep = resolveTimelineSegmentOperationalStep(inboundStartStop, statusIndex);
    const fromScheduledAt = resolveTimelineSegmentScheduledAt(
      inboundStartStop,
      fromMatchedStep,
      useBackendDisplayProjection,
    );
    const fromScheduledAtMinutes = resolveTimelineSegmentScheduledAtMinutes(
      inboundStartStop,
      fromMatchedStep,
      useBackendDisplayProjection,
    );
    const hasDepotStartAnchor = !initialDepotTravelSegment && inboundStartStop?.stop_type === "depot_start" && fromScheduledAtMinutes != null;
    const segmentStartAt = displayContext.isDepotReturnSegment
      ? scheduledAt != null
        ? shiftTimelineTimestamp(scheduledAt, -travelDurationMinutes)
        : null
      : hasDepotStartAnchor
      ? normalizeTimelineTimestamp(fromScheduledAt)
      : scheduledAt != null
        ? normalizeTimelineTimestamp(scheduledAt)
        : null;
    const segmentStartMinutes = displayContext.isDepotReturnSegment
      ? resolveTimelineSegmentStartMinutesFromSchedule(scheduledAtMinutes, travelDurationMinutes)
      : hasDepotStartAnchor
      ? fromScheduledAtMinutes
      : scheduledAtMinutes != null
        ? scheduledAtMinutes
        : null;
    const shouldCollapseZeroDurationSameSiteFollower = Boolean(displayContext.shouldZeroSameSiteFollower)
      && travelDurationMinutes <= 0
      && serviceDurationMinutes <= 0
      && manualBufferMinutes <= 0;
    const nextArrivalAt = displayContext.usesOutboundSegment
      ? resolveTimelineFieldText(
          [toStop],
          useBackendDisplayProjection ? ["arrival", "start"] : ["arrival"],
          useBackendDisplayProjection,
        )
      : null;
    const nextArrivalAtMinutes = displayContext.usesOutboundSegment
      ? resolveTimelineFieldMinutes(
          [toStop],
          useBackendDisplayProjection ? ["arrival", "start"] : ["arrival"],
          useBackendDisplayProjection,
        )
      : null;
    const departureAt = resolveTimelineFieldText(
      [targetStop, matchedStep],
      useBackendDisplayProjection ? ["departure", "service_end", "end"] : ["departure", "service_end"],
      useBackendDisplayProjection,
    );
    const departureAtMinutes = resolveTimelineFieldMinutes(
      [targetStop, matchedStep],
      useBackendDisplayProjection ? ["departure", "service_end", "end"] : ["departure", "service_end"],
      useBackendDisplayProjection,
    );
    const rawSegmentEndMinutes = shouldCollapseZeroDurationSameSiteFollower
      ? segmentStartMinutes
      : displayContext.isDepotReturnSegment
      ? scheduledAtMinutes != null
      ? scheduledAtMinutes + serviceDurationMinutes
      : null
      : nextArrivalAtMinutes != null
        ? nextArrivalAtMinutes
        : departureAtMinutes != null
          ? departureAtMinutes + travelDurationMinutes
          : scheduledAtMinutes != null
            ? scheduledAtMinutes + serviceDurationMinutes + travelDurationMinutes
            : null;
      const rawSegmentEndAt = shouldCollapseZeroDurationSameSiteFollower
      ? (segmentStartAt ?? normalizeTimelineTimestamp(scheduledAt))
      : displayContext.isDepotReturnSegment
      ? scheduledAt != null
        ? shiftTimelineTimestamp(scheduledAt, serviceDurationMinutes)
        : null
      : nextArrivalAt != null
        ? normalizeTimelineTimestamp(nextArrivalAt)
        : departureAt != null
          ? shiftTimelineTimestamp(departureAt, travelDurationMinutes)
          : scheduledAt != null
            ? shiftTimelineTimestamp(scheduledAt, serviceDurationMinutes + travelDurationMinutes)
            : null;
    const segmentEndMinutes = clampTimelineSegmentEndMinutes(
      segmentStartMinutes,
      scheduledAtMinutes,
        rawSegmentEndMinutes != null
          ? rawSegmentEndMinutes + (useBackendDisplayProjection ? 0 : manualBufferMinutes)
          : null,
      null,
    );
      const segmentEndAt = rawSegmentEndAt != null
        ? shiftTimelineTimestamp(rawSegmentEndAt, useBackendDisplayProjection ? 0 : manualBufferMinutes)
        : null;
    const displayedServiceDurationMinutes = serviceDurationMinutes;
    const displayedTimelineDurationMinutes = segmentStartMinutes != null && segmentEndMinutes != null
      ? Math.max(segmentEndMinutes - segmentStartMinutes, 0)
      : timelineDurationMinutes;
    const hasPriorityTimeAnchor = Boolean(priorityMetadata.dominantPriority && scheduledAtMinutes != null);
    const visualSegmentStartMinutes = hasPriorityTimeAnchor
      ? scheduledAtMinutes
      : segmentStartMinutes;
    const visualSegmentStartAt = hasPriorityTimeAnchor
      ? normalizeTimelineTimestamp(scheduledAt)
      : segmentStartAt;
    const deliveryHighlightId = normalizeTimelineStopMatchValue(targetStop?.delivery_id)
      || normalizeTimelineStopMatchValue(matchedStep?.delivery_id);
    const activationHighlightKey = matchedStep?.step_type === "loading"
      ? getLoadingHighlightKey(matchedStep?.step_id)
      : getDeliveryHighlightKey(deliveryHighlightId);
    const loadingOrderHighlightKey = matchedStep?.step_type === "loading"
      ? getLoadingOrderHighlightKey(matchedStep?.order_id)
      : "";
    const routeSegmentHighlightKeys = [
      mapHighlightSegmentIndex,
      displayedRouteSegment?.index,
      segment?.index,
      ...routeAlternativeSegmentIndexes,
    ]
      .map((routeSegmentIndex) => getRouteSegmentHighlightKey(trip?.trip_id || "", routeSegmentIndex))
      .filter((highlightKey, highlightKeyIndex, items) => (
        highlightKey
        && items.indexOf(highlightKey) === highlightKeyIndex
      ));
    const segmentBufferHighlightKey = getSegmentBufferHighlightKey(manualBufferStepKey);
    const reassignmentContexts = matchedStep && state
      ? getOperationalStepReassignmentContexts(state, matchedStep)
      : [];
    const reassignmentPreviewContexts = matchedStep && state
      ? getOperationalStepReassignmentCandidatePoolContexts(state, matchedStep)
      : [];
    const relatedDeliveryItem = typeof matchedStep?.delivery_id === "string"
      ? (Array.isArray(assignment?.delivery_items)
        ? assignment.delivery_items.find((item) => item?.delivery_id === matchedStep.delivery_id) || null
        : null)
      : null;
    const manualMixBlockIndex = Number.isInteger(Number(matchedStep?.manual_mix_block_index))
      ? Number(matchedStep.manual_mix_block_index)
      : null;
    const pmProductLabels = targetStop?.is_pm_operational_loading
      ? [...new Set(getStopCompartmentAllocations(targetStop)
        .map((allocation) => (typeof allocation?.grain_type === "string" ? allocation.grain_type.trim() : ""))
        .filter(Boolean))]
      : [];
    const hasPmProductChange = Boolean(
      previousStepProductLabel
      && pmProductLabels.some((productLabel) => productLabel !== previousStepProductLabel)
    );
    const stepProductLabel = typeof matchedStep?.grain_type === "string" ? matchedStep.grain_type.trim() : "";
    if (stepProductLabel && !targetStop?.is_pm_operational_loading) {
      previousStepProductLabel = stepProductLabel;
    }
    const currentManualMixProductLabel = hasPmProductChange
      ? pmProductLabels.join(" / ")
      : resolveManualMixProductLabel(matchedStep, manualMixBlockIndex);
    const hasManualMixBoundaryBefore = hasPmProductChange || Boolean(
      assignment?.is_manual_mix
      && manualMixBlockIndex != null
      && previousManualMixBlockIndex != null
      && manualMixBlockIndex > previousManualMixBlockIndex
    );
    const manualMixPreviousProductLabel = hasPmProductChange
      ? previousStepProductLabel
      : hasManualMixBoundaryBefore
      ? previousManualMixProductLabel
      : "";
    const manualMixBoundaryTitle = hasManualMixBoundaryBefore
      ? (manualMixPreviousProductLabel && currentManualMixProductLabel
        ? `Changement de produit: ${manualMixPreviousProductLabel} → ${currentManualMixProductLabel}`
        : currentManualMixProductLabel
          ? `Changement de produit: ${currentManualMixProductLabel}`
          : "Changement de produit")
      : null;
    if (manualMixBlockIndex != null) {
      previousManualMixBlockIndex = manualMixBlockIndex;
      if (currentManualMixProductLabel) {
        previousManualMixProductLabel = currentManualMixProductLabel;
      }
    }
    const businessStep = matchedStep
      ? buildOperationalStepViewModel({
          assignmentId,
          canManageDispatcherFreeze: state?.canManageDispatcherFreeze === true,
          openOperationalReassignmentKey,
          pendingDeliveryIds,
          pendingLoadingIds,
          selectedClientId,
          stepRecord: {
            reassignmentContexts,
            relatedDeliveryItem,
            step: matchedStep,
          },
          transientActivatedStepKeys,
          trip,
          tripRecords,
        })
      : null;
    // Chargé PM : sa protection automatique s'affiche avec l'icône PM, pas le bouclier d'une protection manuelle.
    const isEndOfDayLoadSourceStep = matchedStep?.is_end_of_day_load_source_preload === true;
    const statusMarkers = mergeBusinessStepStatusMarkers(baseStatusMarkers, businessStep).map((marker) => (
      isEndOfDayLoadSourceStep && ["protected", "protected-reordered"].includes(marker?.key)
        ? { ...marker, description: "Commande chargée en PM : elle reste à cette place.", icon: "sunset", key: "end-of-day-load", label: "Chargé PM", tone: "pm" }
        : marker
    ));
    const reassignmentAvailability = matchedStep && state
      ? getOperationalStepReassignmentAvailability(state, matchedStep)
      : null;
    const dispatcherStatusCode = normalizeTimelineStopMatchValue(businessStep?.dispatcherStatus?.code);
    const isPortalLoadingReassignmentLocked = isPortalDriverLoadingReassignmentLocked(
      trip,
      targetStop,
      statusIndex,
      matchedStep,
    );
    const isDispatcherReassignmentLocked = isFrozenDispatcherStatusCode(dispatcherStatusCode)
      || dispatcherStatusCode === "delivered"
      || isPortalLoadingReassignmentLocked
      || (businessStep?.sharedLoadingFreezeChain?.isMember === true && businessStep?.stepType !== "loading");
    const matchedStepType = normalizeTimelineStopMatchValue(matchedStep?.step_type);
    const matchedOrderId = normalizeTimelineStopMatchValue(matchedStep?.order_id) || operationIdentity.operationOrderId || "";
    const matchedDeliveryId = normalizeTimelineStopMatchValue(matchedStep?.delivery_id) || operationIdentity.operationDeliveryId || "";
    const matchedLoadingStepId = matchedStepType === "loading"
      ? normalizeTimelineStopMatchValue(matchedStep?.step_id)
      : normalizeTimelineStopMatchValue(matchedStep?.loading_step_id);
    const hasSiblingDeliveriesForOrder = matchedStepType === "delivery"
      && countTripDeliveryStepsByOrderId(trip, matchedOrderId) > 1;
    const deliveryBlockMetadata = matchedStepType === "delivery"
      ? deliveryBlockMetadataByStepId.get(normalizeTimelineStopMatchValue(matchedStep?.step_id)) || null
      : null;
    const hasSuspendableDrag = Boolean(
      !isDispatcherReassignmentLocked
      && !isEndOfDayLoadSourceStep
      &&
      businessStep?.suspendButton
      && !businessStep.suspendButton.disabled
      && matchedStep?.step_id
      && matchedStep?.step_type,
    );
    const hasReorderableStep = Boolean(
      !isDispatcherReassignmentLocked
      && !targetStop?.is_pm_operational_loading
      && !matchedStep?.is_pm_operational_loading
      && matchedStep?.step_id
      && matchedStep?.step_type
      && (
        matchedStepType === "loading"
        || (matchedStepType === "delivery" && matchedLoadingStepId)
      ),
    );
    const hasDirectReassignmentDrag = Boolean(
      hasSuspendableDrag
      &&
      businessStep?.reassignButton
      && !businessStep.reassignButton.disabled
      && reassignmentContexts.length === 1
      && reassignmentAvailability?.hasAssignableTarget
      && matchedStep?.step_id
      && matchedStep?.step_type,
    );

    return {
      assignmentId: assignment?.truck_id || "",
      assignmentIsManualMix: Boolean(assignment?.is_manual_mix),
      aspiratorRequirementLabel: operationIdentity.aspiratorRequirementLabel,
      baseTimelineDurationLabel: formatDurationShort(baseTimelineDurationMinutes) || "0 min",
      baseTimelineDurationMinutes,
      businessStep,
      clientId,
      chips: buildRouteSegmentChips(displayedRouteSegment, targetStop, trip, { serviceMinutesOverride: serviceDurationMinutes }),
      color: getStopDisplayColor(targetStop),
      ...(targetStop?.is_pm_operational_loading ? {
        isPmOperationalLoading: true,
        endOfDayLoadMarkerKey: targetStop.pm?.marker_key || targetStop.end_of_day_load_marker_key || "",
        endOfDayLoadMarkerDispatchDate: targetStop.pm?.source_dispatch_date || "",
        endOfDayLoadSourceTruckId: targetStop.pm?.source_truck_id || assignment?.truck_id || "",
        endOfDayLoadSourceTruckName: assignment?.truck_name || "",
        endOfDayLoadTargetTruckId: targetStop.pm?.target_truck_id || "",
        endOfDayLoadOrderIds: targetStop.order_ids || [targetStop.order_id].filter(Boolean),
        endOfDayLoadOrderLines: buildPmOrderLines(targetStop, assignment),
        endOfDayLoadDeliveryIds: [...new Set((assignment?.end_of_day_loads || [])
          .filter(marker => marker.marker_key === targetStop.pm?.marker_key
            || targetStop.end_of_day_load_marker_keys?.includes(marker.marker_key))
          .flatMap(marker => marker.delivery_ids || []))],
        endOfDayLoadSourceDispatchDate: targetStop.pm?.source_dispatch_date || "",
        endOfDayLoadTargetDispatchDate: targetStop.pm?.target_dispatch_date || "",
        endOfDayLoadTargetPeriod: targetStop.pm?.target_period || "day",
        endOfDayLoadProductLabels: pmProductLabels,
      } : {}),
      distanceLabel: displayedRouteSegment.distanceLabel || null,
      fromAddress: getRouteStopAddressLabel(fromStop?.address),
      fromLabel: getRouteStopLabel(fromStop),
      globalSequence,
      isActivationHighlighted: (
        (Boolean(activationHighlightKey) && transientActivatedStepKeys.has(activationHighlightKey))
        || (Boolean(loadingOrderHighlightKey) && transientActivatedStepKeys.has(loadingOrderHighlightKey))
        || (Boolean(segmentBufferHighlightKey) && transientActivatedStepKeys.has(segmentBufferHighlightKey))
        || routeSegmentHighlightKeys.some((highlightKey) => transientActivatedStepKeys.has(highlightKey))
      ),
      isManualBufferEditable: Boolean(manualBufferStepKey),
      hasManualMixBoundaryBefore,
      manualMixBoundaryTitle,
      isSelectable: Boolean(clientId),
      isSelectedClient: Boolean(clientId && selectedClientId === clientId),
      isCurrentTrip: Boolean(trip?.trip_id),
      manualBufferLabel: manualBufferMinutes > 0
        ? `+${formatDurationShort(manualBufferMinutes) || `${manualBufferMinutes} min`}`
        : null,
      manualBufferMinutes,
      manualBufferNote,
      manualBufferOrderId: normalizeTimelineStopMatchValue(matchedStep?.order_id) || null,
      manualBufferReason,
      manualBufferReasonLabel: formatManualBufferReasonLabel(manualBufferReason) || null,
      manualBufferStepKey: manualBufferStepKey || null,
      manualBufferUpdatedAt,
      manualBufferUpdatedBy,
      manualMixBlockIndex,
      manualMixCurrentProductLabel: currentManualMixProductLabel || null,
      manualMixPreviousProductLabel: manualMixPreviousProductLabel || null,
      orderLabel: targetStop?.order_id || "",
      workOrderLabel: typeof matchedStep?.work_order_number === "string"
        ? matchedStep.work_order_number.trim()
        : (typeof targetStop?.work_order_number === "string" ? targetStop.work_order_number.trim() : ""),
      siloLabel: resolveRouteSegmentSiloLabel(trip, targetStop),
      operationAddress: operationIdentity.operationAddress,
      aspiratorMissionGroupKey: operationIdentity.aspiratorMissionGroupKey,
      aspiratorMissionRouteSignature: operationIdentity.aspiratorMissionRouteSignature,
      operationDeliveryId: operationIdentity.operationDeliveryId,
      operationDisambiguatorLabel: operationIdentity.operationDisambiguatorLabel,
      operationKey: operationIdentity.operationKey,
      operationLabel: operationIdentity.operationLabel,
      operationOwnerLabel: operationIdentity.operationOwnerLabel,
      operationOrderId: operationIdentity.operationOrderId,
      operationTypeLabel: operationIdentity.operationTypeLabel,
      productCodeLabel: productData.productCode,
      ...resolveRouteSegmentCompartmentFill(trip, targetStop, compartmentFillContext),
      productLabel: productData.productLabel || productChip?.label || "",
      quantityLabel: typeof targetStop?.quantity_tons === "number" ? `${formatTons(targetStop.quantity_tons)} t` : null,
      quantityTons: typeof targetStop?.quantity_tons === "number" ? targetStop.quantity_tons : null,
      compartmentAllocations: getStopCompartmentAllocations(targetStop),
      requiresAspirator: operationIdentity.requiresAspirator,
      requiresOntario: operationIdentity.requiresOntario,
      arrivalAt: priorityMetadata.arrivalAt,
      arrivalAtMinutes: priorityMetadata.arrivalAtMinutes,
      dominantPriority: priorityMetadata.dominantPriority,
      hasPriorityTimeAnchor,
      isStartOfDayPreload: false,
      isPreloadedPreviousDay: resolveTimelineSegmentPreloadedPreviousDay(targetStop, matchedStep),
      // Chargements à la suite : commandes chargées avec celle-ci avant les livraisons.
      joinedLoadOrderIds: matchedStepType === "loading" && Array.isArray(matchedStep?.joined_load_order_ids)
        ? matchedStep.joined_load_order_ids.filter((orderId) => typeof orderId === "string" && orderId.trim())
        : [],
      loading_client_name: typeof targetStop?.loading_client_name === "string" ? targetStop.loading_client_name.trim() : "",
      meunerie_loading_extra_minutes: targetStop?.stop_type === "loading" && !targetStop?.is_preloaded_previous_day
        ? Math.max(0, Number(targetStop?.meunerie_loading_extra_minutes) || 0)
        : 0,
      aspirator_service_minutes: targetStop?.stop_type === "delivery"
        ? Math.max(0, Math.round(Number(targetStop?.aspirator_service_minutes) || 0))
        : 0,
      aspiratorServiceMinutes: targetStop?.stop_type === "delivery"
        ? Math.max(0, Math.round(Number(targetStop?.aspirator_service_minutes) || 0))
        : 0,
      delivery_fixed_minutes: targetStop?.stop_type === "delivery"
        ? Math.max(0, Math.round(Number(targetStop?.delivery_fixed_minutes) || 0))
        : 0,
      loading_fixed_minutes: targetStop?.stop_type === "loading" && !targetStop?.is_preloaded_previous_day
        ? Math.max(0, Math.round(Number(targetStop?.loading_fixed_minutes) || 0))
        : 0,
      client_loading_opening_time: targetStop?.stop_type === "loading"
        && !resolveTimelineSegmentPreloadedPreviousDay(targetStop, matchedStep)
        && typeof targetStop?.client_loading_opening_time === "string"
        ? targetStop.client_loading_opening_time
        : null,
      client_loading_closing_time: targetStop?.stop_type === "loading"
        && !resolveTimelineSegmentPreloadedPreviousDay(targetStop, matchedStep)
        && typeof targetStop?.client_loading_closing_time === "string"
        ? targetStop.client_loading_closing_time
        : null,
      routeDistanceSource: displayedRouteSegment?.distanceSource || "unavailable",
      routeDistanceSourceLabel: displayedRouteSegment?.distanceSourceLabel || null,
      routeEstimatedTravelDurationMinutes: normalizeTimelineDurationMinutes(displayedRouteSegment?.estimatedDurationMinutes),
      routeSegmentAlerts: Array.isArray(displayedRouteSegment?.routeSegmentAlerts) ? displayedRouteSegment.routeSegmentAlerts : [],
      hasMontrealRiskAlert: Boolean(displayedRouteSegment?.hasMontrealRiskAlert),
      montrealRiskAlert: displayedRouteSegment?.montrealRiskAlert || null,
      routeAlternativeApplied: routeAlternativeSegmentIndexes.length > 0
        ? routeAlternativeGroupApplied
        : Boolean(displayedRouteSegment?.routeAlternativeApplied),
      routeAlternativeAvailable: routeAlternativeSegmentIndexes.length > 0
        ? routeAlternativeGroupAvailable
        : Boolean(displayedRouteSegment?.routeAlternativeAvailable),
      routeAlternativeDeltaDistanceMeters: Number.isFinite(Number(routeAlternativeDisplaySegment?.routeAlternativeDeltaDistanceMeters))
        ? Number(routeAlternativeDisplaySegment.routeAlternativeDeltaDistanceMeters)
        : null,
      routeAlternativeDeltaDurationMinutes: Number.isFinite(Number(routeAlternativeDisplaySegment?.routeAlternativeDeltaDurationMinutes))
        ? Number(routeAlternativeDisplaySegment.routeAlternativeDeltaDurationMinutes)
        : null,
      routeAlternativeId: typeof routeAlternativeDisplaySegment?.routeAlternativeId === "string" ? routeAlternativeDisplaySegment.routeAlternativeId : "",
      routeAlternativeLabel: typeof routeAlternativeDisplaySegment?.routeAlternativeLabel === "string" ? routeAlternativeDisplaySegment.routeAlternativeLabel : "",
      routeAlternativeReason: typeof routeAlternativeDisplaySegment?.routeAlternativeReason === "string" ? routeAlternativeDisplaySegment.routeAlternativeReason : "",
      routeAlternativeCurrent: routeAlternativeDisplaySegment?.routeAlternativeCurrent || null,
      routeAlternativeSummary: routeAlternativeDisplaySegment?.routeAlternativeSummary || null,
      routeSpeedKmh: typeof displayedRouteSegment?.speedKmh === "number" ? displayedRouteSegment.speedKmh : null,
      reassignmentAvailability,
      reassignmentPreviewContexts,
      // Chargé PM : réordonnable sur son camion seulement (pas de glisser vers un autre camion).
      isEndOfDayLoadSourceStep,
      reassignmentDrag: hasSuspendableDrag
        ? {
            stepId: matchedStep.step_id,
            stepType: matchedStep.step_type,
          }
        : null,
      segmentReorder: hasReorderableStep
        ? matchedStepType === "delivery" && deliveryBlockMetadata?.deliveryBlockId
          ? {
              deliveryBlockId: deliveryBlockMetadata.deliveryBlockId,
              deliveryId: matchedDeliveryId,
              manualMixBlockIndex: Number.isInteger(manualMixBlockIndex) ? manualMixBlockIndex : null,
              orderId: matchedOrderId,
              parentLoadingStepId: matchedLoadingStepId,
              scope: "delivery-block",
              segmentId: operationIdentity.operationKey,
              stepId: matchedStep.step_id,
              stepType: "delivery",
              tripId: trip?.trip_id || "",
            }
          : matchedStepType === "delivery" && hasSiblingDeliveriesForOrder
          ? {
              deliveryId: matchedDeliveryId,
              manualMixBlockIndex: Number.isInteger(manualMixBlockIndex) ? manualMixBlockIndex : null,
              orderId: matchedOrderId,
              parentLoadingStepId: matchedLoadingStepId,
              scope: "deliveries-with-same-parent-load",
              segmentId: operationIdentity.operationKey,
              stepId: matchedStep.step_id,
              stepType: "delivery",
              tripId: trip?.trip_id || "",
            }
          : {
              deliveryId: matchedStepType === "delivery" ? matchedDeliveryId : "",
              manualMixBlockIndex: Number.isInteger(manualMixBlockIndex) ? manualMixBlockIndex : null,
              orderId: matchedOrderId,
              parentLoadingStepId: "",
              scope: "loads",
              segmentId: operationIdentity.operationKey,
              stepId: matchedStepType === "delivery" ? matchedLoadingStepId : matchedStep.step_id,
              stepType: "loading",
              tripId: trip?.trip_id || "",
            }
        : null,
      // Retain the incoming leg independently of the Gantt's outgoing travel attribution.
      incomingRouteSegmentIndex: segment.index,
      incomingTravelDurationMinutes: segment.displayDurationMinutes ?? segment.durationMinutes,
      routeSegmentIndex: mapHighlightSegmentIndex ?? segment.index,
      routeAlternativeSegmentIndexes,
      routeFromAddress: getRouteStopAddressLabel(fromStop?.address),
      routeFromLabel: getRouteStopLabel(fromStop),
      routeScheduledTravelDurationMinutes: normalizeTimelineDurationMinutes(displayedRouteSegment?.scheduledDurationMinutes),
      routeToAddress: getRouteStopAddressLabel(toStop?.address),
      routeToLabel: getRouteStopLabel(toStop),
      serviceDurationLabel: hasExplicitStopServiceDuration(targetStop)
        ? formatDisplayedServiceDurationLabel(displayedServiceDurationMinutes)
        : null,
      serviceDurationMinutes: displayedServiceDurationMinutes,
      isSameSiteSequenceGroup: Boolean(visualLayout?.isSameSiteSequenceGroup),
      sameSiteGroupContainsMixedStopTypes: Boolean(visualLayout?.sameSiteGroupContainsMixedStopTypes),
      sameSiteGroupIndex: Number.isInteger(visualLayout?.sameSiteGroupIndex) ? visualLayout.sameSiteGroupIndex : 0,
      sameSiteGroupKey,
      sameSiteGroupStartSequence,
      sameSiteGroupSize: Number.isInteger(visualLayout?.sameSiteGroupSize) ? visualLayout.sameSiteGroupSize : 1,
      scheduledAt,
      scheduledAtMinutes,
      segmentEndAt,
      segmentEndMinutes,
      segmentStartAt,
      segmentStartMinutes,
      stopType: targetStop?.stop_type || fromStop?.stop_type || "delivery",
      statusMarkers,
      statusSummaryLabel: statusMarkers.map((marker) => marker.label).join(" · "),
      timeWindowOverride: businessStep?.timeWindowOverride && typeof businessStep.timeWindowOverride === "object"
        ? businessStep.timeWindowOverride
        : null,
      timelineDurationLabel: formatDurationShort(displayedTimelineDurationMinutes) || "0 min",
      timelineDurationMinutes: displayedTimelineDurationMinutes,
      toAddress: getRouteStopAddressLabel(targetStop?.address),
      toLabel: getRouteStopLabel(targetStop),
      displayTravelDurationMinutes,
      travelDurationLabel: travelDurationMinutes > 0 ? formatDurationShort(travelDurationMinutes) : (travelDurationMinutes === 0 ? "0 min" : null),
      travelDurationMinutes,
      tripId: trip?.trip_id || "",
      tripLabel: trip?.trip_id || "Tournée",
      tripSequence: globalSequence,
      typeLabel: getStopTypeLabel(targetStop),
      visualSegmentEndAt: segmentEndAt,
      visualSegmentEndMinutes: segmentEndMinutes,
      visualSegmentStartAt,
      visualSegmentStartMinutes,
      visualServiceDurationMinutes: visualServiceDurationMinutes ?? displayedServiceDurationMinutes,
      visualTimelineDurationMinutes: visualTimelineDurationMinutes != null
        ? visualTimelineDurationMinutes
        : displayedTimelineDurationMinutes,
      usesDurationDrivenTimelineGeometry: useDurationDrivenTimelineGeometry,
      waitMinutes: priorityMetadata.waitMinutes,
    };
  });

  const resolvedSegments = [
    ...initialPreloadSegments,
    initialDepotTravelSegment
      ? buildInitialDepotTravelTimelineSegment(initialDepotTravelSegment, assignment, trip, {
          sequenceStart: sequenceStart + initialPreloadCount,
          truckLoadBeforeTons: initialTruckLoadBeforeTons,
          useBackendDisplayProjection,
          useDurationDrivenTimelineGeometry,
        })
      : null,
    ...operationSegments,
  ].filter(Boolean);

  return useBackendDisplayProjection
    ? resolvedSegments
    : applyManualBufferTimelineOffsets(resolvedSegments);
}

function normalizeTimelineContentText(value) {
  return typeof value === "string" ? value.trim() : "";
}

const TIMELINE_INACTIVE_CONTENT_STATUSES = new Set([
  "annule",
  "annulee",
  "cancelled",
  "canceled",
  "delivery_suspended",
  "removed",
  "suspended",
]);

function normalizeTimelineContentStatus(value) {
  return normalizeTimelineContentText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
}

function isTimelineContentItemActive(item) {
  if (!item || typeof item !== "object") {
    return false;
  }
  if (
    item.is_cancelled === true
    || item.is_canceled === true
    || item.is_delivery_suspended === true
    || item.is_removed === true
    || item.is_suspended === true
    || item.cancelled_at
    || item.canceled_at
    || item.removed_at
  ) {
    return false;
  }

  return ![
    item.dispatch_status,
    item.manual_status_code,
    item.planning_status,
    item.status,
    item.status_code,
  ].some((status) => TIMELINE_INACTIVE_CONTENT_STATUSES.has(normalizeTimelineContentStatus(status)));
}

function buildTimelineContentItem(item, fallbackKey = "") {
  if (!item || typeof item !== "object") {
    return null;
  }

  const orderId = normalizeTimelineContentText(item.order_id || item.orderId);
  const deliveryId = normalizeTimelineContentText(item.delivery_id || item.deliveryId);
  const clientName = normalizeClientDisplayLabel(
    item.client_name
    || item.delivery_client_name
    || item.order_client_name
    || item.label,
  );
  const key = deliveryId
    ? `delivery:${deliveryId}`
    : orderId
      ? `order:${orderId}`
      : normalizeTimelineContentText(fallbackKey);
  if (!key) {
    return null;
  }

  const businessLabel = orderId || deliveryId || "Commande";
  return {
    clientName,
    deliveryId,
    key,
    label: clientName && clientName !== businessLabel
      ? `${businessLabel} — ${clientName}`
      : businessLabel,
    orderId,
  };
}

function buildAssignmentTimelineContentItems(assignment) {
  const items = [];
  const seenKeys = new Set();
  const coveredOrderIds = new Set();
  const pushItem = (item, fallbackKey = "") => {
    if (!isTimelineContentItemActive(item)) {
      return;
    }
    const resolvedItem = buildTimelineContentItem(item, fallbackKey);
    if (!resolvedItem || seenKeys.has(resolvedItem.key)) {
      return;
    }

    seenKeys.add(resolvedItem.key);
    if (resolvedItem.orderId) {
      coveredOrderIds.add(resolvedItem.orderId);
    }
    items.push(resolvedItem);
  };

  (Array.isArray(assignment?.delivery_items) ? assignment.delivery_items : []).forEach((item, index) => {
    pushItem(item, `assignment-delivery:${index}`);
  });
  (Array.isArray(assignment?.orders) ? assignment.orders : []).forEach((item, index) => {
    const orderId = normalizeTimelineContentText(item?.order_id || item?.orderId);
    if (orderId && coveredOrderIds.has(orderId)) {
      return;
    }
    pushItem(item, `assignment-order:${index}`);
  });

  if (items.length > 0) {
    return items;
  }

  const assignmentOperationalSteps = Array.isArray(assignment?.operational_steps)
    ? assignment.operational_steps
    : [];
  assignmentOperationalSteps
    .filter((step) => normalizeTimelineContentText(step?.step_type || step?.stepType).toLowerCase() === "delivery")
    .forEach((step, index) => {
      pushItem(step, `assignment-step:${index}`);
    });

  if (items.length > 0) {
    return items;
  }

  (Array.isArray(assignment?.trips) ? assignment.trips : []).forEach((trip, tripIndex) => {
    const tripDeliveryItems = Array.isArray(trip?.delivery_items) ? trip.delivery_items : [];
    const tripOrders = Array.isArray(trip?.orders) ? trip.orders : [];
    const tripClients = Array.isArray(trip?.clients) ? trip.clients : [];
    const tripOperationalSteps = Array.isArray(trip?.operational_steps) ? trip.operational_steps : [];

    tripDeliveryItems.forEach((item, index) => {
      pushItem(item, `trip:${tripIndex}:delivery:${index}`);
    });
    tripOrders.forEach((item, index) => {
      const orderId = normalizeTimelineContentText(item?.order_id || item?.orderId);
      if (orderId && coveredOrderIds.has(orderId)) {
        return;
      }
      pushItem(item, `trip:${tripIndex}:order:${index}`);
    });
    tripClients.forEach((item, index) => {
      pushItem(item, `trip:${tripIndex}:client:${index}`);
    });
    tripOperationalSteps
      .filter((step) => normalizeTimelineContentText(step?.step_type || step?.stepType).toLowerCase() === "delivery")
      .forEach((step, index) => {
        pushItem(step, `trip:${tripIndex}:step:${index}`);
      });
  });

  return items;
}

function countTimelineAssignedCommands(contentItems) {
  const commandKeys = new Set();
  (Array.isArray(contentItems) ? contentItems : []).forEach((item) => {
    const orderId = normalizeTimelineContentText(item?.orderId);
    const deliveryId = normalizeTimelineContentText(item?.deliveryId);
    const commandKey = orderId
      ? `order:${orderId}`
      : deliveryId
        ? `delivery:${deliveryId}`
        : "";
    if (commandKey) {
      commandKeys.add(commandKey);
    }
  });
  return commandKeys.size;
}

function hasTimelineLoadingOrDeliverySegment(segments) {
  return (Array.isArray(segments) ? segments : []).some((segment) => {
    const segmentStopType = normalizeTimelineContentText(
      segment?.stopType || segment?.stop_type,
    ).toLowerCase();
    if (segmentStopType === "loading" || segmentStopType === "delivery") {
      return true;
    }

    return (Array.isArray(segment?.stops) ? segment.stops : []).some((stop) => {
      const stopType = normalizeTimelineContentText(
        stop?.stop_type || stop?.stopType,
      ).toLowerCase();
      return stopType === "loading" || stopType === "delivery";
    });
  });
}

function buildTimelineRenderState(contentItems, segments) {
  const items = Array.isArray(contentItems) ? contentItems : [];
  const assignedItemCount = countTimelineAssignedCommands(items);
  const resolvedSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];
  const resolvedSegmentCount = resolvedSegments.length;
  const hasLoadingOrDeliverySegment = hasTimelineLoadingOrDeliverySegment(resolvedSegments);
  if (
    resolvedSegmentCount > 0
    && (assignedItemCount === 0 || hasLoadingOrDeliverySegment)
  ) {
    return {
      assignedItemCount,
      items,
      message: "",
      status: "ready",
      title: "",
    };
  }

  if (assignedItemCount > 0) {
    return {
      assignedItemCount,
      items,
      message: "Les étapes nécessaires à cette tournée sont incomplètes. Relancez Optimiser; si l’alerte demeure, suivez le plan dans Alertes ou M-à-J Commandes.",
      status: "unrenderable",
      title: `Tournée à vérifier — ${assignedItemCount} commande${assignedItemCount > 1 ? "s" : ""}`,
    };
  }

  return {
    assignedItemCount: 0,
    items: [],
    message: "Ce camion ne contient aucune commande planifiée.",
    status: "empty",
    title: "Aucune étape planifiée",
  };
}

function normalizeTruckIncidentTruckId(value) {
  return typeof value === "string" ? value.trim() : "";
}

function resolveTruckIncidentStartMinutes(incident = null) {
  const candidates = [
    incident?.display_start_minutes,
    incident?.start_minutes,
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === "") {
      continue;
    }
    const minutes = Number(candidate);
    if (Number.isFinite(minutes) && minutes >= 0) {
      return minutes;
    }
  }

  return parseTimelineClockMinutes(incident?.start_time);
}

function buildTruckIncidentsByTruckId(resultsData = null, assignments = []) {
  const topLevelIncidents = Array.isArray(resultsData?.truck_incidents)
    ? resultsData.truck_incidents.filter(Boolean)
    : null;
  const incidentList = topLevelIncidents ?? (Array.isArray(assignments) ? assignments : [])
    .flatMap((assignment) => (
      Array.isArray(assignment?.truck_incidents) ? assignment.truck_incidents.filter(Boolean) : []
    ));
  const incidentsByTruckId = new Map();

  incidentList.forEach((incident) => {
    const truckId = normalizeTruckIncidentTruckId(incident?.truck_id || incident?.truckId);
    if (!truckId) {
      return;
    }

    const incidents = incidentsByTruckId.get(truckId) || [];
    incidents.push(incident);
    incidentsByTruckId.set(truckId, incidents);
  });

  incidentsByTruckId.forEach((incidents, truckId) => {
    incidentsByTruckId.set(truckId, [...incidents].sort((left, right) => {
      const leftStartMinutes = resolveTruckIncidentStartMinutes(left);
      const rightStartMinutes = resolveTruckIncidentStartMinutes(right);
      const normalizedLeftStart = leftStartMinutes == null ? Number.POSITIVE_INFINITY : leftStartMinutes;
      const normalizedRightStart = rightStartMinutes == null ? Number.POSITIVE_INFINITY : rightStartMinutes;
      if (normalizedLeftStart !== normalizedRightStart) {
        return normalizedLeftStart - normalizedRightStart;
      }

      return String(left?.id || "").localeCompare(String(right?.id || ""));
    }));
  });

  return incidentsByTruckId;
}

export function buildFleetTimelineData(state, assignments = null) {
  const assignmentList = Array.isArray(assignments)
    ? assignments.filter(Boolean)
    : Array.isArray(state?.assignmentList)
      ? state.assignmentList.filter(Boolean)
      : [];
  const selectedAssignmentId = state?.currentAssignment?.truck_id || "";
  const selectedTripId = state?.currentTrip?.trip_id || "";
  const selectedClientId = state?.selectedClientId || "";
  const compartmentFillContext = buildCompartmentFillContext(state);
  const openOperationalReassignmentKey = state?.openOperationalReassignmentKey || "";
  const pendingDeliveryIds = state?.pendingDeliveryMutationById instanceof Set
    ? state.pendingDeliveryMutationById
    : new Set();
  const pendingLoadingIds = state?.pendingLoadingMutationById instanceof Set
    ? state.pendingLoadingMutationById
    : new Set();
  const transientActivatedStepKeys = state?.transientActivatedStepKeys instanceof Set
    ? state.transientActivatedStepKeys
    : new Set();
  const excludedTruckRefreshByTruckId = buildExcludedTruckRefreshByTruckId(state?.resultsData || state || {});
  const dispatchPeriod = normalizeDispatchPeriodValue(
    state?.resultsData?.ui_dispatch_period
    || state?.resultsData?.dispatch_period
    || state?.ui_dispatch_period
    || state?.dispatch_period,
  );
  const dispatchDate = normalizeEndOfDayLoadDateValue(
    state?.resultsData?.dispatch_date
    || state?.resultsData?.planning_summary?.dispatch_date
    || state?.dispatch_date,
  );
  const eveningStandardTransferFilter = resolveEveningStandardTransferGanttFilter(state, dispatchDate);
  const useBackendDisplayProjection = usesBackendTimelineDisplayProjection(state);
  const displayProjectionVersion = useBackendDisplayProjection ? 1 : null;
  const resultsData = state?.resultsData && typeof state.resultsData === "object"
    ? state.resultsData
    : state;
  const truckIncidentsByTruckId = buildTruckIncidentsByTruckId(resultsData, assignmentList);

  const rows = assignmentList.map((assignment) => {
    const assignmentId = assignment?.truck_id || "";
    const incidents = truckIncidentsByTruckId.get(assignmentId) || [];
    const trips = Array.isArray(assignment?.trips) ? assignment.trips.filter(Boolean) : [];
    let sequenceCursor = 1;
    const tripGroups = trips.map((trip) => {
      const segments = buildTripTimelineSegments(trip, assignment, {
        openOperationalReassignmentKey,
        pendingDeliveryIds,
        pendingLoadingIds,
        selectedClientId,
        sequenceStart: sequenceCursor,
        state,
        transientActivatedStepKeys,
      });
      sequenceCursor += segments.length;

      return {
        trip,
        tripId: trip?.trip_id || "",
        tripLabel: trip?.trip_id || "Tournée",
        segments,
        totalDurationMinutes: resolveTripTimelineDurationMinutes(trip, segments),
      };
    });
    const visibleEndOfDayLoadSourcePreloadMarkers = filterEveningStandardTransferResidualEndOfDayLoads(
      assignment?.end_of_day_load_source_preloads,
      eveningStandardTransferFilter,
    );
    const endOfDayLoadSourcePreloads = filterEndOfDayLoadSourcePreloadsCoveredByInitialPreloads(
      visibleEndOfDayLoadSourcePreloadMarkers,
      trips,
    );
    const endOfDayLoadSourcePreloadSegments = buildEndOfDayLoadTimelineSegments(endOfDayLoadSourcePreloads, assignment, {
      compartmentFillContext,
      dispatchDate,
      dispatchPeriod,
      sequenceStart: sequenceCursor,
      sourcePreload: true,
      transientActivatedStepKeys,
    });
    sequenceCursor += endOfDayLoadSourcePreloadSegments.length;
    const rawTripSegments = tripGroups.flatMap((tripGroup) => tripGroup.segments);
    const visibleEndOfDayLoadMarkers = filterEveningStandardTransferResidualEndOfDayLoads(
      (assignment?.end_of_day_loads || []).filter(marker => !(assignment?.operational_steps || []).some(step =>
        step.is_pm_operational_loading === true && (step.end_of_day_load_marker_key === marker.marker_key
          || step.end_of_day_load_marker_keys?.includes(marker.marker_key)))),
      eveningStandardTransferFilter,
    );
    const hasEndOfDayLoads = visibleEndOfDayLoadMarkers.length > 0;
    const tripSegments = hasEndOfDayLoads
      ? mergeTerminalDepotReturnBeforeEndOfDayLoadSegments(rawTripSegments)
      : rawTripSegments;
    const truckLoadBeforeEndOfDayLoad = resolveRunningTruckLoadTonsAfterSegments([
      ...endOfDayLoadSourcePreloadSegments,
      ...tripSegments,
    ]);
    const endOfDayLoadSegments = buildEndOfDayLoadTimelineSegments(visibleEndOfDayLoadMarkers, assignment, {
      compartmentFillContext,
      dispatchDate,
      dispatchPeriod,
      sequenceStart: sequenceCursor,
      initialTruckLoadBeforeTons: truckLoadBeforeEndOfDayLoad,
      transientActivatedStepKeys,
    });
    sequenceCursor += endOfDayLoadSegments.length;
    const segments = [
      ...endOfDayLoadSourcePreloadSegments,
      ...tripSegments,
      ...endOfDayLoadSegments,
    ];
    const timelineContentItems = buildAssignmentTimelineContentItems(assignment);
    const backendTotalDurationMinutes = resolveAssignmentTimelineDurationMinutes(assignment, tripGroups);
    const timelineStartMinutes = resolveRowTimelineStartMinutes(
      assignment,
      segments,
      useBackendDisplayProjection,
    );
    const preserveBackendDisplayProjection = hasCompleteBackendTimelineDisplayProjection(
      assignment,
      state,
    );
    const anchoredSegments = resolveAnchoredSegmentTimeline(
      segments,
      timelineStartMinutes,
      { preserveAbsoluteTimelinePositions: preserveBackendDisplayProjection },
    );
    const timelineRenderState = buildTimelineRenderState(timelineContentItems, anchoredSegments);
    const totalDurationMinutes = timelineRenderState.status === "unrenderable"
      ? null
      : timelineRenderState.status === "empty"
        ? 0
        : useBackendDisplayProjection
          ? backendTotalDurationMinutes ?? 0
          : resolveEffectiveAssignmentTimelineDurationMinutes(
              timelineStartMinutes,
              anchoredSegments,
              backendTotalDurationMinutes,
            ) ?? 0;
    const resolvedTimelineEndMinutes = resolveRowTimelineEndMinutes(
      timelineStartMinutes,
      anchoredSegments,
      totalDurationMinutes,
    );
    const projectedAssignmentEndMinutes = useBackendDisplayProjection
      ? normalizeTimelineClockAnchorMinutes(assignment?.display_end_minutes)
      : null;
    const timelineEndMinutes = projectedAssignmentEndMinutes != null
      ? Math.max(resolvedTimelineEndMinutes, projectedAssignmentEndMinutes)
      : resolvedTimelineEndMinutes;
    const excludedTruckRefresh = excludedTruckRefreshByTruckId.get(assignment?.truck_id || "") || null;
    const hasTimelineActivity = timelineRenderState.status !== "empty";

    return {
      assignment,
      assignmentId,
      displayProjectionVersion,
      dispatchPeriod,
      distanceLabel: timelineRenderState.status === "unrenderable"
        ? null
        : assignment?.total_estimated_distance_label || null,
      driverLabel: typeof assignment?.driver_name === "string" && assignment.driver_name.trim()
        ? assignment.driver_name.trim()
        : assignment?.driver_assignment_status === "unassigned" ? "Chauffeur à compléter" : "Chauffeur non renseigné",
      hasTrips: hasTimelineActivity,
      hasIncidents: incidents.length > 0,
      incidents,
      excludedTruckRefresh,
      hasExcludedTruckRefreshWarning: excludedTruckRefresh?.refreshStatus === "failed",
      hasHaversineRouteWarning: assignmentHasHaversineRouteWarning(assignment, anchoredSegments),
      haversineRouteSummary: summarizeHaversineRouteSegments(assignment, anchoredSegments),
      isTimelineUnrenderable: timelineRenderState.status === "unrenderable",
      isDispatchDisabled: Boolean(assignment?.is_dispatch_disabled),
      isTruckProtected: Boolean(assignment?.is_truck_protected),
      isSelected: Boolean(selectedAssignmentId && selectedAssignmentId === assignment?.truck_id),
      segmentCount: anchoredSegments.length,
      segments: anchoredSegments,
      selectedTripId,
      timelineRenderState,
      totalDurationLabel: timelineRenderState.status === "unrenderable"
        ? "Durée non calculable"
        : formatDurationShort(totalDurationMinutes) || "0 min",
      totalDurationMinutes,
      timelineEndMinutes,
      timelineStartMinutes,
      tripCount: tripGroups.length,
      tripGroups,
      isTractorMissing: assignment?.tractor_status === "missing",
      missingTractorTitle: assignment?.tractor_status === "missing" ? resolveMissingTractorTitle(assignment) : "",
      truckLabel: resolveAssignmentDisplayName(assignment) || "Camion",
      truckId: assignment?.truck_id || "",
    };
  });

  const maxDurationMinutes = rows.reduce((maximum, row) => Math.max(maximum, row.totalDurationMinutes || 0), 0);
  const selectedRow = rows.find((row) => row.isSelected) || null;
  const activeRowCount = rows.filter((row) => row.hasTrips).length;

  return {
    activeRowCount,
    idleRowCount: rows.length - activeRowCount,
    maxDurationLabel: formatDurationShort(maxDurationMinutes) || "0 min",
    maxDurationMinutes,
    rows,
    selectedRow,
    totalRowCount: rows.length,
  };
}

function buildInvalidStopsData(trip) {
  const invalidStops = Array.isArray(trip?.invalid_stops) ? trip.invalid_stops.filter(Boolean) : [];

  return {
    hasItems: invalidStops.length > 0,
    countLabel: `${invalidStops.length} client(s)`,
    items: invalidStops.map((stop, index) => ({
      key: stop?.error_entry_id || stop?.delivery_id || stop?.order_id || `invalid-stop-${index}`,
      title: stop?.client_name || stop?.label || stop?.order_id || "Arrêt invalide",
      address: stop?.address || "Adresse non disponible",
      warningMessage: stop?.warning_message || "",
      badgeLabel: stop?.issue_label || "Non routable",
      badgeTone: stop?.severity === "warning" ? "warning" : "danger",
      errorEntryId: stop?.error_entry_id || "",
    })),
  };
}
